import { toJsDate } from "../utils/toJsDate";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  memo,
} from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import {
  useActivityActions,
  useActivityChatData,
  useActivityFeedData,
} from "../contexts/ActivityContext";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeftIcon,
  SendIcon,
  PlusIcon,
  CameraIcon,
  ImageIcon,
  MapPinIcon,
  InfoIcon,
  UserIcon,
  ClockIcon,
  Trash2Icon,
  XIcon,
  CornerDownRightIcon,
  CheckIcon2,
  CheckCheckIcon,
  AlertTriangleIcon,
  LockIcon,
  StarIcon,
  Edit2Icon,
  CopyIcon,
  SearchIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ForwardIcon,
  MoreVerticalIcon,
  SmileIcon,
  MicIcon,
  StopCircleIcon,
  UploadIcon,
} from "../components/icons/FeatherIcons";
import {
  type ActivityRequest,
  type Chat,
  type ChatLocationPayload,
  type ChatMediaAttachment,
  type ChatMessage,
  type MessageReaction,
  type User,
} from "../types";
import ReadReceiptsModal from "../components/ReadReceiptsModal";
import MessageStatusModal from "../components/MessageStatusModal";
import ReactionDetailsModal from "../components/ReactionDetailsModal";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { AppLauncher } from "@capacitor/app-launcher";
import { Geolocation } from "@capacitor/geolocation";
import { KeyboardHandler } from "../utils/keyboardHandler";
import { PushNotificationManager } from "../utils/pushNotificationManager";
import { readChatDrafts, writeChatDraft } from "../utils/chatDrafts";
import { getChatLiveLocationState } from "../utils/chatLiveLocationState";
import { getChatMessageStatusColor } from "../utils/chatMessageStatus";
import { SwipeNavigationManager } from "../utils/swipeNavigation";
import {
  getMicrophonePermissionErrorMessage,
  requestVoiceNoteMediaStream,
} from "../utils/voiceNoteMedia";
import { usePageTracking } from "../hooks/usePageTracking";
import { fallbackImage } from "../utils/getCachedImage";
import { crashlytics, storage, db } from "../services/firebase";
import {
  collection,
  query,
  updateDoc,
  doc as firestoreDoc,
  onSnapshot,
  setDoc,
  where,
} from "firebase/firestore";
import { useCachedImage } from "../hooks/useCachedImage";
import { markChatMessagesAsRead } from "../services/functionsApi";
import {
  getActiveChatLiveLocationShareId,
  startChatLiveLocationShare,
  stopChatLiveLocationShare,
} from "../services/chatLiveLocationService";
import CameraService from "../services/cameraService";
import {
  CHAT_REACTION_QUICK_DEFAULT,
} from "../constants";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
} from "firebase/storage";

interface ChatBubbleProps {
  message: ChatMessage;
  sender: User | null;
  activeLiveLocationShare?: ChatLiveLocationShareRecord | null;
  onReply: (message: ChatMessage, sender: User) => void;
  onRetryFailedMessage?: (message: ChatMessage) => void;
  isSlowSending?: boolean;
  onEnterSelectionMode?: (messageId: string) => void;
  onToggleSelection?: (messageId: string) => void;
  selectionMode?: boolean;
  isSelectedForAction?: boolean;
  onReplyClick?: (messageId: string) => void;
  repliedToSender?: User | null;
  isGroupChat: boolean;
  participants: User[];
  onShowReadReceipts?: (message: ChatMessage) => void;
  onAddReaction?: (message: ChatMessage, emoji: string) => void;
  onOpenMedia?: (media: ChatMediaAttachment) => void;
  onDownloadMedia?: (media: ChatMediaAttachment) => Promise<boolean>;
  onShowMessageStatus?: (message: ChatMessage) => void;
  onShowReactionDetails?: (emoji: string, message: ChatMessage) => void; // Callback to show reaction details
  onReactionUsed?: (emoji: string) => void;
  quickReactionEmojis?: string[];
  isReactingTo?: boolean; // Whether this message is currently being reacted to
  showSenderName?: boolean; // Whether to show sender name (if message is from different user than previous)
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>; // Reference to input to refocus after reaction picker opens
  isOpeningReactionPickerRef?: React.RefObject<boolean>; // Flag to prevent scroll when opening reaction picker
  isNavigatingToReplyRef?: React.RefObject<boolean>; // Flag to prevent blur when navigating to reply message
  onPickerStateChange?: (isOpen: boolean) => void; // Callback when local picker opens/closes
  senderStatus?: "active" | "left" | "kicked"; // Whether the sender is an active participant, has left, or been kicked
  organizerId?: string | null; // Activity creator id for organizer hint
  keyboardHeight?: number;
}

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

const timeAgoPlain = (dateInput?: string) => {
  if (!dateInput) return "N/A";
  const date = toJsDate(dateInput);
  if (!date || isNaN(date.getTime())) return "N/A";
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return `${Math.floor(interval)}y ago`;
  interval = seconds / 2592000;
  if (interval > 1) return `${Math.floor(interval)}mo ago`;
  interval = seconds / 86400;
  if (interval > 1) return `${Math.floor(interval)}d ago`;
  interval = seconds / 3600;
  if (interval > 1) return `${Math.floor(interval)}h ago`;
  interval = seconds / 60;
  if (interval > 1) return `${Math.floor(interval)}m ago`;
  return `${Math.floor(seconds)}s ago`;
};

const formatDateSeparator = (dateInput: string) => {
  const date = toJsDate(dateInput);
  if (!date || isNaN(date.getTime())) return "";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const messageDate = new Date(date);
  messageDate.setHours(0, 0, 0, 0);

  if (messageDate.getTime() === today.getTime()) {
    return "Today";
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return "Yesterday";
  } else {
    // Format as "January 10, 2026" or "Jan 10, 2026" depending on how recent
    const daysDiff = Math.floor(
      (today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (daysDiff < 7) {
      // For last week, show day of week
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
      });
    } else {
      // For older messages, show full date
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  }
};

const isSameDay = (date1: string, date2: string) => {
  const d1 = toJsDate(date1);
  const d2 = toJsDate(date2);
  if (!d1 || !d2 || isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

const CHAT_REACTION_PICKER_WIDTH_PX = 280;
const CHAT_REACTION_LONG_PRESS_MS = 400;
const CHAT_RECENT_REACTIONS_STORAGE_KEY = "chat-recent-reactions-v1";
const CHAT_REACTION_DISMISS_GUARD_MS = 500;
const CHAT_REACTION_PICKER_COLLAPSED_HEIGHT_PX = 56;
const CHAT_REACTION_PICKER_EXPANDED_HEIGHT_PX = 104;
const CHAT_REACTION_PICKER_MARGIN_PX = 8;
const CHAT_REACTION_PICKER_GAP_PX = 8;

const areStringArraysEqual = (
  left: string[] | undefined,
  right: string[] | undefined,
) => {
  const normalizedLeft = left || [];
  const normalizedRight = right || [];

  if (normalizedLeft.length !== normalizedRight.length) return false;

  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

const areReactionArraysEqual = (
  left: MessageReaction[] | undefined,
  right: MessageReaction[] | undefined,
) => {
  const normalizedLeft = left || [];
  const normalizedRight = right || [];

  if (normalizedLeft.length !== normalizedRight.length) return false;

  return normalizedLeft.every((reaction, index) => {
    const other = normalizedRight[index];
    return (
      reaction.emoji === other?.emoji &&
      reaction.userId === other?.userId &&
      reaction.timestamp === other?.timestamp
    );
  });
};

const splitGraphemes = (value: string): string[] => {
  if (!value) return [];

  if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    return Array.from(segmenter.segment(value), (item) => item.segment);
  }

  return Array.from(value);
};

const extractReactionCandidates = (value: string, limit: number): string[] => {
  const trimmed = value.trim();
  if (!trimmed) return [];

  const graphemes = splitGraphemes(trimmed)
    .map((item) => item.trim())
    .filter(Boolean);

  if (graphemes.length === 0) return [];

  // Keep latest keyboard picks first and dedupe, capped to picker width.
  const dedupedLatestFirst: string[] = [];
  for (let index = graphemes.length - 1; index >= 0; index -= 1) {
    const grapheme = graphemes[index];
    if (dedupedLatestFirst.includes(grapheme)) continue;
    dedupedLatestFirst.push(grapheme);
    if (dedupedLatestFirst.length >= limit) break;
  }

  return dedupedLatestFirst;
};

const normalizeCustomReaction = (value: string): string | null => {
  const candidates = extractReactionCandidates(value, 1);
  if (candidates.length === 0) return null;
  return candidates[0];
};

const readRecentReactionsByUser = (): Record<string, string[]> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(CHAT_RECENT_REACTIONS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string[]>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const getRecentReactionsForUser = (userId: string): string[] => {
  const all = readRecentReactionsByUser();
  const list = all[userId];
  if (!Array.isArray(list)) return [];
  return list
    .map((emoji) => normalizeCustomReaction(emoji))
    .filter((emoji): emoji is string => Boolean(emoji));
};

const writeRecentReactionForUser = (userId: string, emoji: string): string[] => {
  const normalized = normalizeCustomReaction(emoji);
  if (!normalized || typeof window === "undefined") return [];

  const all = readRecentReactionsByUser();
  const existing = Array.isArray(all[userId]) ? all[userId] : [];
  const deduped = [normalized, ...existing.filter((value) => value !== normalized)];
  const limited = deduped.slice(0, CHAT_REACTION_QUICK_DEFAULT.length);
  all[userId] = limited;
  localStorage.setItem(CHAT_RECENT_REACTIONS_STORAGE_KEY, JSON.stringify(all));
  return limited;
};

const CHAT_ACTIVITY_DETAIL_TTL_MS = 5 * 60 * 1000;
type ActivityDetailCacheEntry = {
  activity: ActivityRequest | null;
  fetchedAt: number;
};

const activityChatDetailCache = new Map<string, ActivityDetailCacheEntry>();
const activityChatDetailInFlight = new Map<string, Promise<ActivityRequest | null>>();
const dismissedKeyboardByChatId = new Set<string>();

const getCachedActivityDetail = (activityId: string): ActivityRequest | null | undefined => {
  const cached = activityChatDetailCache.get(activityId);
  if (!cached) return undefined;

  const isExpired = Date.now() - cached.fetchedAt > CHAT_ACTIVITY_DETAIL_TTL_MS;
  if (isExpired) {
    activityChatDetailCache.delete(activityId);
    return undefined;
  }

  return cached.activity;
};

const setCachedActivityDetail = (
  activityId: string,
  activity: ActivityRequest | null,
) => {
  activityChatDetailCache.set(activityId, {
    activity,
    fetchedAt: Date.now(),
  });
};

type ChatComposerMediaItem = {
  id: string;
  file: File;
  previewUrl: string;
  type: "image" | "video" | "audio" | "document";
};

type PendingChatSendPayload = {
  clientRequestId: string;
  inputSnapshot: string;
  replySnapshot: {
    message: ChatMessage;
    sender: User;
  } | null;
  mediaSnapshot: ChatComposerMediaItem[];
  cancelled?: boolean;
};

type ChatRuntimeReceipt = {
  readBy?: Record<string, string>;
  deliveredTo?: Record<string, string>;
};

type ChatRuntimeStarsByMessage = Record<string, Record<string, boolean>>;

// Firestore sidecar document: chatMeta/{chatId}
// {
//   typing: { [userId]: ISO timestamp | null },
//   receipts: {
//     [messageId]: {
//       readBy: { [userId]: ISO timestamp },
//       deliveredTo: { [userId]: ISO timestamp }
//     }
//   },
//   stars: {
//     [messageId]: { [userId]: true }
//   },
//   updatedAt: ISO timestamp
// }

const CHAT_MEDIA_MAX_FILES = 6;
const CHAT_MEDIA_MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
const CHAT_MEDIA_INPUT_ID = "chat-media-input";
const CHAT_MEDIA_ACCEPT_ALL =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx";
const CHAT_MEDIA_ACCEPT_VISUAL = "image/*,video/*";
const CHAT_MEDIA_ACCEPT_AUDIO = "audio/*";
const CHAT_MEDIA_ACCEPT_DOCUMENT =
  ".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.ppt,.pptx";
const CHAT_COMPOSER_MAX_HEIGHT_PX = 120;
const CHAT_COMPOSER_TEXT_SIZE_PX = 15;
const CHAT_UPLOAD_TIMEOUT_MS = 90_000;
const CHAT_SEND_TIMEOUT_MS = 30_000;
const CHAT_SLOW_SEND_THRESHOLD_MS = 20_000;
const CHAT_VOICE_NOTE_MIN_DURATION_MS = 700;
const CHAT_MESSAGE_ACTION_WINDOW_MS = 10 * 60 * 1000;
const CHAT_OUTBOX_STORAGE_KEY = "chat-outbox-v1";
const CHAT_REACTION_OUTBOX_STORAGE_KEY = "chat-reaction-outbox-v1";
const CHAT_STAR_OUTBOX_STORAGE_KEY = "chat-star-outbox-v1";
const CHAT_OUTBOX_MEDIA_DB_NAME = "chat-outbox-media-v1";
const CHAT_OUTBOX_MEDIA_STORE_NAME = "mediaByClientRequestId";
const CHAT_OUTBOX_RETRY_BASE_DELAY_MS = 2_000;
const CHAT_OUTBOX_RETRY_MAX_DELAY_MS = 60_000;
const CHAT_REACTION_RETRY_BASE_DELAY_MS = 2_000;
const CHAT_REACTION_RETRY_MAX_DELAY_MS = 60_000;
const CHAT_STAR_RETRY_BASE_DELAY_MS = 2_000;
const CHAT_STAR_RETRY_MAX_DELAY_MS = 60_000;
const MEMBER_NAME_COLORS = [
  "#E53935",
  "#1E88E5",
  "#43A047",
  "#8E24AA",
  "#FB8C00",
  "#00897B",
  "#7CB342",
  "#D81B60",
  "#3949AB",
  "#6D4C41",
  "#546E7A",
  "#00ACC1",
];

const getSenderNameColor = (userId: string) => {
  let hash = 0;
  for (let index = 0; index < userId.length; index++) {
    hash = (hash * 31 + userId.charCodeAt(index)) >>> 0;
  }
  return MEMBER_NAME_COLORS[hash % MEMBER_NAME_COLORS.length];
};

const formatFileSize = (size: number) => {
  if (!size || Number.isNaN(size)) return "Unknown size";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDurationClock = (durationMs: number) => {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const isVoiceNoteFileName = (fileName?: string) =>
  Boolean(fileName && fileName.toLowerCase().startsWith("voice-note-"));

const getSupportedVoiceNoteMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";

  const candidates = [
    "audio/webm;codecs=opus",
    "audio/mp4",
    "audio/webm",
    "audio/ogg;codecs=opus",
  ];

  return candidates.find((mimeType) => {
    if (typeof MediaRecorder.isTypeSupported !== "function") return false;
    return MediaRecorder.isTypeSupported(mimeType);
  }) || "";
};

const getVoiceNoteFileExtension = (mimeType: string) => {
  if (mimeType.includes("mp4") || mimeType.includes("aac")) return "m4a";
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("mpeg")) return "mp3";
  return "webm";
};

type ChatLiveLocationShareRecord = {
  id: string;
  chatId: string;
  senderId: string;
  latitude: number;
  longitude: number;
  label?: string;
  placeName?: string;
  accuracyMeters?: number;
  updatedAt?: string;
  expiresAt?: string;
  isActive?: boolean;
};

type LiveLocationDurationOption = {
  id: string;
  label: string;
  durationMs?: number;
  kind: "static" | "live";
};

const LIVE_LOCATION_DURATION_OPTIONS: LiveLocationDurationOption[] = [
  { id: "current", label: "Share current location", kind: "static" },
  { id: "15m", label: "Share live for 15 minutes", kind: "live", durationMs: 15 * 60 * 1000 },
  { id: "1h", label: "Share live for 1 hour", kind: "live", durationMs: 60 * 60 * 1000 },
  { id: "8h", label: "Share live for 8 hours", kind: "live", durationMs: 8 * 60 * 60 * 1000 },
];

const buildChatLocationMapsUrl = (location: {
  latitude: number;
  longitude: number;
  kind?: "static" | "live";
  label?: string;
}) => {
  const queryValue = `${location.latitude},${location.longitude}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(queryValue)}`;
};

const formatLiveLocationRemaining = (expiresAt?: string) => {
  if (!expiresAt) return null;
  const remainingMs = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return "Ended";
  const minutes = Math.ceil(remainingMs / 60000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const leftoverMinutes = minutes % 60;
    return leftoverMinutes > 0 ? `${hours}h ${leftoverMinutes}m left` : `${hours}h left`;
  }
  return `${minutes}m left`;
};

type ChatOutboxRecord = {
  chatId: string;
  clientRequestId: string;
  text: string;
  createdAt: string;
  status: "queued" | "failed";
  format?: "plain" | "whatsapp";
  replyTo?: {
    messageId: string;
    senderId: string;
    senderUsername: string;
    text: string;
  };
  hasMedia?: boolean;
  attempts?: number;
  nextAttemptAt?: string;
};

type ChatReactionOutboxRecord = {
  id: string;
  chatId: string;
  messageId: string;
  emoji: string;
  createdAt: string;
  attempts: number;
  nextAttemptAt: string;
};

type ChatStarOutboxRecord = {
  id: string;
  chatId: string;
  messageId: string;
  targetStarred: boolean;
  createdAt: string;
  attempts: number;
  nextAttemptAt: string;
};

type PersistedOutboxMediaItem = {
  id: string;
  type: ChatComposerMediaItem["type"];
  blob: Blob;
  mimeType: string;
  fileName: string;
  size: number;
};

type PersistedOutboxMediaRecord = {
  clientRequestId: string;
  chatId: string;
  createdAt: string;
  items: PersistedOutboxMediaItem[];
};

const readChatOutbox = (): ChatOutboxRecord[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(CHAT_OUTBOX_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatOutboxRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeChatOutbox = (records: ChatOutboxRecord[]) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(CHAT_OUTBOX_STORAGE_KEY, JSON.stringify(records));
};

const upsertChatOutboxRecord = (nextRecord: ChatOutboxRecord) => {
  const nowIso = new Date().toISOString();
  const normalizedRecord: ChatOutboxRecord = {
    ...nextRecord,
    attempts: nextRecord.attempts ?? 0,
    nextAttemptAt: nextRecord.nextAttemptAt ?? nowIso,
  };

  const records = readChatOutbox();
  const next = records.filter(
    (record) => record.clientRequestId !== normalizedRecord.clientRequestId,
  );
  next.push(normalizedRecord);
  writeChatOutbox(next);
};

const removeChatOutboxRecord = (clientRequestId: string) => {
  const records = readChatOutbox();
  const next = records.filter((record) => record.clientRequestId !== clientRequestId);
  writeChatOutbox(next);
};

const updateChatOutboxRecord = (
  clientRequestId: string,
  updater: (record: ChatOutboxRecord) => ChatOutboxRecord,
) => {
  const records = readChatOutbox();
  let changed = false;
  const next = records.map((record) => {
    if (record.clientRequestId !== clientRequestId) return record;
    changed = true;
    return updater(record);
  });
  if (changed) {
    writeChatOutbox(next);
  }
};

const readReactionOutbox = (): ChatReactionOutboxRecord[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(CHAT_REACTION_OUTBOX_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatReactionOutboxRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeReactionOutbox = (records: ChatReactionOutboxRecord[]) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(CHAT_REACTION_OUTBOX_STORAGE_KEY, JSON.stringify(records));
};

const enqueueReactionOutboxRecord = (
  chatId: string,
  messageId: string,
  emoji: string,
) => {
  const normalizedEmoji = normalizeCustomReaction(emoji);
  if (!normalizedEmoji) return;

  const records = readReactionOutbox();
  const duplicate = records.some(
    (record) =>
      record.chatId === chatId &&
      record.messageId === messageId &&
      record.emoji === normalizedEmoji,
  );
  if (duplicate) return;

  const nowIso = new Date().toISOString();
  const record: ChatReactionOutboxRecord = {
    id: `rxn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    chatId,
    messageId,
    emoji: normalizedEmoji,
    createdAt: nowIso,
    attempts: 0,
    nextAttemptAt: nowIso,
  };

  writeReactionOutbox([...records, record]);
};

const removeReactionOutboxRecord = (id: string) => {
  const records = readReactionOutbox();
  const next = records.filter((record) => record.id !== id);
  writeReactionOutbox(next);
};

const updateReactionOutboxRecord = (
  id: string,
  updater: (record: ChatReactionOutboxRecord) => ChatReactionOutboxRecord,
) => {
  const records = readReactionOutbox();
  let changed = false;
  const next = records.map((record) => {
    if (record.id !== id) return record;
    changed = true;
    return updater(record);
  });
  if (changed) {
    writeReactionOutbox(next);
  }
};

const readStarOutbox = (): ChatStarOutboxRecord[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(CHAT_STAR_OUTBOX_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatStarOutboxRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStarOutbox = (records: ChatStarOutboxRecord[]) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(CHAT_STAR_OUTBOX_STORAGE_KEY, JSON.stringify(records));
};

const upsertStarOutboxRecord = (
  chatId: string,
  messageId: string,
  targetStarred: boolean,
) => {
  const records = readStarOutbox();
  const nowIso = new Date().toISOString();
  const next = records.filter(
    (record) => !(record.chatId === chatId && record.messageId === messageId),
  );

  next.push({
    id: `star-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    chatId,
    messageId,
    targetStarred,
    createdAt: nowIso,
    attempts: 0,
    nextAttemptAt: nowIso,
  });

  writeStarOutbox(next);
};

const removeStarOutboxRecord = (id: string) => {
  const records = readStarOutbox();
  writeStarOutbox(records.filter((record) => record.id !== id));
};

const updateStarOutboxRecord = (
  id: string,
  updater: (record: ChatStarOutboxRecord) => ChatStarOutboxRecord,
) => {
  const records = readStarOutbox();
  let changed = false;
  const next = records.map((record) => {
    if (record.id !== id) return record;
    changed = true;
    return updater(record);
  });
  if (changed) {
    writeStarOutbox(next);
  }
};

const openOutboxMediaDb = (): Promise<IDBDatabase | null> => {
  if (typeof window === "undefined" || typeof indexedDB === "undefined") {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const request = indexedDB.open(CHAT_OUTBOX_MEDIA_DB_NAME, 1);

    request.onupgradeneeded = () => {
      const dbInstance = request.result;
      if (!dbInstance.objectStoreNames.contains(CHAT_OUTBOX_MEDIA_STORE_NAME)) {
        dbInstance.createObjectStore(CHAT_OUTBOX_MEDIA_STORE_NAME, {
          keyPath: "clientRequestId",
        });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
};

const putOutboxMediaRecord = async (record: PersistedOutboxMediaRecord) => {
  const dbInstance = await openOutboxMediaDb();
  if (!dbInstance) return;

  await new Promise<void>((resolve) => {
    const tx = dbInstance.transaction(CHAT_OUTBOX_MEDIA_STORE_NAME, "readwrite");
    const store = tx.objectStore(CHAT_OUTBOX_MEDIA_STORE_NAME);
    store.put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
};

const getOutboxMediaRecord = async (
  clientRequestId: string,
): Promise<PersistedOutboxMediaRecord | null> => {
  const dbInstance = await openOutboxMediaDb();
  if (!dbInstance) return null;

  return await new Promise<PersistedOutboxMediaRecord | null>((resolve) => {
    const tx = dbInstance.transaction(CHAT_OUTBOX_MEDIA_STORE_NAME, "readonly");
    const store = tx.objectStore(CHAT_OUTBOX_MEDIA_STORE_NAME);
    const request = store.get(clientRequestId);
    request.onsuccess = () => resolve((request.result as PersistedOutboxMediaRecord) || null);
    request.onerror = () => resolve(null);
  });
};

const deleteOutboxMediaRecord = async (clientRequestId: string) => {
  const dbInstance = await openOutboxMediaDb();
  if (!dbInstance) return;

  await new Promise<void>((resolve) => {
    const tx = dbInstance.transaction(CHAT_OUTBOX_MEDIA_STORE_NAME, "readwrite");
    const store = tx.objectStore(CHAT_OUTBOX_MEDIA_STORE_NAME);
    store.delete(clientRequestId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
};

const persistOutboxMediaSnapshot = async (
  chatId: string,
  clientRequestId: string,
  mediaSnapshot: ChatComposerMediaItem[],
) => {
  if (mediaSnapshot.length === 0) return;

  const record: PersistedOutboxMediaRecord = {
    clientRequestId,
    chatId,
    createdAt: new Date().toISOString(),
    items: mediaSnapshot.map((item) => ({
      id: item.id,
      type: item.type,
      blob: item.file,
      mimeType: item.file.type,
      fileName: item.file.name,
      size: item.file.size,
    })),
  };

  await putOutboxMediaRecord(record);
};

const hydrateOutboxMediaSnapshot = async (
  clientRequestId: string,
): Promise<ChatComposerMediaItem[]> => {
  const record = await getOutboxMediaRecord(clientRequestId);
  if (!record || !record.items.length) return [];

  return record.items.map((item) => {
    const file = new File([item.blob], item.fileName, {
      type: item.mimeType,
      lastModified: Date.now(),
    });

    return {
      id: item.id,
      file,
      previewUrl: URL.createObjectURL(item.blob),
      type: item.type,
    };
  });
};

const detectChatAttachmentType = (
  file: File,
): ChatComposerMediaItem["type"] | null => {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";

  const lowered = file.name.toLowerCase();
  if (
    file.type.startsWith("application/") ||
    [".pdf", ".doc", ".docx", ".txt", ".csv", ".xls", ".xlsx", ".ppt", ".pptx"].some((ext) =>
      lowered.endsWith(ext),
    )
  ) {
    return "document";
  }

  return null;
};

const sanitizeAttachmentFileName = (
  fileName: string | undefined,
  fallbackPrefix: string,
) => {
  const trimmed = (fileName || "").trim();
  const sanitized = trimmed.replace(/[\\/:*?"<>|]+/g, "_");
  if (sanitized.length > 0) return sanitized;
  return `${fallbackPrefix}-${Date.now()}`;
};

const triggerAttachmentDownload = (href: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  link.target = "_blank";
  link.rel = "noreferrer";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadChatAttachment = async (media: ChatMediaAttachment): Promise<boolean> => {
  const fallbackPrefix = media.type === "document" ? "document" : "media";
  const fileName = sanitizeAttachmentFileName(media.fileName, fallbackPrefix);
  const mediaUrl = (media.url || "").trim();

  if (!mediaUrl) {
    return false;
  }

  // Native WebViews frequently block anchor downloads/popups after async work.
  // Open directly with the OS URL handler so Android routes to the default browser.
  if (Capacitor.isNativePlatform()) {
    try {
      const result = await AppLauncher.openUrl({
        url: mediaUrl,
      });
      if (result.completed !== false) return true;
    } catch {
      // Fall through to browser-style fallbacks
    }

    try {
      const openedWindow = window.open(
        mediaUrl,
        "_blank",
        "noopener,noreferrer",
      );
      if (openedWindow) return true;
    } catch {
      // Fall through to anchor fallback
    }

    try {
      triggerAttachmentDownload(mediaUrl, fileName);
      return true;
    } catch {
      return false;
    }
  }

  try {
    const response = await fetch(mediaUrl);
    if (!response.ok) {
      throw new Error(`Attachment fetch failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    triggerAttachmentDownload(objectUrl, fileName);
    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 1000);
    return true;
  } catch {
    try {
      triggerAttachmentDownload(mediaUrl, fileName);
      return true;
    } catch {
      return false;
    }
  }
};

const openChatAttachmentInBrowser = async (
  media: ChatMediaAttachment,
): Promise<boolean> => {
  const mediaUrl = (media.url || "").trim();
  if (!mediaUrl) return false;

  if (Capacitor.isNativePlatform()) {
    try {
      const result = await AppLauncher.openUrl({
        url: mediaUrl,
      });
      if (result.completed !== false) return true;
    } catch {
      // Fall through to web-style fallback
    }
  }

  try {
    const openedWindow = window.open(
      mediaUrl,
      "_blank",
      "noopener,noreferrer",
    );
    if (openedWindow) return true;
  } catch {
    // Fall through to anchor fallback
  }

  try {
    const link = document.createElement("a");
    link.href = mediaUrl;
    link.target = "_blank";
    link.rel = "noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  } catch {
    return false;
  }
};

const isMessageWithinWindow = (
  timestamp: string | undefined,
  windowMs: number,
): boolean => {
  if (!timestamp) return false;
  const messageDate = toJsDate(timestamp);
  if (!messageDate || Number.isNaN(messageDate.getTime())) return false;
  return Date.now() - messageDate.getTime() <= windowMs;
};

export const getSearchMatchIds = (
  messages: ChatMessage[],
  query: string,
  searchActive: boolean,
): string[] => {
  if (!searchActive || !query.trim()) return [];
  const lowered = query.toLowerCase();
  return messages
    .filter((message) => !message.deletedForEveryone && message.text?.toLowerCase().includes(lowered))
    .map((message) => message.id);
};

export const buildCopiedContentFromMessages = (messages: ChatMessage[]): string => {
  return messages
    .map((message) => {
      const text = (message.text || "").trim();
      const mediaLinks = (message.media || []).map((item) => item.url).filter(Boolean);
      return [text, ...mediaLinks].filter(Boolean).join("\n");
    })
    .filter(Boolean)
    .join("\n\n");
};

export const groupMessageReactions = (
  reactions: MessageReaction[] | undefined,
): Array<{ emoji: string; userIds: string[]; count: number }> => {
  if (!reactions || reactions.length === 0) return [];

  const grouped = new Map<string, { emoji: string; userIds: string[]; count: number }>();
  reactions.forEach((reaction) => {
    const existing = grouped.get(reaction.emoji);
    if (existing) {
      existing.userIds.push(reaction.userId);
      existing.count += 1;
      return;
    }

    grouped.set(reaction.emoji, {
      emoji: reaction.emoji,
      userIds: [reaction.userId],
      count: 1,
    });
  });

  return Array.from(grouped.values());
};

export const isMessageStarredByUser = (
  message: ChatMessage,
  userId?: string,
): boolean => {
  if (!userId) return false;
  return Boolean(message.starredBy?.includes(userId));
};

export const getSelectionActionAvailability = (params: {
  primarySelectedMessage: ChatMessage | null;
  selectedMessages: ChatMessage[];
  selectedCount: number;
  currentUserId?: string;
  pendingSendMessageIds: Set<string> | Map<string, unknown>;
  actionWindowMs: number;
}) => {
  const {
    primarySelectedMessage,
    selectedMessages,
    selectedCount,
    currentUserId,
    pendingSendMessageIds,
    actionWindowMs,
  } = params;
  const hasPendingMessage = (messageId: string) => pendingSendMessageIds.has(messageId);

  const canReplySelected =
    Boolean(primarySelectedMessage) && !primarySelectedMessage?.deletedForEveryone;
  const canReactSelected = selectedCount > 0;
  const canEditPendingSelected =
    Boolean(primarySelectedMessage) &&
    primarySelectedMessage?.senderId === currentUserId &&
    (primarySelectedMessage?.status === "sending" ||
      primarySelectedMessage?.status === "failed") &&
    hasPendingMessage(primarySelectedMessage.id);
  const canEditSelected =
    canEditPendingSelected ||
    (Boolean(primarySelectedMessage) &&
      primarySelectedMessage?.senderId === currentUserId &&
      !primarySelectedMessage?.deletedForEveryone &&
      isMessageWithinWindow(primarySelectedMessage?.timestamp, actionWindowMs));
  const canRetrySelected =
    Boolean(primarySelectedMessage) &&
    primarySelectedMessage?.status === "failed" &&
    hasPendingMessage(primarySelectedMessage.id);
  const canCancelSelected =
    Boolean(primarySelectedMessage) &&
    primarySelectedMessage?.status === "sending" &&
    hasPendingMessage(primarySelectedMessage.id);
  const canDeleteForEveryoneSelected =
    selectedCount > 0 &&
    selectedMessages.every(
      (message) =>
        message.senderId === currentUserId &&
        !message.deletedForEveryone &&
        isMessageWithinWindow(message.timestamp, actionWindowMs),
    );
  const canDeleteSelected =
    selectedCount > 0 &&
    selectedMessages.some((message) =>
      isMessageWithinWindow(message.timestamp, actionWindowMs),
    );

  return {
    canReplySelected,
    canReactSelected,
    canEditPendingSelected,
    canEditSelected,
    canRetrySelected,
    canCancelSelected,
    canDeleteForEveryoneSelected,
    canDeleteSelected,
  };
};

export const isForwardTargetEligible = (params: {
  candidate: Chat;
  currentUserId?: string;
  currentChatId?: string;
  candidateActivity?: ActivityRequest | null;
}) => {
  const { candidate, currentUserId, currentChatId, candidateActivity } = params;
  if (!currentUserId) return false;
  if (candidate.id === currentChatId) return false;
  if (!candidate.participants.includes(currentUserId)) return false;
  if (candidate.leftParticipantIds?.includes(currentUserId)) return false;

  if (candidate.activityId && candidate.activityStatus === "inactive") {
    return false;
  }

  if (candidate.activityId) {
    if (candidate.activityStatus !== "active") {
      return false;
    }

    if (!candidateActivity) {
      return false;
    }

    if (candidateActivity.isDeleted || candidateActivity.isReported) {
      return false;
    }

    const endTime =
      toJsDate(candidateActivity.dateTime).getTime() +
      candidateActivity.duration * 60 * 1000;
    if (endTime < Date.now()) {
      return false;
    }

    const isParticipantInActivity = Boolean(
      candidateActivity.participantIds?.includes(currentUserId) ||
        candidateActivity.participants?.some(
          (participant) => participant.id === currentUserId,
        ),
    );

    if (!isParticipantInActivity) {
      return false;
    }

    if (candidateActivity.kickedParticipantIds?.includes(currentUserId)) {
      return false;
    }
  }

  return true;
};

const withTimeout = async <T,>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const formatMessageToken = (token: string): "url" | "bold" | "italic" | "strike" | "mono" | "text" => {
  if (/^https?:\/\//i.test(token)) return "url";
  if (/^\*[^*\n]+\*$/.test(token)) return "bold";
  if (/^_[^_\n]+_$/.test(token)) return "italic";
  if (/^~[^~\n]+~$/.test(token)) return "strike";
  if (/^`[^`\n]+`$/.test(token)) return "mono";
  return "text";
};

const renderFormattedMessage = (text: string, isCurrentUser: boolean) => {
  const lines = text.split("\n");
  const textToneClass = isCurrentUser ? "text-white" : "text-gray-900";
  const subtleToneClass = isCurrentUser ? "text-white/90" : "text-gray-700";

  return (
    <span className={`text-sm break-words whitespace-pre-wrap flex-1 ${textToneClass}`}>
      {lines.map((line, lineIndex) => {
        const parts = line.split(/(https?:\/\/[^\s]+|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`)/g);
        return (
          <React.Fragment key={`line-${lineIndex}`}>
            {parts.map((part, index) => {
              if (!part) return null;

              const tokenType = formatMessageToken(part);
              if (tokenType === "url") {
                return (
                  <a
                    key={`${lineIndex}-${index}`}
                    href={part}
                    target="_blank"
                    rel="noreferrer"
                    data-message-link="true"
                    className="underline break-all"
                  >
                    {part}
                  </a>
                );
              }

              if (tokenType === "bold") {
                return <strong key={`${lineIndex}-${index}`}>{part.slice(1, -1)}</strong>;
              }

              if (tokenType === "italic") {
                return <em key={`${lineIndex}-${index}`}>{part.slice(1, -1)}</em>;
              }

              if (tokenType === "strike") {
                return (
                  <span key={`${lineIndex}-${index}`} className="line-through">
                    {part.slice(1, -1)}
                  </span>
                );
              }

              if (tokenType === "mono") {
                return (
                  <code
                    key={`${lineIndex}-${index}`}
                    className={`px-1 rounded ${isCurrentUser ? "bg-black/20" : "bg-gray-200"} ${subtleToneClass}`}
                  >
                    {part.slice(1, -1)}
                  </code>
                );
              }

              return <React.Fragment key={`${lineIndex}-${index}`}>{part}</React.Fragment>;
            })}
            {lineIndex < lines.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </span>
  );
};

type TouchBlurGuardConfig = {
  targetRef: React.RefObject<HTMLElement>;
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  platform: string;
  allowSelectors?: string[];
  navigationFlagRef?: React.RefObject<boolean>;
  keyboardWasOpenRef?: React.MutableRefObject<boolean>;
  selectionMode?: boolean;
};

const useAndroidTouchBlurGuard = ({
  targetRef,
  inputRef,
  platform,
  allowSelectors = [],
  navigationFlagRef,
  keyboardWasOpenRef,
  selectionMode = false,
}: TouchBlurGuardConfig) => {
  // Use a ref so handlers always read the latest selectionMode value without
  // needing it in the effect dependency array. Adding selectionMode to deps
  // would tear down & re-attach listeners mid-touch (e.g. when a long-press
  // triggers enterSelectionMode), which resets isTouchActive and breaks the
  // blur guard.
  const selectionModeRef = useRef(selectionMode);
  useEffect(() => { selectionModeRef.current = selectionMode; });

  useEffect(() => {
    if (platform !== "android" || !targetRef.current || !inputRef?.current)
      return;

    const targetEl = targetRef.current;
    const inputEl = inputRef.current;
    let startY = 0;
    let startX = 0;
    let startTime = 0;
    let isScrolling = false;
    let isTouchActive = false;

    const matchesAllowedSelector = (el: HTMLElement | null) => {
      if (!el) return false;
      return allowSelectors.some((sel) => el.closest(sel));
    };

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
      startTime = Date.now();
      isScrolling = false;
      isTouchActive = true;
      if (keyboardWasOpenRef) {
        const isKeyboardOpen =
          inputRef?.current && document.activeElement === inputRef.current;
        keyboardWasOpenRef.current = Boolean(isKeyboardOpen);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      const deltaY = Math.abs(e.touches[0].clientY - startY);
      if (deltaY > 20) {
        isScrolling = true;
      }
      // Horizontal swipe (reply gesture) — stop guarding so blur guard doesn't
      // interfere with the swipe or cause a spurious keyboard restart.
      const deltaX = Math.abs(e.touches[0].clientX - startX);
      if (deltaX > 10) {
        isTouchActive = false;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const touchDuration = Date.now() - startTime;
      const currentX = e.changedTouches[0].clientX;
      const deltaX = currentX - startX;
      const deltaY = Math.abs(e.changedTouches[0].clientY - startY);
      const isSwipeGesture = Math.abs(deltaX) > 10 && deltaY < 30;
      const isLongPress = touchDuration > 300;

      if (matchesAllowedSelector(target)) {
        isTouchActive = false;
        isScrolling = false;
        return;
      }

      if (isSwipeGesture) {
        isTouchActive = false;
        isScrolling = false;
        return;
      }

      const keyboardWasOpen = keyboardWasOpenRef
        ? keyboardWasOpenRef.current
        : inputRef?.current && document.activeElement === inputRef.current;

      if (keyboardWasOpen && (isLongPress || !isScrolling)) {
        if (e.cancelable) {
          e.preventDefault();
          if (!selectionModeRef.current) {
            e.stopPropagation();
          }
        }
        setTimeout(() => {
          isTouchActive = false;
        }, 100);
        isScrolling = false;
        return;
      }
      isTouchActive = false;
      isScrolling = false;
    };

    const handleTouchCancel = () => {
      const isKeyboardOpen =
        inputRef?.current && document.activeElement === inputRef.current;
      if (!isKeyboardOpen) {
        isTouchActive = false;
      }
      isScrolling = false;
    };

    const handleBlur = (e: FocusEvent) => {
      const isComposerSuppressed =
        inputEl.dataset.keyboardSuppressed === "true";
      if (isComposerSuppressed) {
        isTouchActive = false;
        isScrolling = false;
        return;
      }

      const relatedTarget = e.relatedTarget as HTMLElement | null;
      const isAllowed = matchesAllowedSelector(relatedTarget);
      const isNavigating = navigationFlagRef?.current;
      const activeElement = document.activeElement as HTMLElement | null;
      const isActiveAllowed = matchesAllowedSelector(activeElement);

      if (
        isTouchActive &&
        !isScrolling &&
        !isAllowed &&
        !isActiveAllowed &&
        !isNavigating
      ) {
        e.preventDefault();
        e.stopPropagation();

        // Synchronous refocus prevents the one-frame keyboard collapse that
        // a requestAnimationFrame-only refocus would otherwise cause.
        if (inputEl && document.activeElement !== inputEl) {
          inputEl.focus();
        }

        // Also schedule a safety check after focus settles.
        requestAnimationFrame(() => {
          const latestActive = document.activeElement as HTMLElement | null;
          if (matchesAllowedSelector(latestActive)) return;
          if (inputEl && document.activeElement !== inputEl) {
            inputEl.focus();
          }
        });
      }
    };

    targetEl.addEventListener("touchstart", handleTouchStart, {
      passive: true,
      capture: true,
    });
    targetEl.addEventListener("touchmove", handleTouchMove, {
      passive: true,
      capture: true,
    });
    targetEl.addEventListener("touchend", handleTouchEnd, {
      passive: false,
      capture: true,
    });
    targetEl.addEventListener("touchcancel", handleTouchCancel, {
      passive: true,
      capture: true,
    });
    inputEl.addEventListener("blur", handleBlur, { capture: true });

    return () => {
      targetEl.removeEventListener("touchstart", handleTouchStart, {
        capture: true,
      });
      targetEl.removeEventListener("touchmove", handleTouchMove, {
        capture: true,
      });
      targetEl.removeEventListener("touchend", handleTouchEnd, {
        capture: true,
      });
      targetEl.removeEventListener("touchcancel", handleTouchCancel, {
        capture: true,
      });
      inputEl.removeEventListener("blur", handleBlur, { capture: true });
    };
  // NOTE: selectionMode is intentionally excluded — it is read via selectionModeRef
  // so that this effect never re-runs (and tears down listeners) mid-touch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, targetRef, inputRef, allowSelectors, navigationFlagRef, keyboardWasOpenRef]);
};

const ChatBubble = React.memo<ChatBubbleProps>(
  ({
    message,
    sender,
    activeLiveLocationShare = null,
    onReply,
    onRetryFailedMessage,
    isSlowSending = false,
    onEnterSelectionMode,
    onToggleSelection,
    selectionMode = false,
    isSelectedForAction = false,
    onReplyClick,
    repliedToSender,
    isGroupChat,
    participants,
    onShowReadReceipts,
    onAddReaction,
    onOpenMedia,
    onDownloadMedia,
    onShowMessageStatus,
    onShowReactionDetails,
    onReactionUsed,
    quickReactionEmojis = CHAT_REACTION_QUICK_DEFAULT,
    isReactingTo,
    showSenderName = true,
    inputRef,
    isOpeningReactionPickerRef,
    isNavigatingToReplyRef,
    onPickerStateChange,
    senderStatus = "active",
    organizerId = null,
    keyboardHeight = 0,
  }) => {
    const { currentUser } = useAuth();
    const [showReactionPicker, setShowReactionPicker] = useState(false);
    const [showCustomReactionInput, setShowCustomReactionInput] = useState(false);
    const [customReactionInput, setCustomReactionInput] = useState("");
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
    const longPressTimer = useRef<NodeJS.Timeout | null>(null);
    const touchStartPos = useRef<{ x: number; y: number } | null>(null);
    const isSwiping = useRef(false);
    const touchMovedBeyondTapThresholdRef = useRef(false);
    const customReactionInputRef = useRef<HTMLInputElement>(null);
    const reactionPickerDismissGuardUntilRef = useRef(0);
    const messageRef = useRef<HTMLDivElement>(null);
    const wasKeyboardOpenRef = useRef(false);
    const bubbleContainerRef = useRef<HTMLDivElement>(null);
    const messageContentRef = useRef<HTMLDivElement>(null);
    const touchStartedOnInteractiveElementRef = useRef(false);
    const suppressNextClickRef = useRef(false);
    const longPressTriggeredRef = useRef(false);
    const rowTouchStartPosRef = useRef<{ x: number; y: number } | null>(null);
    const rowTouchMovedRef = useRef(false);

    const isAndroid = Capacitor.getPlatform() === "android";
    const isIOS = Capacitor.getPlatform() === "ios";
    const isMobilePlatform = isAndroid || isIOS;
    const isCurrentUser = sender?.id === currentUser?.id;
    const isDeletedForEveryone = Boolean(message.deletedForEveryone);
    const liveLocationState = message.location
      ? getChatLiveLocationState(message.location, activeLiveLocationShare)
      : null;
    const SWIPE_THRESHOLD = 60; // Pixels to swipe before triggering reply
    const swipeProgress = Math.min(Math.abs(swipeOffset) / SWIPE_THRESHOLD, 1);

    const isInteractiveMessageTarget = (target: HTMLElement | null) =>
      Boolean(
        target?.closest(
          "button, a, video, audio, input, textarea, select, label, [role='button'], [data-reaction-button], [data-message-status-button], [data-reply-to-button]",
        ),
      );

    const isSelectionToggleExcludedTarget = (target: HTMLElement | null) =>
      Boolean(
        target?.closest(
          "button, video, audio, input, textarea, select, [data-reaction-button], [data-message-status-button], [data-reply-to-button]",
        ),
      );

    const resetRowTouchTracking = () => {
      rowTouchStartPosRef.current = null;
      rowTouchMovedRef.current = false;
    };

    const resetBubbleTouchTracking = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }

      setSwipeOffset(0);
      touchStartPos.current = null;
      isSwiping.current = false;
      touchMovedBeyondTapThresholdRef.current = false;
      touchStartedOnInteractiveElementRef.current = false;
      longPressTriggeredRef.current = false;
    };

    const handleRowTouchStart = (e: React.TouchEvent) => {
      suppressNextClickRef.current = false;
      rowTouchStartPosRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      rowTouchMovedRef.current = false;
    };

    const handleRowTouchMove = (e: React.TouchEvent) => {
      if (!rowTouchStartPosRef.current) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - rowTouchStartPosRef.current.x);
      const deltaY = Math.abs(touch.clientY - rowTouchStartPosRef.current.y);

      if (deltaX > 8 || deltaY > 8) {
        rowTouchMovedRef.current = true;
      }
    };

    const senderProfileImage = useCachedImage(
      sender?.profileImageUrl,
      sender?.username || "",
      sender?.id || "",
    );

    // Calculate message status
    const getMessageStatus = () => {
      if (!isCurrentUser) return null;

      const otherParticipants = currentUser
        ? participants.filter((p) => p.id !== currentUser.id)
        : participants;
      const readByCount = message.readBy?.length || 0;
      const deliveredToCount = message.deliveredTo?.length || 0;

      if (message.status === "sending") {
        return null;
      }

      if (message.status === "failed") {
        return {
          icon: <AlertTriangleIcon className="w-3 h-3" />,
          color: getChatMessageStatusColor("failed", isCurrentUser),
          label: "Failed. Tap to retry",
          clickable: true,
        };
      }

      if (isGroupChat) {
        // For group chats, show detailed status
        if (message.readBy && message.readBy.length > 0) {
          return {
            icon: <CheckCheckIcon className="w-3 h-3" />,
            color: getChatMessageStatusColor("read", isCurrentUser),
            label: `Read by ${readByCount}`,
            clickable: true,
          };
        }
        if (message.deliveredTo && message.deliveredTo.length > 0) {
          return {
            icon: <CheckCheckIcon className="w-3 h-3" />,
            color: getChatMessageStatusColor("delivered", isCurrentUser),
            label: `Delivered to ${deliveredToCount}`,
          };
        }
        return {
          icon: <CheckIcon2 className="w-3 h-3" />,
          color: getChatMessageStatusColor("sent", isCurrentUser),
          label: "Sent",
        };
      } else {
        // For direct chats, show simple status
        if (message.readBy && message.readBy.length > 0) {
          return {
            icon: <CheckCheckIcon className="w-3 h-3" />,
            color: getChatMessageStatusColor("read", isCurrentUser),
            label: "Read",
          };
        }
        if (message.deliveredTo && message.deliveredTo.length > 0) {
          return {
            icon: <CheckCheckIcon className="w-3 h-3" />,
            color: getChatMessageStatusColor("delivered", isCurrentUser),
            label: "Delivered",
          };
        }
        return {
          icon: <CheckIcon2 className="w-3 h-3" />,
          color: getChatMessageStatusColor("sent", isCurrentUser),
          label: "Sent",
        };
      }
    };

    const messageStatus = getMessageStatus();

    // Stable ref so the native blur-guard effect below never re-runs when
    // selectionMode changes mid-touch (which would reset isTouchActive).
    const selectionModeRef = useRef(selectionMode);
    useEffect(() => { selectionModeRef.current = selectionMode; });

    // Add native touch listeners to prevent blur on Android (React events are passive)
    useEffect(() => {
      if (!isAndroid || !bubbleContainerRef.current || !inputRef?.current) return;

      const container = bubbleContainerRef.current;
      const input = inputRef.current;
      let startY = 0;
      let startX = 0;
      let startTime = 0;
      let isScrolling = false;
      let isTouchActive = false;

      const handleNativeTouchStart = (e: TouchEvent) => {
        startY = e.touches[0].clientY;
        startX = e.touches[0].clientX; // Add this line
        startTime = Date.now();
        isScrolling = false;
        isTouchActive = true;
      };

      const handleNativeTouchMove = (e: TouchEvent) => {
        const deltaY = Math.abs(e.touches[0].clientY - startY);
        // Higher threshold to avoid false positives from slight finger movement during long press
        if (deltaY > 20) {
          isScrolling = true;
        }
        // Horizontal swipe (reply gesture) — clear active flag so blur guard won't
        // fire a sync refocus that could flicker the keyboard during a swipe.
        const deltaXMove = Math.abs(e.touches[0].clientX - startX);
        if (deltaXMove > 10) {
          isTouchActive = false;
        }
      };

      const handleNativeTouchEnd = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        const touchDuration = Date.now() - startTime;

        // Calculate horizontal swipe distance
        const currentX = e.changedTouches[0].clientX;
        const deltaX = currentX - startX;
        const deltaY = Math.abs(e.changedTouches[0].clientY - startY);

        // Check if this was a swipe gesture (match React's initial detection: any horizontal > 10px)
        const isSwipeGesture = Math.abs(deltaX) > 10 && deltaY < 30;

        // If held for >300ms, catch it before React timer (500ms) fires and causes blur
        // Prioritize this over scroll detection to handle slight finger movement
        const isLongPress = touchDuration > 300;

        // Check if target is a button that should be allowed to work
        const isScrollButton = target.closest(
          'button[aria-label="Scroll to new messages"]',
        );
        const isReactionButton = target.closest("[data-reaction-button]");
        const isMessageStatusButton = target.closest(
          "[data-message-status-button]",
        );
        const isReplyToButton = target.closest("[data-reply-to-button]");
        const isMediaOpenButton = target.closest("[data-media-open-button]");
        const isMediaDownloadButton = target.closest("[data-media-download-button]");
        const isAudioControl = target.closest("audio");
        const isMessageLink = target.closest("[data-message-link]");

        if (
          isScrollButton ||
          isReactionButton ||
          isMessageStatusButton ||
          isReplyToButton ||
          isMediaOpenButton ||
          isMediaDownloadButton ||
          isAudioControl ||
          isMessageLink
        ) {
          isTouchActive = false;
          isScrolling = false;
          return; // Allow the button click to proceed without preventDefault
        }

        // DON'T prevent if it's a swipe gesture - let React handlers process it
        if (isSwipeGesture) {
          isTouchActive = false;
          isScrolling = false;
          return; // Allow swipe to be handled by React
        }

        // Only prevent if keyboard is open AND (is long press OR not scrolling)
        if (
          inputRef?.current &&
          document.activeElement === inputRef.current &&
          (isLongPress || !isScrolling)
        ) {
          const isMessageContent = target.closest(".max-w-xs, .max-w-sm");
          const isAvatarOrLink = target.closest("a") || target.tagName === "IMG";
          const isTouchingContainer =
            target === container || target.classList.contains("flex-shrink-0");
          const isMessageRefDiv =
            target.classList.contains("flex-col") &&
            target.classList.contains("relative");

          if (
            (!isMessageContent && !isAvatarOrLink) ||
            isTouchingContainer ||
            isMessageRefDiv
          ) {
            if (e.cancelable) {
              e.preventDefault();
              if (!selectionModeRef.current) {
                e.stopPropagation();
              }
            }
            // Don't reset isTouchActive immediately - keep it true to catch delayed blur events
            // Reset after a short delay instead
            setTimeout(() => {
              isTouchActive = false;
            }, 100);
            isScrolling = false;
            return;
          }
        }
        isTouchActive = false;
        isScrolling = false;
      };

      // Prevent blur during active touch on empty space
      const handleBlur = (e: FocusEvent) => {
        const isComposerSuppressed =
          input.dataset.keyboardSuppressed === "true";
        if (isComposerSuppressed) {
          isTouchActive = false;
          isScrolling = false;
          return;
        }

        // Check if the blur is caused by clicking scroll to bottom button or reaction button
        const relatedTarget = e.relatedTarget as HTMLElement;
        const isScrollButton = relatedTarget?.closest(
          'button[aria-label="Scroll to new messages"]',
        );
        const isReactionButton = relatedTarget?.closest("[data-reaction-button]");
        const isMessageStatusButton = relatedTarget?.closest(
          "[data-message-status-button]",
        );
        const isReplyToButton = relatedTarget?.closest("[data-reply-to-button]");
        const isMediaOpenButton = relatedTarget?.closest(
          "[data-media-open-button]",
        );
        const isMediaDownloadButton = relatedTarget?.closest(
          "[data-media-download-button]",
        );
        const isAudioControl = relatedTarget?.closest("audio");
        const isMessageLink = relatedTarget?.closest("[data-message-link]");
        const isReactionPickerTarget = relatedTarget?.closest(
          "[data-reaction-picker]",
        );
        const isReactionKeyboardInput = relatedTarget?.closest(
          "[data-reaction-keyboard-input]",
        );
        const isNavigating = isNavigatingToReplyRef?.current;
        const activeElement = document.activeElement as HTMLElement | null;
        const isActiveReactionPickerTarget = activeElement?.closest(
          "[data-reaction-picker]",
        );
        const isActiveReactionKeyboardInput = activeElement?.closest(
          "[data-reaction-keyboard-input]",
        );
        const isActiveAllowedTarget =
          Boolean(isActiveReactionPickerTarget) ||
          Boolean(isActiveReactionKeyboardInput);

        // Prevent blur in two cases:
        // 1. Touch on empty space (not scrolling, not on buttons)
        // 2. Navigating to reply message (trust the flag - it's only set when touching reply-to button with keyboard open)
        const shouldPreventEmptySpaceBlur =
          isTouchActive &&
          !isScrolling &&
          !isScrollButton &&
          !isReactionButton &&
          !isMessageStatusButton &&
          !isMediaOpenButton &&
          !isMediaDownloadButton &&
          !isAudioControl &&
          !isMessageLink &&
          !isReactionPickerTarget &&
          !isReactionKeyboardInput &&
          !isActiveAllowedTarget &&
          !isReplyToButton &&
          !isNavigating;
        const shouldPreventReplyNavigationBlur = isNavigating; // Trust the flag regardless of relatedTarget

        if (shouldPreventEmptySpaceBlur || shouldPreventReplyNavigationBlur) {
          e.preventDefault();
          e.stopPropagation();

          // Synchronous refocus prevents the one-frame keyboard collapse that
          // a requestAnimationFrame-only refocus would otherwise cause.
          if (document.activeElement !== input) {
            input.focus();
          }

          // Also schedule a safety check after focus settles.
          // Android may provide null relatedTarget during focus transitions.
          requestAnimationFrame(() => {
            const latestActive = document.activeElement as HTMLElement | null;
            const isPickerFocused = latestActive?.closest("[data-reaction-picker]");
            const isReactionInputFocused = latestActive?.closest(
              "[data-reaction-keyboard-input]",
            );
            if (isPickerFocused || isReactionInputFocused) return;
            if (document.activeElement !== input) {
              input.focus();
            }
          });
        } else {
        }
      };

      const handleNativeTouchCancel = () => {
        // Only reset isTouchActive if keyboard is not open
        // This prevents keyboard from closing during long press when touchcancel fires
        const isKeyboardOpen =
          inputRef?.current && document.activeElement === inputRef.current;
        if (!isKeyboardOpen) {
          isTouchActive = false;
        }
        isScrolling = false;
      };

      container.addEventListener("touchstart", handleNativeTouchStart, {
        passive: true,
        capture: true,
      });
      container.addEventListener("touchmove", handleNativeTouchMove, {
        passive: true,
        capture: true,
      });
      container.addEventListener("touchend", handleNativeTouchEnd, {
        passive: false,
        capture: true,
      });
      container.addEventListener("touchcancel", handleNativeTouchCancel, {
        passive: true,
        capture: true,
      });
      input.addEventListener("blur", handleBlur, { capture: true });

      return () => {
        container.removeEventListener("touchstart", handleNativeTouchStart, {
          capture: true,
        });
        container.removeEventListener("touchmove", handleNativeTouchMove, {
          capture: true,
        });
        container.removeEventListener("touchend", handleNativeTouchEnd, {
          capture: true,
        });
        container.removeEventListener("touchcancel", handleNativeTouchCancel, {
          capture: true,
        });
        input.removeEventListener("blur", handleBlur, { capture: true });
      };
    // NOTE: selectionMode is intentionally excluded — it is read via selectionModeRef
    // so that this effect never re-runs (and tears down listeners) mid-touch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAndroid, inputRef, message.id, sender?.id, currentUser?.id]);

    useAndroidTouchBlurGuard({
      targetRef: messageContentRef,
      inputRef,
      platform: Capacitor.getPlatform(),
      selectionMode,
      allowSelectors: [
        "[data-reaction-button]",
        "[data-message-status-button]",
        "[data-reply-to-button]",
        "[data-message-link]",
        "[data-media-open-button]",
        "[data-media-download-button]",
        "audio",
        "[data-reaction-picker]",
        "[data-reaction-keyboard-input]",
        'button[aria-label="Scroll to new messages"]',
      ],
      navigationFlagRef: isNavigatingToReplyRef,
      keyboardWasOpenRef: wasKeyboardOpenRef,
    });

    useAndroidTouchBlurGuard({
      targetRef: messageRef,
      inputRef,
      platform: Capacitor.getPlatform(),
      selectionMode,
      allowSelectors: [
        'button[aria-label="Scroll to new messages"]',
        "[data-reaction-button]",
        "[data-message-status-button]",
        "[data-reply-to-button]",
        "[data-message-link]",
        "[data-media-open-button]",
        "[data-media-download-button]",
        "audio",
        "[data-reaction-picker]",
        "[data-reaction-keyboard-input]",
      ],
      navigationFlagRef: isNavigatingToReplyRef,
    });

    // Add native touch listeners for reaction buttons to prevent keyboard close
    useEffect(() => {
      if (!isAndroid || !messageContentRef.current || !inputRef?.current) return;

      const reactionButtons = messageContentRef.current.querySelectorAll(
        "[data-reaction-button]",
      );
      if (reactionButtons.length === 0) return;

      const input = inputRef.current;
      const touchStartTimes = new WeakMap<Element, number>();
      const keyboardOpenStates = new WeakMap<Element, boolean>();

      const handleReactionTouchStart = (e: TouchEvent) => {
        const button = e.currentTarget as Element;
        touchStartTimes.set(button, Date.now());
        keyboardOpenStates.set(button, input === document.activeElement);
      };

      const handleReactionFocus = (e: FocusEvent) => {
        const button = e.currentTarget as HTMLElement;
        const wasKeyboardOpen = keyboardOpenStates.get(button) || false;
        e.preventDefault();
        e.stopPropagation();
        // If button gets focus, immediately blur it
        button.blur();
        // Only refocus input if keyboard was already open
        if (wasKeyboardOpen && input && document.activeElement !== input) {
          input.focus();
        }
      };

      const handleReactionTouchEnd = (e: TouchEvent) => {
        const button = e.currentTarget as Element;
        const touchStartTime = touchStartTimes.get(button) || 0;
        const touchDuration = Date.now() - touchStartTime;

        // Only prevent on LONG press (>=500ms) to stop keyboard close
        // For short tap (<500ms), allow React handlers to execute
        if (input === document.activeElement && touchDuration >= 500) {
          if (e.cancelable) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
          }
        }
      };

      // Attach listeners to all reaction buttons
      reactionButtons.forEach((button) => {
        if (!button) return;
        button.addEventListener(
          "touchstart",
          (e: Event) => {
            const touchEvent = e as TouchEvent;
            return handleReactionTouchStart(touchEvent);
          },
          { passive: false, capture: true },
        );
        button.addEventListener(
          "touchend",
          (e: Event) => {
            const touchEvent = e as TouchEvent;
            return handleReactionTouchEnd(touchEvent);
          },
          { passive: false, capture: true },
        );
        button.addEventListener(
          "focus",
          (e: Event) => {
            const focusEvent = e as FocusEvent;
            return handleReactionFocus(focusEvent);
          },
          { capture: true },
        );
      });

      return () => {
        reactionButtons.forEach((button) => {
          button.removeEventListener("touchstart", handleReactionTouchStart, {
            capture: true,
          });
          button.removeEventListener("touchend", handleReactionTouchEnd, {
            capture: true,
          });
          button.removeEventListener("focus", handleReactionFocus, {
            capture: true,
          });
        });
      };
    }, [isAndroid, inputRef, message.id, message.reactions]);

    // Add native touch listeners for message status button to prevent keyboard close
    useEffect(() => {
      if (!isAndroid || !messageContentRef.current || !inputRef?.current) return;

      const messageStatusButton = messageContentRef.current.querySelector(
        "[data-message-status-button]",
      );
      if (!messageStatusButton) return;

      const input = inputRef.current;
      let touchStartTime = 0;
      let wasKeyboardOpen = false;

      const handleStatusTouchStart = (e: TouchEvent) => {
        touchStartTime = Date.now();
        wasKeyboardOpen = input === document.activeElement;
      };

      const handleStatusFocus = (e: FocusEvent) => {
        const button = e.currentTarget as HTMLElement;
        e.preventDefault();
        e.stopPropagation();
        button.blur();
        // Only refocus input if keyboard was already open
        if (wasKeyboardOpen && input && document.activeElement !== input) {
          input.focus();
        }
      };

      const handleStatusTouchEnd = (e: TouchEvent) => {
        const touchDuration = Date.now() - touchStartTime;

        // Only prevent on LONG press (>=500ms) to stop keyboard close
        // For short tap (<500ms), allow React onClick handler to execute
        if (input === document.activeElement && touchDuration >= 500) {
          if (e.cancelable) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
          }
        }
      };

      messageStatusButton.addEventListener("touchstart", handleStatusTouchStart, {
        passive: false,
        capture: true,
      });
      messageStatusButton.addEventListener("touchend", handleStatusTouchEnd, {
        passive: false,
        capture: true,
      });
      messageStatusButton.addEventListener("focus", handleStatusFocus, {
        capture: true,
      });

      return () => {
        messageStatusButton.removeEventListener(
          "touchstart",
          handleStatusTouchStart,
          { capture: true },
        );
        messageStatusButton.removeEventListener(
          "touchend",
          handleStatusTouchEnd,
          { capture: true },
        );
        messageStatusButton.removeEventListener("focus", handleStatusFocus, {
          capture: true,
        });
      };
    }, [
      isAndroid,
      inputRef,
      message.id,
      message.status,
      message.readBy,
      message.deliveredTo,
      onShowMessageStatus,
    ]);

    // Add native touch listeners for reply-to button to prevent keyboard close
    useEffect(() => {
      if (!isAndroid || !messageContentRef.current || !inputRef?.current) return;

      const replyToButton = messageContentRef.current.querySelector(
        "[data-reply-to-button]",
      );
      if (!replyToButton) return;

      const input = inputRef.current;
      let touchStartTime = 0;
      // Use a ref-like object to store keyboard state deterministically
      const keyboardStateRef = { wasOpen: false };

      const handleReplyToTouchStart = (e: TouchEvent) => {
        touchStartTime = Date.now();
        // Capture keyboard state deterministically at touchstart
        keyboardStateRef.wasOpen = input === document.activeElement;

        // Set navigation flag IMMEDIATELY to prevent blur handler from allowing blur
        if (keyboardStateRef.wasOpen && isNavigatingToReplyRef) {
          isNavigatingToReplyRef.current = true;
        }
      };

      const handleReplyToFocus = (e: FocusEvent) => {
        const button = e.currentTarget as HTMLElement;

        // Always prevent default and blur button
        e.preventDefault();
        e.stopPropagation();
        button.blur();

        // Deterministically refocus input based on captured state
        if (keyboardStateRef.wasOpen) {
          // Refocus synchronously without setTimeout
          input.focus();
        }
      };

      const handleReplyToTouchEnd = (e: TouchEvent) => {
        const touchDuration = Date.now() - touchStartTime;

        // Don't preventDefault for long press - let the parent messageContent handler manage it
        // This prevents conflicts between multiple handlers trying to manage keyboard state

        // For short tap, let the click propagate
        if (touchDuration < 500) {
          // Clear navigation flag after a delay to allow onClick to complete
          setTimeout(() => {
            if (isNavigatingToReplyRef) {
              isNavigatingToReplyRef.current = false;
            }
          }, 500);
        } else {
          // Long press - clear navigation flag immediately
          if (isNavigatingToReplyRef) {
            isNavigatingToReplyRef.current = false;
          }
        }
      };

      replyToButton.addEventListener("touchstart", handleReplyToTouchStart, {
        passive: false,
        capture: true,
      });
      replyToButton.addEventListener("touchend", handleReplyToTouchEnd, {
        passive: false,
        capture: true,
      });
      replyToButton.addEventListener("focus", handleReplyToFocus, {
        capture: true,
      });

      return () => {
        replyToButton.removeEventListener("touchstart", handleReplyToTouchStart, {
          capture: true,
        });
        replyToButton.removeEventListener("touchend", handleReplyToTouchEnd, {
          capture: true,
        });
        replyToButton.removeEventListener("focus", handleReplyToFocus, {
          capture: true,
        });
      };
    }, [
      isAndroid,
      inputRef,
      message.id,
      message.replyTo,
      onReplyClick,
      isNavigatingToReplyRef,
    ]);

    // Notify parent component when picker/menu opens/closes
    useEffect(() => {
      onPickerStateChange?.(showReactionPicker);

      // Clear the opening flag when picker actually opens
      if (showReactionPicker && isOpeningReactionPickerRef) {
        // Wait for layout, then clear flag
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (isOpeningReactionPickerRef) {
              isOpeningReactionPickerRef.current = false;
            }
          });
        });
      }
    }, [
      showReactionPicker,
      onPickerStateChange,
      isOpeningReactionPickerRef,
    ]);

    useEffect(() => {
      if (showReactionPicker) return;
      setShowCustomReactionInput(false);
      setCustomReactionInput("");
    }, [showReactionPicker]);

    const updateReactionPickerPosition = useCallback(() => {
      if (!messageRef.current) return;

      const rect = messageRef.current.getBoundingClientRect();
      const visualViewport = window.visualViewport;
      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const viewportWidth = visualViewport?.width ?? window.innerWidth;
      const viewportOffsetTop = visualViewport?.offsetTop ?? 0;
      const viewportOffsetLeft = visualViewport?.offsetLeft ?? 0;
      const pickerWidth = CHAT_REACTION_PICKER_WIDTH_PX;
      const pickerHeight = showCustomReactionInput
        ? CHAT_REACTION_PICKER_EXPANDED_HEIGHT_PX
        : CHAT_REACTION_PICKER_COLLAPSED_HEIGHT_PX;
      const minTop = viewportOffsetTop + CHAT_REACTION_PICKER_MARGIN_PX;
      const maxTop =
        viewportOffsetTop +
        viewportHeight -
        pickerHeight -
        CHAT_REACTION_PICKER_MARGIN_PX;
      const preferredBelowTop = rect.bottom + CHAT_REACTION_PICKER_GAP_PX;
      const preferredAboveTop =
        rect.top - pickerHeight - CHAT_REACTION_PICKER_GAP_PX;
      const spaceBelow =
        viewportOffsetTop + viewportHeight - rect.bottom - CHAT_REACTION_PICKER_MARGIN_PX;
      const spaceAbove = rect.top - viewportOffsetTop - CHAT_REACTION_PICKER_MARGIN_PX;

      let nextTop = preferredBelowTop;
      if (
        spaceBelow < pickerHeight + CHAT_REACTION_PICKER_GAP_PX &&
        spaceAbove > spaceBelow
      ) {
        nextTop = preferredAboveTop;
      }

      if (maxTop >= minTop) {
        nextTop = Math.min(Math.max(nextTop, minTop), maxTop);
      } else {
        nextTop = minTop;
      }

      const preferredCenterX = isCurrentUser
        ? rect.right - pickerWidth / 2
        : rect.left + pickerWidth / 2;
      const minCenterX =
        viewportOffsetLeft + pickerWidth / 2 + CHAT_REACTION_PICKER_MARGIN_PX;
      const maxCenterX =
        viewportOffsetLeft +
        viewportWidth -
        pickerWidth / 2 -
        CHAT_REACTION_PICKER_MARGIN_PX;
      const nextLeft =
        maxCenterX >= minCenterX
          ? Math.min(Math.max(preferredCenterX, minCenterX), maxCenterX)
          : viewportOffsetLeft + viewportWidth / 2;

      setPickerPosition({ top: nextTop, left: nextLeft });
    }, [isCurrentUser, showCustomReactionInput]);

    useEffect(() => {
      if (!showReactionPicker) return;

      const updatePosition = () => {
        requestAnimationFrame(() => {
          updateReactionPickerPosition();
        });
      };

      updatePosition();

      const visualViewport = window.visualViewport;
      visualViewport?.addEventListener("resize", updatePosition);
      visualViewport?.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);

      return () => {
        visualViewport?.removeEventListener("resize", updatePosition);
        visualViewport?.removeEventListener("scroll", updatePosition);
        window.removeEventListener("resize", updatePosition);
      };
    }, [showReactionPicker, showCustomReactionInput, keyboardHeight, updateReactionPickerPosition]);

    const armReactionPickerDismissGuard = useCallback((durationMs = CHAT_REACTION_DISMISS_GUARD_MS) => {
      reactionPickerDismissGuardUntilRef.current = Date.now() + durationMs;
    }, []);

    useEffect(() => {
      if (!showCustomReactionInput) return;

      armReactionPickerDismissGuard();

      requestAnimationFrame(() => {
        customReactionInputRef.current?.focus();
      });
    }, [showCustomReactionInput, armReactionPickerDismissGuard]);

    const closeReactionPicker = useCallback(() => {
      const activeElement = document.activeElement as HTMLElement | null;
      const isReactionInputFocused = Boolean(
        activeElement?.closest("[data-reaction-keyboard-input]"),
      );
      const isDismissGuardActive =
        showCustomReactionInput &&
        Date.now() < reactionPickerDismissGuardUntilRef.current;

      if (isReactionInputFocused) {
        activeElement?.blur();
        armReactionPickerDismissGuard(250);
        return;
      }

      if (isDismissGuardActive) {
        return;
      }

      setShowReactionPicker(false);
    }, [showCustomReactionInput, armReactionPickerDismissGuard]);

    // Group reactions by emoji - MUST be before early return
    const groupedReactions = useMemo(
      () => groupMessageReactions(message.reactions),
      [message.reactions],
    );

    const pickerReactionEmojis = useMemo(() => {
      const existingOnMessage = Array.from(
        new Set(groupedReactions.map((group) => group.emoji)),
      );
      const baseTail = quickReactionEmojis.filter(
        (emoji) => !existingOnMessage.includes(emoji),
      );
      const baseList = [...existingOnMessage, ...baseTail];
      const keyboardCandidates = showCustomReactionInput
        ? extractReactionCandidates(
            customReactionInput,
            Math.max(quickReactionEmojis.length, 1),
          )
        : [];

      if (keyboardCandidates.length > 0) {
        const targetLength = Math.max(baseList.length, existingOnMessage.length);
        const replaceableSlots = Math.max(targetLength - existingOnMessage.length, 0);
        const keyboardPart = keyboardCandidates
          .filter((emoji) => !existingOnMessage.includes(emoji))
          .slice(0, replaceableSlots);
        const fallbackTail = baseTail
          .filter((emoji) => !keyboardPart.includes(emoji))
          .slice(0, Math.max(replaceableSlots - keyboardPart.length, 0));

        // Keep existing message reactions pinned at the start.
        return [...existingOnMessage, ...keyboardPart, ...fallbackTail];
      }

      return baseList;
    }, [quickReactionEmojis, groupedReactions, showCustomReactionInput, customReactionInput]);

    const handleTouchStart = (e: React.TouchEvent) => {
      if (selectionMode) return;

      // Check if input is currently focused (keyboard is open)
      const isInputFocused =
        isAndroid &&
        inputRef?.current &&
        document.activeElement === inputRef.current;

      // Store keyboard state for use in other touch handlers
      wasKeyboardOpenRef.current = isInputFocused;

      // DON'T prevent default here on Android even if keyboard is open
      // The native listener handles prevention for padding/empty areas
      // We need the touch events to work for long press detection

      // Check if touch started on a button (reaction, message status, reply-to)
      const target = e.target as HTMLElement;
      const isTouchOnButton = isInteractiveMessageTarget(target);
      touchStartedOnInteractiveElementRef.current = Boolean(isTouchOnButton);
      suppressNextClickRef.current = false;
      longPressTriggeredRef.current = false;
      touchMovedBeyondTapThresholdRef.current = false;

      // Store initial touch position
      touchStartPos.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };

      // Don't start long press timer if touching a button
      if (isTouchOnButton) {
        return;
      }

      // Start long press timer for reaction picker
      longPressTimer.current = setTimeout(() => {
        if (!isSwiping.current && messageRef.current) {
          longPressTriggeredRef.current = true;
          // Consume the matching row touchend so the just-selected message
          // isn't immediately toggled off when finger lifts.
          suppressNextClickRef.current = true;
          handleSelectMessage();
          openReactionPicker();
          if (navigator.vibrate) {
            navigator.vibrate(45);
          }
        }
      }, CHAT_REACTION_LONG_PRESS_MS);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      if (selectionMode) return;
      if (!touchStartPos.current) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartPos.current.x;
      const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

      // If vertical movement, mark as scrolling
      if (deltaY > 10) {
        touchMovedBeyondTapThresholdRef.current = true;
        isSwiping.current = false; // Not a swipe if scrolling
      }

      // If vertical movement is too much, cancel swipe and long press
      if (deltaY > 30) {
        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
        setSwipeOffset(0);
        touchStartPos.current = null;
        isSwiping.current = false;
        return;
      }

      // Detect horizontal swipe
      const absSwipe = Math.abs(deltaX);
      if (absSwipe > 10) {
        touchMovedBeyondTapThresholdRef.current = true;
        isSwiping.current = true;
        // Cancel long press if swiping
        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
        const clampedOffset =
          deltaX > 0
            ? Math.min(deltaX, SWIPE_THRESHOLD)
            : Math.max(deltaX, -SWIPE_THRESHOLD);
        setSwipeOffset(clampedOffset);
      }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
      if (selectionMode) {
        resetBubbleTouchTracking();
        return;
      }

      // Don't prevent touchend - let document-level handlers manage keyboard
      // This allows scrolling to work while document handler prevents blur when needed

      const target = e.target as HTMLElement;
      const isInteractiveTouch =
        touchStartedOnInteractiveElementRef.current ||
        isInteractiveMessageTarget(target);
      const shouldToggleSelectionOnTouch =
        selectionMode &&
        !longPressTriggeredRef.current &&
        !touchMovedBeyondTapThresholdRef.current &&
        !isSwiping.current &&
        Math.abs(swipeOffset) < SWIPE_THRESHOLD &&
        !isInteractiveTouch;

      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }

      // Check if swipe threshold was reached
      if (Math.abs(swipeOffset) >= SWIPE_THRESHOLD) {
        // Trigger reply
        onReply(message, sender);
        if (navigator.vibrate) {
          navigator.vibrate(30);
        }
      } else if (shouldToggleSelectionOnTouch) {
        suppressNextClickRef.current = true;
        onToggleSelection?.(message.id);
      }

      resetBubbleTouchTracking();
    };

    const handleTouchCancel = () => {
      resetBubbleTouchTracking();
    };

    // Failsafe: if touchend is lost (common with keyboard/system gestures on Android),
    // clear any leftover swipe state so reply arrow cannot remain stuck on screen.
    useEffect(() => {
      const clearSwipeState = () => {
        if (swipeOffset !== 0 || touchStartPos.current || isSwiping.current) {
          resetBubbleTouchTracking();
        }
      };

      window.addEventListener("touchend", clearSwipeState, { passive: true });
      window.addEventListener("touchcancel", clearSwipeState, { passive: true });

      return () => {
        window.removeEventListener("touchend", clearSwipeState);
        window.removeEventListener("touchcancel", clearSwipeState);
      };
    }, [swipeOffset, isAndroid, message.id]);

    const handleReactionClick = (emoji: string) => {
      if (onAddReaction) {
        onAddReaction(message, emoji);
      }
      onReactionUsed?.(emoji);
      setShowReactionPicker(false);
      setShowCustomReactionInput(false);
      setCustomReactionInput("");
    };

    const handleSelectMessage = () => {
      if (selectionMode) {
        onToggleSelection?.(message.id);
      } else {
        onEnterSelectionMode?.(message.id);
      }
      setShowReactionPicker(false);
      setShowCustomReactionInput(false);
      setCustomReactionInput("");
    };

    const openReactionPicker = () => {
      if (!messageRef.current) return;

      if (isOpeningReactionPickerRef) {
        isOpeningReactionPickerRef.current = true;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!messageRef.current) return;

          setShowCustomReactionInput(false);
          setCustomReactionInput("");
          setShowReactionPicker(true);
          updateReactionPickerPosition();

          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
        });
      });
    };

    const handleReplyToClick = () => {
      if (message.replyTo && onReplyClick) {
        // Save keyboard state before calling onReplyClick
        const wasKeyboardOpen =
          isAndroid &&
          inputRef?.current &&
          document.activeElement === inputRef.current;

        if (wasKeyboardOpen) {
          isNavigatingToReplyRef.current = true;
          // Reset after a delay
          setTimeout(() => {
            isNavigatingToReplyRef.current = false;
          }, 500);
        }

        onReplyClick(message.replyTo.messageId);
      } else {
      }
    };

    // Early return AFTER all hooks
    if (!sender || !currentUser) return null;

    return (
      <div
        ref={bubbleContainerRef}
        className={`flex items-start gap-2 ${isCurrentUser ? "justify-end" : "justify-start"} group relative`}
        data-message-id={message.id}
        // style={{ paddingRight: !isCurrentUser ? '3rem' : '0', paddingLeft: isCurrentUser ? '3rem' : '0' }}
        onClick={(e) => {
          if (!selectionMode) return;
          if (isMobilePlatform) return;
          if (suppressNextClickRef.current) {
            suppressNextClickRef.current = false;
            return;
          }

          const target = e.target as HTMLElement;
          if (isSelectionToggleExcludedTarget(target)) return;

          e.preventDefault();
          e.stopPropagation();
          onToggleSelection?.(message.id);
        }}
        onTouchStart={handleRowTouchStart}
        onTouchMove={handleRowTouchMove}
        onTouchEnd={(e) => {
          // Prevent keyboard from closing when touching bubble padding/empty areas
          if (
            isAndroid &&
            inputRef?.current &&
            document.activeElement === inputRef.current
          ) {
            // Only prevent if not touching the actual message content
            const target = e.target as HTMLElement;
            const isMessageContent = target.closest(".max-w-xs, .max-w-sm");
            if (!isMessageContent) {
              e.preventDefault();
              e.stopPropagation();
            }
          }

          if (!selectionMode) return;
          if (suppressNextClickRef.current) {
            resetRowTouchTracking();
            return;
          }

          const target = e.target as HTMLElement;
          if (isSelectionToggleExcludedTarget(target) || rowTouchMovedRef.current) {
            resetRowTouchTracking();
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          suppressNextClickRef.current = true;
          onToggleSelection?.(message.id);
          resetRowTouchTracking();
        }}
        onTouchCancel={resetRowTouchTracking}
        onContextMenu={(e) => {
          if (
            isAndroid &&
            inputRef?.current &&
            document.activeElement === inputRef.current
          ) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        {/* Reply icon that appears during swipe */}
        {swipeOffset !== 0 && (
          <div
            className={`absolute top-1/2 -translate-y-1/2 transition-opacity ${swipeOffset > 0 ? "left-4" : "right-4"
              }`}
            style={{ opacity: swipeProgress }}
          >
            <CornerDownRightIcon
              className={`w-5 h-5 ${swipeProgress >= 1 ? "text-primary" : "text-gray-400"}`}
            />
          </div>
        )}

        {!isCurrentUser && showSenderName ? (
          <Link to={`/profile/${sender.id}`}>
            <img
              onError={handleImageError}
              src={senderProfileImage}
              alt={sender.username}
              className="w-8 h-8 rounded-lg flex-shrink-0 cursor-pointer"
            />
          </Link>
        ) : !isCurrentUser ? (
          <div className="w-8 h-8 flex-shrink-0" />
        ) : null}

        <div
          ref={messageRef}
          className={`flex flex-col ${isCurrentUser ? "items-end" : "items-start"} relative transition-transform`}
          style={{
            transform: `translateX(${swipeOffset}px)`,
            transition: swipeOffset === 0 ? "transform 0.2s ease-out" : "none",
          }}
          onContextMenu={(e) => {
            if (
              isAndroid &&
              inputRef?.current &&
              document.activeElement === inputRef.current
            ) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <div
            ref={messageContentRef}
            className={`max-w-xs md:max-w-sm p-2.5 shadow-sm ${isReactingTo
              ? "ring-2 ring-primary ring-opacity-50 bg-primary/5" // Highlight when reacting
              : ""
              } ${isCurrentUser
                ? showSenderName ? "rounded-2xl rounded-tr-none bg-user-bubble text-white" : "rounded-2xl bg-user-bubble text-white"
                : showSenderName
                  ? "rounded-2xl rounded-tl-none bg-white text-gray-900 border border-gray-200"
                  : "rounded-2xl bg-white text-gray-900 border border-gray-200"
              } ${isSelectedForAction ? "ring-2 ring-primary" : ""} relative select-none transition-all duration-200`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            onContextMenu={(e) => {
              // Prevent context menu on long press when keyboard is open
              if (
                isAndroid &&
                inputRef?.current &&
                document.activeElement === inputRef.current
              ) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            style={{
              WebkitUserSelect: isAndroid ? "none" : "text",
              userSelect: isAndroid ? "none" : "text",
            }}
          >
            {/* Bubble tail */}
            {showSenderName && isCurrentUser && (
              <span className="absolute pointer-events-none" style={{ right: -7, top: 0, width: 0, height: 0, borderLeft: '8px solid #7c4ddf', borderTop: '8px solid transparent' }} />
            )}
            {showSenderName && !isCurrentUser && (
              <>
                <span className="absolute pointer-events-none" style={{ left: -9, top: 0, width: 0, height: 0, borderRight: '9px solid #e5e7eb', borderTop: '9px solid transparent' }} />
                <span className="absolute pointer-events-none" style={{ left: -7, top: 1, width: 0, height: 0, borderRight: '8px solid white', borderTop: '8px solid transparent' }} />
              </>
            )}
            {message.isForwarded && !isDeletedForEveryone && (
              <div
                className={`inline-flex items-center gap-1 mb-1 ${isCurrentUser ? "text-white/70" : "text-gray-500"}`}
              >
                <ForwardIcon className="w-3 h-3" />
                <span className="text-[10px] italic">Forwarded</span>
              </div>
            )}
            {/* Sender name inside bubble for non-current users */}
            {!isCurrentUser && showSenderName && (
              <div className="flex items-center gap-1.5 mb-1">
                <p
                  className="text-xs font-semibold"
                  style={{
                    color: isCurrentUser
                      ? "rgba(255,255,255,0.8)"
                      : getSenderNameColor(sender.id),
                  }}
                >
                  {sender.username}
                </p>
                {isGroupChat && organizerId && sender.id === organizerId && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">
                    Organizer
                  </span>
                )}
                {senderStatus === "left" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium">
                    Left
                  </span>
                )}
                {senderStatus === "kicked" && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">
                    Removed
                  </span>
                )}
              </div>
            )}

            {/* Replied message preview - clickable */}
            {message.replyTo && (
              <button
                data-reply-to-button="true"
                onClick={(e) => {
                  handleReplyToClick();
                }}
                className={`w-full mb-1.5 pb-1.5 border-l-2 pl-2 text-left hover:bg-black/5 rounded transition-colors ${isCurrentUser ? "border-white/40" : "border-primary/40"}`}
              >
                <p
                  className={`text-xs font-semibold ${isCurrentUser ? "text-white/80" : "text-primary"}`}
                >
                  {repliedToSender?.username || message.replyTo.senderUsername}
                </p>
                <p
                  className={`text-xs ${isCurrentUser ? "text-white/70" : "text-gray-600"} truncate`}
                >
                  {message.replyTo.text || "Media"}
                </p>
              </button>
            )}

            {message.location && !isDeletedForEveryone && (
              <a
                href={buildChatLocationMapsUrl(message.location)}
                target="_blank"
                rel="noreferrer"
                className={`block mb-1.5 rounded-2xl border px-3 py-3 ${isCurrentUser ? "border-white/15 bg-white/10 text-white" : "border-gray-200 bg-gray-50 text-gray-900"}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCurrentUser ? "bg-white/15" : "bg-primary/10 text-primary"}`}>
                    <MapPinIcon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate">
                        {liveLocationState?.title || message.location.label || "Current location"}
                      </p>
                      {message.location.kind === "live" && liveLocationState && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isCurrentUser ? "bg-white/15 text-white/85" : liveLocationState.isExpired ? "bg-gray-200 text-gray-700" : "bg-red-100 text-red-600"}`}>
                          {liveLocationState.badgeLabel}
                        </span>
                      )}
                    </div>
                    {(message.location.placeName || message.location.label) && (
                      <p className={`text-xs mt-0.5 truncate ${isCurrentUser ? "text-white/75" : "text-gray-600"}`}>
                        {message.location.placeName || message.location.label}
                      </p>
                    )}
                    {liveLocationState?.detailLabel && (
                      <p className={`text-xs mt-1 ${isCurrentUser ? "text-white/80" : "text-gray-600"}`}>
                        {liveLocationState.detailLabel}
                      </p>
                    )}
                    <p className={`text-xs mt-1 ${isCurrentUser ? "text-white/75" : "text-gray-500"}`}>
                      {message.location.latitude.toFixed(5)}, {message.location.longitude.toFixed(5)}
                    </p>
                    <p className={`text-xs mt-1 underline ${isCurrentUser ? "text-white/85" : "text-primary"}`}>
                      {liveLocationState?.mapsLabel || "Open in Maps"}
                    </p>
                  </div>
                </div>
              </a>
            )}

            {Array.isArray(message.media) && message.media.length > 0 && !isDeletedForEveryone && (
              <div
                className={`grid gap-1.5 mb-1.5 ${message.media.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}
              >
                {message.media.map((media) => (
                  <div
                    key={media.id}
                    className="block rounded-xl overflow-hidden border border-black/10 bg-black/5"
                  >
                    {media.type === "video" || media.type === "image" ? (
                      <button
                        type="button"
                        data-media-open-button="true"
                        className="block w-full"
                        onClick={() => onOpenMedia?.(media)}
                        title="Open fullscreen"
                      >
                        {media.type === "video" ? (
                          <video
                            src={media.url}
                            preload="metadata"
                            className="w-full max-h-56 object-cover"
                          />
                        ) : (
                          <img
                            src={media.url}
                            alt={media.fileName || "Media"}
                            className="w-full max-h-56 object-cover"
                            onError={handleImageError}
                          />
                        )}
                      </button>
                    ) : media.type === "audio" ? (
                      <div className={`p-3 min-w-[220px] ${isCurrentUser ? "text-white" : "text-gray-900"}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isCurrentUser ? "bg-white/20" : "bg-primary/10 text-primary"}`}>
                            <MicIcon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {isVoiceNoteFileName(media.fileName) ? "Voice note" : media.fileName || "Audio"}
                            </p>
                            <p className={`text-[11px] ${isCurrentUser ? "text-white/70" : "text-gray-500"}`}>
                              {formatFileSize(media.size)}
                            </p>
                          </div>
                        </div>
                        <audio
                          src={media.url}
                          controls
                          preload="metadata"
                          className="w-full h-10"
                        />
                      </div>
                    ) : (
                      <div className={`p-3 ${isCurrentUser ? "text-white" : "text-gray-900"}`}>
                        <a
                          href={media.url}
                          target="_blank"
                          rel="noreferrer"
                          data-media-open-button="true"
                          data-document-link="true"
                          className={`text-sm font-medium truncate underline block ${isCurrentUser ? "text-white" : "text-primary"}`}
                          onClick={(event) => {
                            event.preventDefault();
                            void onOpenMedia?.(media);
                          }}
                          title="Open document"
                        >
                          📄 {media.fileName || "Document"}
                        </a>
                        <p className={`text-xs mt-1 ${isCurrentUser ? "text-white/70" : "text-gray-500"}`}>
                          {formatFileSize(media.size)}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end justify-between gap-2">
              {isDeletedForEveryone ? (
                <span className={`text-sm italic flex-1 ${isCurrentUser ? "text-white/80" : "text-gray-500"}`}>
                  This message was deleted
                </span>
              ) : message.text?.trim()
                ? renderFormattedMessage(message.text || "", isCurrentUser)
                : <span className="flex-1" />}
              <div className="flex items-center gap-1 flex-shrink-0 self-end">
                <span
                  className={`text-[10px] ${isCurrentUser ? "text-white/60" : "text-gray-400"}`}
                >
                  {toJsDate(message.timestamp).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
                {message.editedAt && !isDeletedForEveryone && (
                  <span
                    className={`text-[10px] ${isCurrentUser ? "text-white/60" : "text-gray-400"}`}
                  >
                    edited
                  </span>
                )}
                {isMessageStarredByUser(message, currentUser?.id) &&
                  !isDeletedForEveryone && (
                    <StarIcon
                      className={`w-2.5 h-2.5 ${isCurrentUser ? "text-yellow-300" : "text-yellow-500"}`}
                      fill="currentColor"
                    />
                  )}
                {messageStatus && isCurrentUser && (
                  <button
                    data-message-status-button="true"
                    onClick={() => {
                      if (message.status === "failed" && onRetryFailedMessage) {
                        onRetryFailedMessage(message);
                      } else if (messageStatus.clickable && onShowReadReceipts) {
                        onShowReadReceipts(message);
                      } else if (onShowMessageStatus) {
                        onShowMessageStatus(message);
                      }
                    }}
                    className={`flex items-center ${(message.status === "failed" || messageStatus.clickable || onShowMessageStatus) ? "cursor-pointer hover:opacity-70" : "cursor-default"}`}
                    title={messageStatus.label}
                  >
                    <span className={messageStatus.color}>
                      {messageStatus.icon}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Reactions display */}
            {groupedReactions.length > 0 && !isDeletedForEveryone && (
              <div className="flex flex-wrap gap-1 mt-1">
                {groupedReactions.map((group, idx) => {
                  const hasCurrentUserReacted = group.userIds.includes(
                    currentUser?.id || "",
                  );
                  return (
                    <button
                      key={idx}
                      data-reaction-button="true"
                      onClick={() => {
                        // Show reaction details modal
                        onShowReactionDetails?.(group.emoji, message);
                      }}
                      className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs transition-all ${hasCurrentUserReacted
                        ? "bg-primary/20 border border-primary/40"
                        : "bg-gray-100 border border-gray-200 hover:bg-gray-200"
                        }`}
                      title={`${group.count} reaction${group.count > 1 ? "s" : ""}`}
                    >
                      <span>{group.emoji}</span>
                      {group.count > 1 && (
                        <span className="text-[10px] font-medium">
                          {group.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Long-press action menu and reaction picker - rendered via Portal at document root */}
          {showReactionPicker &&
            ReactDOM.createPortal(
              <>
                {/* Backdrop to close picker */}
                <div
                  className="fixed inset-0 z-[51]"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }} // Prevent focus loss
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }} // Prevent keyboard close
                  onTouchEnd={(e) => {
                    // Prevent keyboard from closing when lifting the finger on the
                    // backdrop after a long-press that opened the picker (Android).
                    if (isAndroid && inputRef?.current && document.activeElement === inputRef.current) {
                      e.preventDefault();
                    }
                    e.stopPropagation();
                    closeReactionPicker();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeReactionPicker();
                  }}
                />
                {/* Picker with fixed positioning */}
                {showReactionPicker && !isDeletedForEveryone && (
                  <div
                    className="fixed z-[100]"
                    data-reaction-picker="true"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }} // Prevent focus loss
                    onTouchStart={(e) => {
                      e.stopPropagation();
                    }} // Prevent keyboard close
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                    }}
                    style={{
                      top: `${pickerPosition.top}px`,
                      left: `${pickerPosition.left}px`,
                      transform: "translateX(-50%)", // Center horizontally
                    }}
                  >
                    <div
                      className="bg-white border border-gray-200 rounded-2xl shadow-2xl flex gap-1.5 flex-wrap items-center"
                      style={{
                        maxWidth: "calc(100vw - 16px)",
                        height: "auto",
                        padding: 8,
                        width: CHAT_REACTION_PICKER_WIDTH_PX,
                        overflowY: "hidden",
                        overflowX: "hidden",
                      }}
                      data-reaction-picker="true"
                    >
                      {pickerReactionEmojis.map((emoji) => {
                        // Check if current user has reacted with this emoji
                        const hasReacted = message.reactions?.some(
                          (r) => r.emoji === emoji && r.userId === currentUser.id,
                        );
                        return (
                          <button
                            key={emoji}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }} // Prevent focus shift
                            onTouchStart={(e) => {
                              e.stopPropagation();
                            }} // Prevent keyboard close
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              // Handle reaction on touchEnd for touch devices
                              handleReactionClick(emoji);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle reaction on click for mouse/desktop
                              handleReactionClick(emoji);
                            }}
                            className={`flex items-center justify-center rounded-full transition-all ${hasReacted
                              ? "bg-primary/20 border border-primary/40"
                              : "hover:bg-gray-100"
                              }`}
                            title={`React with ${emoji}`}
                            style={{ width: 32, height: 32, fontSize: 18 }}
                          >
                            {emoji}
                          </button>
                        );
                      })}

                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          armReactionPickerDismissGuard();
                          setShowCustomReactionInput(true);
                        }}
                        className="w-8 h-8 rounded-full border border-gray-300 text-gray-600 text-sm hover:bg-gray-100"
                        title="Open keyboard emoji input"
                      >
                        ⌨
                      </button>

                      {showCustomReactionInput && (
                        <div className="w-full mt-1 flex items-center gap-2">
                          <input
                            ref={customReactionInputRef}
                            type="text"
                            data-reaction-keyboard-input="true"
                            value={customReactionInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCustomReactionInput(value);
                            }}
                            placeholder="Pick emoji from keyboard, then tap it in the list"
                            className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
                            autoFocus
                            autoComplete="off"
                            autoCapitalize="off"
                            autoCorrect="off"
                            spellCheck={false}
                            name="reaction-emoji-input"
                            enterKeyHint="done"
                            onFocus={() => {
                              armReactionPickerDismissGuard();
                            }}
                            onBlur={() => {
                              armReactionPickerDismissGuard(250);
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>,
              document.body,
            )}
        </div>
      </div>
    );
  },
  (prev, next) => {
    if (prev.message !== next.message) return false;
    if (prev.sender !== next.sender) return false;
    if (prev.activeLiveLocationShare !== next.activeLiveLocationShare) return false;
    if (prev.repliedToSender !== next.repliedToSender) return false;
    if (prev.isReactingTo !== next.isReactingTo) return false;
    if (prev.showSenderName !== next.showSenderName) return false;
    if (prev.isGroupChat !== next.isGroupChat) return false;
    if (prev.participants !== next.participants) return false;
    if (prev.senderStatus !== next.senderStatus) return false;
    if (prev.organizerId !== next.organizerId) return false;
    if (prev.selectionMode !== next.selectionMode) return false;
    if (prev.isSelectedForAction !== next.isSelectedForAction) return false;
    if (prev.quickReactionEmojis !== next.quickReactionEmojis) return false;
    if (prev.keyboardHeight !== next.keyboardHeight) return false;
    return true;
  },
);

const ActivityChatPage: React.FC = () => {
  usePageTracking("Chat", "ActivityChatPage");
  const { chatId } = useParams<{ chatId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isAndroid = Capacitor.getPlatform() === "android";

  // Detect if running on native platform
  const isNative = Capacitor.isNativePlatform();

  // Check if this is readonly mode for expired/deleted activities
  const searchParams = new URLSearchParams(location.search);
  const isReadonly = searchParams.get("readonly") === "true";
  const routeState = location.state as
    | {
      prefetchedParticipants?: User[];
      prefetchedActivity?: ActivityRequest | null;
      returnToPath?: string;
      returnToState?: Record<string, unknown>;
    }
    | undefined;

  const { activities } = useActivityFeedData();
  const { getChatsForUser, getChatById } = useActivityChatData();
  const {
    refreshSingleActivity,
    sendMessage,
    toggleStarMessage,
    forwardMessages,
    editMessage,
    deleteMessage,
    addReactionToMessage,
    markChatNotificationsAsRead,
  } = useActivityActions();
  const { currentUser, getUsersByIds, isLoading: isAuthLoading } = useAuth();

  const chat = getChatById(chatId!);
  const [inputValue, setInputValue] = useState("");
  const [participantUsers, setParticipantUsers] = useState<User[]>(
    routeState?.prefetchedParticipants || [],
  );
  const [headerHeight, setHeaderHeight] = useState(isNative ? 60 : 90);
  const [footerHeight, setFooterHeight] = useState(isNative ? 54 : 54);
  const [navHeight, setNavHeight] = useState(67); // Bottom navigation height (h-16 = 64px, but account for slight variations)
  const [heightsInitialized, setHeightsInitialized] = useState(false);
  const [initializedForScroll, setInitializedForScroll] = useState(false);

  // Reply state
  const [replyingTo, setReplyingTo] = useState<{
    message: ChatMessage;
    sender: User;
  } | null>(null);
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);

  const replyPreviewRef = useRef<HTMLDivElement>(null);
  const [replyPreviewHeight, setReplyPreviewHeight] = useState(0);

  // Message pagination state
  const MESSAGES_PER_PAGE = 50;
  const [displayedMessageCount, setDisplayedMessageCount] =
    useState(MESSAGES_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Read receipts modal state
  const [readReceiptsMessage, setReadReceiptsMessage] =
    useState<ChatMessage | null>(null);

  // Message status modal state
  const [messageStatusMessage, setMessageStatusMessage] =
    useState<ChatMessage | null>(null);

  // Reaction details modal state
  const [reactionDetailsEmoji, setReactionDetailsEmoji] = useState<
    string | null
  >(null);
  const [reactionDetailsMessage, setReactionDetailsMessage] =
    useState<ChatMessage | null>(null);

  // Reaction picker state (like reply preview)
  const [reactingToMessage, setReactingToMessage] =
    useState<ChatMessage | null>(null);
  const [anyReactionPickerOpen, setAnyReactionPickerOpen] = useState(false);

  // Keyboard height state for native platforms
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState<ChatComposerMediaItem[]>(
    [],
  );
  const [showAttachmentSheet, setShowAttachmentSheet] = useState(false);
  const [isRecordingVoiceNote, setIsRecordingVoiceNote] = useState(false);
  const [voiceNoteDurationMs, setVoiceNoteDurationMs] = useState(0);
  const [showLocationShareSheet, setShowLocationShareSheet] = useState(false);
  const [isPreparingLocationShare, setIsPreparingLocationShare] = useState(false);
  const [liveLocationSharesById, setLiveLocationSharesById] = useState<
    Record<string, ChatLiveLocationShareRecord>
  >({});
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const [showForwardPicker, setShowForwardPicker] = useState(false);
  const [forwardTargetIds, setForwardTargetIds] = useState<Set<string>>(
    new Set(),
  );
  const [forwardDirectChatNameById, setForwardDirectChatNameById] = useState<
    Record<string, string>
  >({});
  const [isForwarding, setIsForwarding] = useState(false);
  const [showStarredMessages, setShowStarredMessages] = useState(false);
  const [showSelectionActionsMenu, setShowSelectionActionsMenu] = useState(false);
  const [showBulkReactionPicker, setShowBulkReactionPicker] = useState(false);
  const [showBulkCustomReactionInput, setShowBulkCustomReactionInput] =
    useState(false);
  const [bulkCustomReactionInput, setBulkCustomReactionInput] = useState("");
  const bulkCustomReactionInputRef = useRef<HTMLInputElement>(null);
  const bulkReactionPickerDismissGuardUntilRef = useRef(0);
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [showComposerFormatOverflow, setShowComposerFormatOverflow] =
    useState(false);
  const [composerSelection, setComposerSelection] = useState({ start: 0, end: 0 });
  const [typingByUserId, setTypingByUserId] = useState<Record<string, string>>({});
  const [recentReactionEmojis, setRecentReactionEmojis] = useState<string[]>(
    CHAT_REACTION_QUICK_DEFAULT,
  );
  const [optimisticReactionsByMessageId, setOptimisticReactionsByMessageId] =
    useState<Record<string, MessageReaction[]>>({});
  const [messageReceiptsById, setMessageReceiptsById] = useState<Record<string, ChatRuntimeReceipt>>({});
  const [messageStarsById, setMessageStarsById] = useState<ChatRuntimeStarsByMessage>({});
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [fullscreenMedia, setFullscreenMedia] =
    useState<ChatMediaAttachment | null>(null);
  const fullscreenMediaRef = useRef<ChatMediaAttachment | null>(null);
  const fullscreenMediaHistoryActiveRef = useRef(false);
  const ignorePreviewPopstateRef = useRef(false);
  const fullscreenMediaTouchStartRef = useRef<{ x: number; y: number } | null>(
    null,
  );
  const [optimisticMessages, setOptimisticMessages] = useState<ChatMessage[]>([]);
  const [activeBackgroundSends, setActiveBackgroundSends] = useState(0);
  const [slowSendingMessageIds, setSlowSendingMessageIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(
    new Set(),
  );

  // New message indicator state
  const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
  // Track if a new message arrived while user is not at the bottom
  const [hasUnseenNewMessage, setHasUnseenNewMessage] = useState(false);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const voiceRecorderRef = useRef<MediaRecorder | null>(null);
  const voiceRecorderStreamRef = useRef<MediaStream | null>(null);
  const voiceRecorderChunksRef = useRef<Blob[]>([]);
  const voiceRecorderMimeTypeRef = useRef("");
  const voiceRecorderCancelRef = useRef(false);
  const voiceRecorderStartTimeRef = useRef<number | null>(null);
  const voiceRecorderTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const previousScrollHeightRef = useRef<number>(0);
  const isOpeningReactionPickerRef = useRef(false);
  const isNavigatingToReplyRef = useRef(false);
  const messageScrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const composerBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressComposerAutoFocusRef = useRef(false);
  const scrollRafRef = useRef<number | null>(null);
  const composerSelectionPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastComposerSelectionRef = useRef<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });
  const loadMoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isLoadingMoreRef = useRef(false);
  const hasMoreMessagesRef = useRef(false);
  const showNewMessageIndicatorRef = useRef(false);
  const participantFetchInFlightRef = useRef<Promise<void> | null>(null);
  const scrollButtonTouchStartTime = useRef<number>(0);
  const scrollButtonRef = useRef<HTMLButtonElement>(null);
  const isScrollingToBottomRef = useRef(false);
  const autoOpenedKeyboardChatIdRef = useRef<string | null>(null);
  const userInitiatedComposerFocusRef = useRef(false);
  const blockingProgrammaticFocusRef = useRef(false);
  const lastSuppressedKeyboardHideAtRef = useRef(0);
  const chatEntryShowRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatEntryShowRetryTimeout2Ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousKeyboardHeightRef = useRef(0);
  const setComposerAutoFocusSuppressed = useCallback((next: boolean) => {
    suppressComposerAutoFocusRef.current = next;
    const input = inputRef.current;
    if (input) {
      input.dataset.keyboardSuppressed = next ? "true" : "false";
    }
  }, []);
  const [activity, setActivity] = useState<ActivityRequest | null>(
    routeState?.prefetchedActivity || null,
  );
  const [isWaitingForChat, setIsWaitingForChat] = useState(true);
  const isActivityDeleted = activity?.isDeleted || false;
  const selectedMediaRef = useRef<ChatComposerMediaItem[]>([]);
  const wasComposerFocusedBeforeSelectionRef = useRef(false);
  const submitDedupeRef = useRef<{ fingerprint: string; ts: number } | null>(null);
  const pendingSendsRef = useRef<Map<string, PendingChatSendPayload>>(new Map());
  const slowSendTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const reactionSyncProcessingRef = useRef(false);
  const reactionSyncWakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const outboxProcessingRef = useRef(false);
  const outboxWakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const starOutboxProcessingRef = useRef(false);
  const starOutboxWakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastDraftPersistRef = useRef<string>("");
  const liveLocationStartInFlightRef = useRef<Set<string>>(new Set());
  const liveLocationWatcherShareIdRef = useRef<string | null>(null);

  const cleanupVoiceNoteRecorder = useCallback(() => {
    if (voiceRecorderTimerRef.current) {
      clearInterval(voiceRecorderTimerRef.current);
      voiceRecorderTimerRef.current = null;
    }

    voiceRecorderStreamRef.current?.getTracks().forEach((track) => track.stop());
    voiceRecorderStreamRef.current = null;
    voiceRecorderRef.current = null;
    voiceRecorderChunksRef.current = [];
    voiceRecorderMimeTypeRef.current = "";
    voiceRecorderStartTimeRef.current = null;
    setIsRecordingVoiceNote(false);
    setVoiceNoteDurationMs(0);
  }, []);

  const finalizeVoiceNoteRecording = useCallback(() => {
    const mimeType = voiceRecorderMimeTypeRef.current || "audio/webm";
    const durationMs = voiceRecorderStartTimeRef.current
      ? Date.now() - voiceRecorderStartTimeRef.current
      : voiceNoteDurationMs;
    const wasCancelled = voiceRecorderCancelRef.current;
    const blob = new Blob(voiceRecorderChunksRef.current, { type: mimeType });

    cleanupVoiceNoteRecorder();

    if (wasCancelled) {
      return;
    }

    if (durationMs < CHAT_VOICE_NOTE_MIN_DURATION_MS || blob.size === 0) {
      addToast("Voice note was too short.", "info");
      return;
    }

    const extension = getVoiceNoteFileExtension(mimeType);
    const fileName = `voice-note-${new Date().toISOString().replace(/[:.]/g, "-")}.${extension}`;
    const file = new File([blob], fileName, {
      type: mimeType,
      lastModified: Date.now(),
    });

    setSelectedMedia((prev) => {
      if (prev.length >= CHAT_MEDIA_MAX_FILES) {
        addToast(`Maximum ${CHAT_MEDIA_MAX_FILES} attachments allowed.`, "error");
        return prev;
      }

      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          file,
          previewUrl: URL.createObjectURL(file),
          type: "audio",
        },
      ];
    });
  }, [addToast, cleanupVoiceNoteRecorder, voiceNoteDurationMs]);

  const startVoiceNoteRecording = useCallback(async () => {
    if (editingMessage || isRecordingVoiceNote) {
      return;
    }

    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      addToast("Voice notes are not supported on this device.", "error");
      return;
    }

    if (selectedMediaRef.current.length >= CHAT_MEDIA_MAX_FILES) {
      addToast(`Maximum ${CHAT_MEDIA_MAX_FILES} attachments allowed.`, "error");
      return;
    }

    if (chat?.activityId !== null && activity?.isReported) {
      return;
    }

    inputRef.current?.blur();
    setIsComposerFocused(false);

    try {
      const stream = await requestVoiceNoteMediaStream(navigator.mediaDevices);
      const mimeType = getSupportedVoiceNoteMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      voiceRecorderStreamRef.current = stream;
      voiceRecorderRef.current = recorder;
      voiceRecorderChunksRef.current = [];
      voiceRecorderMimeTypeRef.current = recorder.mimeType || mimeType || "audio/webm";
      voiceRecorderCancelRef.current = false;
      voiceRecorderStartTimeRef.current = Date.now();
      setVoiceNoteDurationMs(0);
      setIsRecordingVoiceNote(true);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          voiceRecorderChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        if (voiceRecorderCancelRef.current) {
          cleanupVoiceNoteRecorder();
          return;
        }
        finalizeVoiceNoteRecording();
      };
      recorder.onerror = (event) => {
        addToast("Voice note recording failed.", "error");
        cleanupVoiceNoteRecorder();
        crashlytics.logError(
          `Voice note recorder error: ${JSON.stringify(event)}`,
          "ActivityChatPage.tsx",
          event as unknown as Error,
        );
      };

      voiceRecorderTimerRef.current = setInterval(() => {
        if (!voiceRecorderStartTimeRef.current) return;
        setVoiceNoteDurationMs(Date.now() - voiceRecorderStartTimeRef.current);
      }, 250);

      recorder.start(250);
    } catch (error) {
      addToast(getMicrophonePermissionErrorMessage(error), "error");
      cleanupVoiceNoteRecorder();
      crashlytics.logError(
        `Voice note recording failed: ${JSON.stringify((error as Error)?.message || error)}`,
        "ActivityChatPage.tsx",
        error as Error,
      );
    }
  }, [
    activity?.isReported,
    addToast,
    chat?.activityId,
    cleanupVoiceNoteRecorder,
    editingMessage,
    finalizeVoiceNoteRecording,
    isRecordingVoiceNote,
  ]);

  const stopVoiceNoteRecording = useCallback(
    (cancel: boolean) => {
      voiceRecorderCancelRef.current = cancel;

      const recorder = voiceRecorderRef.current;
      if (!recorder) {
        cleanupVoiceNoteRecorder();
        return;
      }

      if (recorder.state === "inactive") {
        cleanupVoiceNoteRecorder();
        return;
      }

      recorder.stop();
    },
    [cleanupVoiceNoteRecorder],
  );

  const appendSelectedFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const availableSlots = CHAT_MEDIA_MAX_FILES - selectedMediaRef.current.length;
      if (availableSlots <= 0) {
        addToast(`Maximum ${CHAT_MEDIA_MAX_FILES} attachments allowed.`, "error");
        return;
      }

      const nextItems: ChatComposerMediaItem[] = [];
      for (const file of files.slice(0, availableSlots)) {
        const detectedType = detectChatAttachmentType(file);

        if (!detectedType) {
          addToast(`Unsupported file type: ${file.name}`, "error");
          continue;
        }

        if (file.size > CHAT_MEDIA_MAX_FILE_SIZE_BYTES) {
          addToast(`File too large: ${file.name}`, "error");
          continue;
        }

        nextItems.push({
          id: crypto.randomUUID(),
          file,
          previewUrl: URL.createObjectURL(file),
          type: detectedType,
        });
      }

      if (nextItems.length > 0) {
        setSelectedMedia((prev) => [...prev, ...nextItems]);
      }
    },
    [addToast],
  );

  const openAttachmentPicker = useCallback(
    ({
      accept,
      multiple = true,
      logLabel,
    }: {
      accept: string;
      multiple?: boolean;
      logLabel: string;
    }) => {
      const logPrefix = `[ChatComposer:+:${logLabel}]`;
      const fileInput = mediaInputRef.current;

      if (!fileInput) {
        console.warn(`${logPrefix} missing file input ref`);
        void crashlytics.log(`${logPrefix} no file input ref`);
        return;
      }

      const wasComposerFocused =
        inputRef.current && document.activeElement === inputRef.current;

      console.log(
        `${logPrefix} pre-open state`,
        JSON.stringify({
          isAndroid,
          wasComposerFocused: Boolean(wasComposerFocused),
          hasInputRef: Boolean(inputRef.current),
          accept,
          multiple,
        }),
      );
      void crashlytics.log(
        `${logPrefix} pre-open isAndroid=${isAndroid} focused=${Boolean(wasComposerFocused)}`,
      );

      if (isAndroid && wasComposerFocused) {
        inputRef.current?.blur();
        console.log(`${logPrefix} blurred composer before opening picker`);
        void crashlytics.log(`${logPrefix} blurred composer before opening picker`);
      }

      fileInput.accept = accept;
      fileInput.multiple = multiple;
      fileInput.value = "";

      try {
        fileInput.click();
        console.log(`${logPrefix} fileInput.click() invoked`);
        void crashlytics.log(`${logPrefix} fileInput.click invoked`);
      } catch (error: any) {
        console.error(`${logPrefix} fileInput.click() failed`, error);
        crashlytics.logError(
          `${logPrefix} fileInput.click failed: ${JSON.stringify(error?.message || error)}`,
          "ActivityChatPage.tsx",
          error,
        );
      }
    },
    [isAndroid],
  );

  function handleAttachmentSheetPick(
    kind: "camera" | "media" | "audio" | "document" | "location",
  ) {
    setShowAttachmentSheet(false);

    if (kind === "location") {
      setShowLocationShareSheet(true);
      return;
    }

    if (kind === "camera") {
      void (async () => {
        try {
          const result = await CameraService.takePhoto({
            quality: 90,
            width: 1600,
            height: 1600,
          });

          if (!result.success || !result.imageUrl) {
            if (result.error && !/cancel/i.test(result.error)) {
              addToast(result.error || "Camera capture failed.", "error");
            }
            return;
          }

          appendSelectedFiles([
            CameraService.dataUrlToFile(
              result.imageUrl,
              `camera-${Date.now()}.jpg`,
            ),
          ]);
        } catch (error) {
          console.error("[ChatComposer:+:camera] capture failed", error);
          addToast("Camera capture failed.", "error");
        }
      })();
      return;
    }

    if (kind === "media") {
      openAttachmentPicker({
        accept: CHAT_MEDIA_ACCEPT_VISUAL,
        multiple: true,
        logLabel: "media",
      });
      return;
    }

    if (kind === "audio") {
      openAttachmentPicker({
        accept: CHAT_MEDIA_ACCEPT_AUDIO,
        multiple: true,
        logLabel: "audio",
      });
      return;
    }

    openAttachmentPicker({
      accept: CHAT_MEDIA_ACCEPT_DOCUMENT,
      multiple: true,
      logLabel: "document",
    });
  }

  const clearMessageSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedMessageIds(new Set());
    setShowSelectionActionsMenu(false);

    if (
      wasComposerFocusedBeforeSelectionRef.current &&
      inputRef.current &&
      !activity?.isReported
    ) {
      requestAnimationFrame(() => {
        // Only focus if not already focused — avoids keyboard restart animation on Android.
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
        }
      });
    }

    wasComposerFocusedBeforeSelectionRef.current = false;
  }, [activity?.isReported]);

  useEffect(() => {
    if (!currentUser?.id) {
      setRecentReactionEmojis(CHAT_REACTION_QUICK_DEFAULT);
      return;
    }
    setRecentReactionEmojis(getRecentReactionsForUser(currentUser.id));
  }, [currentUser?.id]);

  const quickReactionEmojis = useMemo(() => {
    const merged = [
      ...recentReactionEmojis,
      ...CHAT_REACTION_QUICK_DEFAULT,
    ];
    return Array.from(new Set(merged)).slice(0, CHAT_REACTION_QUICK_DEFAULT.length);
  }, [recentReactionEmojis]);

  const bulkPickerReactionEmojis = useMemo(() => {
    const baseList = [...quickReactionEmojis];
    const keyboardCandidates = showBulkCustomReactionInput
      ? extractReactionCandidates(
          bulkCustomReactionInput,
          Math.max(quickReactionEmojis.length, 1),
        )
      : [];

    // In keyboard mode, the user's keyboard picks fully drive the visible list.
    if (keyboardCandidates.length > 0) return keyboardCandidates;

    return baseList;
  }, [quickReactionEmojis, showBulkCustomReactionInput, bulkCustomReactionInput]);

  const armBulkReactionPickerDismissGuard = useCallback((durationMs = CHAT_REACTION_DISMISS_GUARD_MS) => {
    bulkReactionPickerDismissGuardUntilRef.current = Date.now() + durationMs;
  }, []);

  useEffect(() => {
    if (!showBulkCustomReactionInput) return;

    armBulkReactionPickerDismissGuard();

    requestAnimationFrame(() => {
      bulkCustomReactionInputRef.current?.focus();
    });
  }, [showBulkCustomReactionInput, armBulkReactionPickerDismissGuard]);

  const closeBulkReactionPicker = useCallback(() => {
    const activeElement = document.activeElement as HTMLElement | null;
    const isBulkReactionInputFocused = Boolean(
      activeElement?.closest("[data-reaction-keyboard-input]"),
    );
    const isDismissGuardActive =
      showBulkCustomReactionInput &&
      Date.now() < bulkReactionPickerDismissGuardUntilRef.current;

    if (isBulkReactionInputFocused) {
      activeElement?.blur();
      armBulkReactionPickerDismissGuard(250);
      return;
    }

    if (isDismissGuardActive) {
      return;
    }

    setShowBulkReactionPicker(false);
    setShowBulkCustomReactionInput(false);
    setBulkCustomReactionInput("");
  }, [showBulkCustomReactionInput, armBulkReactionPickerDismissGuard]);

  const rememberRecentReaction = useCallback(
    (emoji: string) => {
      if (!currentUser?.id) return;
      const next = writeRecentReactionForUser(currentUser.id, emoji);
      setRecentReactionEmojis(next);
    },
    [currentUser?.id],
  );

  const isMessageCurrentlyStarred = useCallback(
    (messageId: string): boolean => {
      const sidecar = messageStarsById[messageId];
      if (currentUser?.id && sidecar && sidecar[currentUser.id] !== undefined) {
        return sidecar[currentUser.id] === true;
      }

      const message = chat?.messages?.find((item) => item.id === messageId);
      if (!message || !currentUser?.id) return false;
      return Boolean(message.starredBy?.includes(currentUser.id));
    },
    [messageStarsById, chat?.messages, currentUser?.id],
  );

  const applyOptimisticStarUpdate = useCallback(
    (messageId: string, targetStarred: boolean) => {
      if (!currentUser?.id) return;
      setMessageStarsById((prev) => ({
        ...prev,
        [messageId]: {
          ...(prev[messageId] || {}),
          [currentUser.id]: targetStarred,
        },
      }));
    },
    [currentUser?.id],
  );

  const processStarOutbox = useCallback(async () => {
    if (!chatId || starOutboxProcessingRef.current) return;
    starOutboxProcessingRef.current = true;

    try {
      while (true) {
        const records = readStarOutbox()
          .filter((record) => record.chatId === chatId)
          .sort(
            (a, b) =>
              new Date(a.nextAttemptAt).getTime() -
              new Date(b.nextAttemptAt).getTime(),
          );

        if (records.length === 0) {
          if (starOutboxWakeTimeoutRef.current) {
            clearTimeout(starOutboxWakeTimeoutRef.current);
            starOutboxWakeTimeoutRef.current = null;
          }
          break;
        }

        const now = Date.now();
        const due = records.filter(
          (record) => new Date(record.nextAttemptAt).getTime() <= now,
        );

        if (due.length === 0) {
          const nextAt = new Date(records[0].nextAttemptAt).getTime();
          const delay = Math.max(300, nextAt - now);
          if (starOutboxWakeTimeoutRef.current) {
            clearTimeout(starOutboxWakeTimeoutRef.current);
          }
          starOutboxWakeTimeoutRef.current = setTimeout(() => {
            void processStarOutbox();
          }, delay);
          break;
        }

        for (const record of due) {
          try {
            const currentlyStarred = isMessageCurrentlyStarred(record.messageId);
            if (currentlyStarred !== record.targetStarred) {
              const result = await toggleStarMessage(chatId, record.messageId);
              applyOptimisticStarUpdate(record.messageId, result);
            }
            removeStarOutboxRecord(record.id);
          } catch {
            updateStarOutboxRecord(record.id, (prev) => {
              const nextAttempts = prev.attempts + 1;
              const retryDelay = Math.min(
                CHAT_STAR_RETRY_BASE_DELAY_MS * Math.pow(2, Math.min(nextAttempts, 6)),
                CHAT_STAR_RETRY_MAX_DELAY_MS,
              );
              return {
                ...prev,
                attempts: nextAttempts,
                nextAttemptAt: new Date(Date.now() + retryDelay).toISOString(),
              };
            });
          }
        }
      }
    } finally {
      starOutboxProcessingRef.current = false;
    }
  }, [chatId, isMessageCurrentlyStarred, toggleStarMessage, applyOptimisticStarUpdate]);

  useEffect(() => {
    if (!chatId) return;
    void processStarOutbox();

    const trigger = () => {
      void processStarOutbox();
    };
    const onVisibility = () => {
      if (!document.hidden) trigger();
    };

    window.addEventListener("online", trigger);
    window.addEventListener("focus", trigger);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("online", trigger);
      window.removeEventListener("focus", trigger);
      document.removeEventListener("visibilitychange", onVisibility);
      if (starOutboxWakeTimeoutRef.current) {
        clearTimeout(starOutboxWakeTimeoutRef.current);
        starOutboxWakeTimeoutRef.current = null;
      }
    };
  }, [chatId, processStarOutbox]);

  const applyOptimisticReaction = useCallback(
    (messageId: string, emoji: string) => {
      if (!currentUser?.id) return;
      const normalizedEmoji = normalizeCustomReaction(emoji);
      if (!normalizedEmoji) return;

      setOptimisticReactionsByMessageId((prev) => {
        const current = prev[messageId] || [];
        const exists = current.some(
          (reaction) =>
            reaction.userId === currentUser.id &&
            reaction.emoji === normalizedEmoji,
        );
        if (exists) return prev;

        const nextReaction: MessageReaction = {
          emoji: normalizedEmoji,
          userId: currentUser.id,
          timestamp: new Date().toISOString(),
        };

        return {
          ...prev,
          [messageId]: [...current, nextReaction],
        };
      });
    },
    [currentUser?.id],
  );

  const processReactionOutbox = useCallback(async () => {
    if (reactionSyncProcessingRef.current) return;
    reactionSyncProcessingRef.current = true;

    try {
      while (true) {
        const records = readReactionOutbox();
        if (records.length === 0) {
          if (reactionSyncWakeTimeoutRef.current) {
            clearTimeout(reactionSyncWakeTimeoutRef.current);
            reactionSyncWakeTimeoutRef.current = null;
          }
          break;
        }

        const now = Date.now();
        const dueRecords = records
          .filter((record) => new Date(record.nextAttemptAt).getTime() <= now)
          .sort(
            (a, b) =>
              new Date(a.nextAttemptAt).getTime() -
              new Date(b.nextAttemptAt).getTime(),
          );

        if (dueRecords.length === 0) {
          const nextAt = Math.min(
            ...records.map((record) => new Date(record.nextAttemptAt).getTime()),
          );
          const delay = Math.max(300, nextAt - now);

          if (reactionSyncWakeTimeoutRef.current) {
            clearTimeout(reactionSyncWakeTimeoutRef.current);
          }
          reactionSyncWakeTimeoutRef.current = setTimeout(() => {
            void processReactionOutbox();
          }, delay);
          break;
        }

        for (const record of dueRecords) {
          try {
            await addReactionToMessage(record.chatId, record.messageId, record.emoji);
            removeReactionOutboxRecord(record.id);
          } catch {
            const nextAttempts = record.attempts + 1;
            const retryDelay = Math.min(
              CHAT_REACTION_RETRY_BASE_DELAY_MS * Math.pow(2, Math.min(nextAttempts, 6)),
              CHAT_REACTION_RETRY_MAX_DELAY_MS,
            );
            updateReactionOutboxRecord(record.id, (prev) => ({
              ...prev,
              attempts: nextAttempts,
              nextAttemptAt: new Date(Date.now() + retryDelay).toISOString(),
            }));
          }
        }
      }
    } finally {
      reactionSyncProcessingRef.current = false;
    }
  }, [addReactionToMessage]);

  const enqueueReactionSync = useCallback(
    (messageId: string, emoji: string) => {
      if (!chatId) return;
      enqueueReactionOutboxRecord(chatId, messageId, emoji);

      if (reactionSyncWakeTimeoutRef.current) {
        clearTimeout(reactionSyncWakeTimeoutRef.current);
        reactionSyncWakeTimeoutRef.current = null;
      }

      void processReactionOutbox();
    },
    [chatId, processReactionOutbox],
  );

  useEffect(() => {
    void processReactionOutbox();

    const handleOnline = () => {
      void processReactionOutbox();
    };
    const handleVisibility = () => {
      if (!document.hidden) {
        void processReactionOutbox();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (reactionSyncWakeTimeoutRef.current) {
        clearTimeout(reactionSyncWakeTimeoutRef.current);
        reactionSyncWakeTimeoutRef.current = null;
      }
    };
  }, [processReactionOutbox]);

  useEffect(() => {
    if (!chat?.messages?.length) return;

    setOptimisticReactionsByMessageId((prev) => {
      const next: Record<string, MessageReaction[]> = {};
      let changed = false;

      Object.entries(prev).forEach(([messageId, optimistic]) => {
        if (!optimistic.length) {
          changed = true;
          return;
        }

        const message = chat.messages.find((item) => item.id === messageId);
        if (!message) {
          changed = true;
          return;
        }

        const serverKeys = new Set(
          (message.reactions || []).map(
            (reaction) => `${reaction.userId}__${reaction.emoji}`,
          ),
        );

        const remaining = optimistic.filter(
          (reaction) => !serverKeys.has(`${reaction.userId}__${reaction.emoji}`),
        );

        if (remaining.length > 0) {
          next[messageId] = remaining;
          if (remaining.length !== optimistic.length) changed = true;
        } else {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [chat?.messages]);

  useEffect(() => {
    if (!chatId || !currentUser?.id) return;

    let cancelled = false;

    const restoreQueuedClientState = async () => {
      const confirmedClientRequestIds = new Set(
        (chat?.messages || [])
          .map((message) => message.clientRequestId)
          .filter((id): id is string => Boolean(id)),
      );

      const pendingMessageRecords = readChatOutbox().filter(
        (record) =>
          record.chatId === chatId &&
          !confirmedClientRequestIds.has(record.clientRequestId),
      );

      setOptimisticMessages((prev) => {
        const prevByRequestId = new Map(
          prev
            .filter((message) => message.clientRequestId)
            .map((message) => [message.clientRequestId as string, message]),
        );

        return pendingMessageRecords.map((record) => {
          const existing = prevByRequestId.get(record.clientRequestId);
          if (existing) {
            return {
              ...existing,
              text: record.text,
              format: record.format || existing.format || "whatsapp",
              timestamp: record.createdAt || existing.timestamp,
              status: record.status === "failed" ? "failed" : "sending",
            };
          }

          return {
            id: `outbox-${record.clientRequestId}`,
            clientRequestId: record.clientRequestId,
            senderId: currentUser.id,
            text: record.text,
            format: record.format || "whatsapp",
            timestamp: record.createdAt,
            status: record.status === "failed" ? "failed" : "sending",
            readBy: [currentUser.id],
            deliveredTo: [currentUser.id],
            media: [],
            ...(record.replyTo ? { replyTo: record.replyTo } : {}),
          } as ChatMessage;
        });
      });

      for (const record of pendingMessageRecords) {
        if (!record.hasMedia) continue;

        const hydratedMedia = await hydrateOutboxMediaSnapshot(record.clientRequestId);
        if (cancelled || hydratedMedia.length === 0) {
          hydratedMedia.forEach((item) => URL.revokeObjectURL(item.previewUrl));
          continue;
        }

        setOptimisticMessages((prev) =>
          prev.map((message) =>
            message.clientRequestId === record.clientRequestId
              ? {
                  ...message,
                  media: hydratedMedia.map((item) => ({
                    id: item.id,
                    type: item.type,
                    url: item.previewUrl,
                    mimeType: item.file.type,
                    fileName: item.file.name,
                    size: item.file.size,
                  })),
                }
              : message,
          ),
        );
      }

      const pendingReactionRecords = readReactionOutbox().filter(
        (record) => record.chatId === chatId,
      );

      const serverReactionKeysByMessage = new Map<string, Set<string>>();
      (chat?.messages || []).forEach((message) => {
        serverReactionKeysByMessage.set(
          message.id,
          new Set(
            (message.reactions || []).map(
              (reaction) => `${reaction.userId}__${reaction.emoji}`,
            ),
          ),
        );
      });

      const restoredByMessage: Record<string, MessageReaction[]> = {};
      pendingReactionRecords.forEach((record) => {
        const key = `${currentUser.id}__${record.emoji}`;
        if (serverReactionKeysByMessage.get(record.messageId)?.has(key)) return;

        if (!restoredByMessage[record.messageId]) {
          restoredByMessage[record.messageId] = [];
        }
        restoredByMessage[record.messageId].push({
          emoji: record.emoji,
          userId: currentUser.id,
          timestamp: record.createdAt,
        });
      });

      if (!cancelled) {
        setOptimisticReactionsByMessageId(restoredByMessage);
      }
    };

    void restoreQueuedClientState();

    return () => {
      cancelled = true;
    };
  }, [chatId, chat?.messages, currentUser?.id]);

  const enterSelectionMode = useCallback((messageId: string) => {
    const isComposerFocused =
      inputRef.current && document.activeElement === inputRef.current;

    setSelectionMode((previousValue) => {
      if (!previousValue) {
        wasComposerFocusedBeforeSelectionRef.current = Boolean(isComposerFocused);
      }
      return true;
    });
    setSelectedMessageIds(new Set([messageId]));
    // NOTE: Do NOT call inputRef.current.focus() here. On Android WebView, calling
    // focus() on an already-focused element can fire blur+focus events, which triggers
    // setIsComposerFocused(true) → a React re-render that makes selectionMode=true
    // visible before the finger lifts → row onTouchEnd toggles the selection off.
    // The blur guards (useAndroidTouchBlurGuard + handleNativeTouchEnd) handle
    // keyboard preservation during long-press.
  }, []);

  const toggleMessageSelection = useCallback((messageId: string) => {
    setSelectedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      if (next.size === 0) {
        setSelectionMode(false);
        setShowSelectionActionsMenu(false);
      }
      return next;
    });
  }, []);

  const effectiveChatMessages = useMemo(() => {
    if (!chat?.messages?.length) return [] as ChatMessage[];

    const mergeReactions = (
      baseReactions: MessageReaction[] | undefined,
      optimisticReactions: MessageReaction[] | undefined,
    ): MessageReaction[] => {
      if (!optimisticReactions?.length) {
        return baseReactions || [];
      }

      const merged = [...(baseReactions || []), ...(optimisticReactions || [])];
      const deduped = new Map<string, MessageReaction>();
      merged.forEach((reaction) => {
        const key = `${reaction.userId}__${reaction.emoji}`;
        deduped.set(key, reaction);
      });

      const nextReactions = Array.from(deduped.values());
      if (areReactionArraysEqual(baseReactions, nextReactions)) {
        return baseReactions || nextReactions;
      }

      return nextReactions;
    };

    return chat.messages.map((message) => {
      const receipt = messageReceiptsById[message.id];
      const optimisticReactions = optimisticReactionsByMessageId[message.id];
      const activeLiveShare = message.location?.liveShareId
        ? liveLocationSharesById[message.location.liveShareId]
        : undefined;
      const mergedLocation = activeLiveShare && message.location
        ? {
            ...message.location,
            latitude: activeLiveShare.latitude,
            longitude: activeLiveShare.longitude,
            label: activeLiveShare.label || message.location.label,
            placeName: activeLiveShare.placeName || message.location.placeName,
            accuracyMeters:
              activeLiveShare.accuracyMeters ?? message.location.accuracyMeters,
            expiresAt: activeLiveShare.expiresAt || message.location.expiresAt,
          }
        : message.location;
      const starsByUserId = messageStarsById[message.id] || {};
      const sidecarStarredBy = Object.entries(starsByUserId)
        .filter(([, isStarred]) => isStarred === true)
        .map(([uid]) => uid);
      const mergedStarredBy = sidecarStarredBy.length
        ? Array.from(new Set([...(message.starredBy || []), ...sidecarStarredBy]))
        : (message.starredBy || []);
      const mergedReactions = mergeReactions(
        message.reactions,
        optimisticReactions,
      );

      if (!receipt) {
        const hasReactionChanges = !areReactionArraysEqual(
          message.reactions,
          mergedReactions,
        );
        const hasLocationChanges =
          message.location?.latitude !== mergedLocation?.latitude ||
          message.location?.longitude !== mergedLocation?.longitude ||
          message.location?.label !== mergedLocation?.label ||
          message.location?.placeName !== mergedLocation?.placeName ||
          message.location?.accuracyMeters !== mergedLocation?.accuracyMeters ||
          message.location?.expiresAt !== mergedLocation?.expiresAt;
        const hasStarChanges = !areStringArraysEqual(
          message.starredBy,
          mergedStarredBy,
        );

        if (!hasReactionChanges && !hasStarChanges && !hasLocationChanges) {
          return message;
        }

        return {
          ...message,
          ...(mergedLocation ? { location: mergedLocation } : {}),
          reactions: mergedReactions,
          starredBy: mergedStarredBy,
        };
      }

      const readBy = Array.from(
        new Set([
          ...(message.readBy || []),
          ...Object.keys(receipt.readBy || {}),
        ]),
      );
      const deliveredTo = Array.from(
        new Set([
          ...(message.deliveredTo || []),
          ...Object.keys(receipt.deliveredTo || {}),
        ]),
      );

      const hasReadByChanges = !areStringArraysEqual(message.readBy, readBy);
      const hasDeliveredChanges = !areStringArraysEqual(
        message.deliveredTo,
        deliveredTo,
      );
      const hasReactionChanges = !areReactionArraysEqual(
        message.reactions,
        mergedReactions,
      );
      const hasLocationChanges =
        message.location?.latitude !== mergedLocation?.latitude ||
        message.location?.longitude !== mergedLocation?.longitude ||
        message.location?.label !== mergedLocation?.label ||
        message.location?.placeName !== mergedLocation?.placeName ||
        message.location?.accuracyMeters !== mergedLocation?.accuracyMeters ||
        message.location?.expiresAt !== mergedLocation?.expiresAt;
      const hasStarChanges = !areStringArraysEqual(
        message.starredBy,
        mergedStarredBy,
      );

      if (
        !hasReadByChanges &&
        !hasDeliveredChanges &&
        !hasReactionChanges &&
        !hasLocationChanges &&
        !hasStarChanges
      ) {
        return message;
      }

      return {
        ...message,
        readBy,
        deliveredTo,
        ...(mergedLocation ? { location: mergedLocation } : {}),
        reactions: mergedReactions,
        starredBy: mergedStarredBy,
      };
    });
  }, [chat?.messages, liveLocationSharesById, messageReceiptsById, messageStarsById, optimisticReactionsByMessageId]);

  const selectedMessages = useMemo(() => {
    if (!effectiveChatMessages.length || selectedMessageIds.size === 0) return [];
    return effectiveChatMessages.filter((msg) => selectedMessageIds.has(msg.id));
  }, [effectiveChatMessages, selectedMessageIds]);

  const handleCopySelectedMessages = useCallback(async () => {
    if (!effectiveChatMessages.length || selectedMessageIds.size === 0) return;

    const selected = effectiveChatMessages.filter((msg) => selectedMessageIds.has(msg.id));
    const content = buildCopiedContentFromMessages(selected);

    if (!content) {
      addToast("No copyable content in selected messages.", "info");
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = content;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      addToast(`Copied ${selected.length} message${selected.length > 1 ? "s" : ""}.`, "success");
      clearMessageSelection();
    } catch {
      addToast("Failed to copy selected messages.", "error");
    }
  }, [effectiveChatMessages, selectedMessageIds, clearMessageSelection]);

  useEffect(() => {
    selectedMediaRef.current = selectedMedia;
  }, [selectedMedia]);

  useEffect(() => {
    return () => {
      selectedMediaRef.current.forEach((item) => {
        URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, []);

  useEffect(() => {
    setSelectionMode(false);
    setSelectedMessageIds(new Set());
    setShowSelectionActionsMenu(false);
    slowSendTimersRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    slowSendTimersRef.current.clear();
    setSlowSendingMessageIds(new Set());
    pendingSendsRef.current.forEach((payload) => {
      payload.mediaSnapshot.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    });
    pendingSendsRef.current.clear();
    setOptimisticReactionsByMessageId({});
    setOptimisticMessages([]);
    wasComposerFocusedBeforeSelectionRef.current = false;
  }, [chatId]);

  useEffect(() => {
    if (!chatId) return;

    const drafts = readChatDrafts();
    const draft = drafts[chatId];
    if (draft?.text && !inputValue && !editingMessage) {
      setInputValue(draft.text);
      lastDraftPersistRef.current = draft.text;
    }
  }, [chatId]);

  useEffect(() => {
    if (!chatId || editingMessage) return;
    if (inputValue === lastDraftPersistRef.current) return;
    writeChatDraft(chatId, inputValue);
    lastDraftPersistRef.current = inputValue;
  }, [chatId, inputValue, editingMessage]);

  useEffect(() => {
    if (!selectionMode) {
      setShowSelectionActionsMenu(false);
      setShowBulkReactionPicker(false);
      setShowBulkCustomReactionInput(false);
      setBulkCustomReactionInput("");
    }
  }, [selectionMode]);

  // Scroll to bottom - wait for layout then scroll once
  const scrollToBottom = useCallback((onComplete?: () => void) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Wait for layout to be ready, then scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
          setHasScrolledToBottom(true);
          // Clear flag after a short delay to catch any pending blur events
          setTimeout(() => {
            isScrollingToBottomRef.current = false;
          }, 100);
          onComplete?.();
        }
      });
    });
    setShowNewMessageIndicator(false);
  }, []);

  // Early return for invalid chatId - must be AFTER all hooks
  useEffect(() => {
    if (!chatId) {
      navigate("/home");
    }
  }, [chatId, navigate]);

  useEffect(() => {
    if (!chatId) return;
    if (chat) {
      setIsWaitingForChat(false);
      return;
    }

    setIsWaitingForChat(true);
    const timeoutId = setTimeout(() => {
      setIsWaitingForChat(false);
    }, 1200);

    return () => clearTimeout(timeoutId);
  }, [chatId, chat]);

  useEffect(() => {
    const activityId = chat?.activityId;
    if (!activityId) {
      setActivity(null);
      return;
    }

    const localActivity = activities.find((a) => a.id === activityId) || null;
    if (localActivity) {
      setActivity(localActivity);
      setCachedActivityDetail(activityId, localActivity);
      return;
    }

    const cachedDetail = getCachedActivityDetail(activityId);
    if (cachedDetail !== undefined) {
      setActivity(cachedDetail);
      return;
    }

    const inFlight = activityChatDetailInFlight.get(activityId);
    if (inFlight) {
      inFlight.then(setActivity);
      return;
    }

    const fetchPromise = refreshSingleActivity(activityId)
      .then((result) => {
        const resolved = result ?? null;
        setCachedActivityDetail(activityId, resolved);
        return resolved;
      })
      .finally(() => {
        activityChatDetailInFlight.delete(activityId);
      });

    activityChatDetailInFlight.set(activityId, fetchPromise);
    fetchPromise.then(setActivity);
  }, [chat?.activityId, activities, refreshSingleActivity]);
  const isDirectChat = chat?.activityId === null;
  const currentUserActiveLiveShare = useMemo(
    () =>
      Object.values(liveLocationSharesById).find(
        (share) =>
          share.senderId === currentUser?.id &&
          share.isActive !== false &&
          (!share.expiresAt || new Date(share.expiresAt).getTime() > Date.now()),
      ) || null,
    [currentUser?.id, liveLocationSharesById],
  );

  // Check if activity is expired or deleted
  const isActivityExpired = useMemo(() => {
    if (!activity) return false;
    const now = new Date().getTime();
    const endTime =
      toJsDate(activity.dateTime).getTime() + activity.duration * 60 * 1000;
    return endTime < now;
  }, [activity]);

  // Keep inactive group chats open in read-only mode instead of auto-redirecting.

  useEffect(() => {
    if (chatId) {
      markChatNotificationsAsRead(chatId);
    }
  }, [chatId, markChatNotificationsAsRead]);

  // Close keyboard if activity is reported
  useEffect(() => {
    if (!isDirectChat && activity?.isReported) {
      // Blur input to close keyboard
      if (inputRef.current) {
        inputRef.current.blur();
      }
      // Also hide keyboard on native platforms
      if (Capacitor.isNativePlatform()) {
        KeyboardHandler.getInstance().hideKeyboard();
      }
    }
  }, [activity?.isReported, isDirectChat]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Don't focus if activity is reported
    if (!isDirectChat && activity?.isReported) {
      inputRef.current?.blur();
      return;
    }

    const activeElement = document.activeElement as HTMLElement | null;
    const isComposerInputFocused = activeElement === inputRef.current;
    const previousKeyboardHeight = previousKeyboardHeightRef.current;
    const justClosedKeyboard = previousKeyboardHeight > 0 && keyboardHeight === 0;
    previousKeyboardHeightRef.current = keyboardHeight;

    if (keyboardHeight > 0) {
      // Keep composer focus stable, but don't steal focus from reaction keyboard inputs.
      const isOtherTextInputFocused =
        activeElement instanceof HTMLInputElement ||
        (activeElement instanceof HTMLTextAreaElement && !isComposerInputFocused);
      const isReactionKeyboardInputFocused = Boolean(
        activeElement?.closest("[data-reaction-keyboard-input]"),
      );

      // Cancel any pending debounced blur since keyboard is clearly open.
      if (blurDebounceRef.current) {
        clearTimeout(blurDebounceRef.current);
        blurDebounceRef.current = null;
      }

      // User explicitly dismissed the keyboard (back/swipe-down). Don't auto-focus
      // it back open until the user taps the composer again.
      if (suppressComposerAutoFocusRef.current) {
        return;
      }

      if (
        !activity?.isReported &&
        !anyReactionPickerOpen &&
        !isOtherTextInputFocused &&
        !isReactionKeyboardInputFocused &&
        !isComposerInputFocused
      ) {
        inputRef.current?.focus();
      }
    } else {
      // Treat keyboardHeight=0 as a dismissal only when transitioning from
      // an open keyboard state. This avoids blurring during initial focus,
      // where Android often reports a transient 0 before IME fully opens.
      if (justClosedKeyboard) {
        setComposerAutoFocusSuppressed(true);
        if (chatId) {
          dismissedKeyboardByChatId.add(chatId);
        }
        // Single deferred blur keeps keyboard dismissed without fighting
        // in-flight insets animations (which can cause focus oscillation).
        if (blurDebounceRef.current) clearTimeout(blurDebounceRef.current);
        blurDebounceRef.current = setTimeout(() => {
          blurDebounceRef.current = null;
          const stillFocused = document.activeElement === inputRef.current;
          if (!stillFocused) return;
          inputRef.current?.blur();
        }, 90);
      }
    }
  }, [keyboardHeight, activity?.isReported, isDirectChat, anyReactionPickerOpen, isComposerFocused, chatId, setComposerAutoFocusSuppressed]);

  // Track active chat to suppress notifications for this chat
  useEffect(() => {
    if (chatId) {
      PushNotificationManager.setActiveChatId(chatId);
      // Don't focus or show keyboard if activity is reported
      if (!isDirectChat && activity?.isReported) {
        inputRef.current?.blur();
        KeyboardHandler.getInstance().hideKeyboard();
      } else if (
        isDirectChat ||
        (!isDirectChat && activity && !activity.isReported)
      ) {
        // Auto-open keyboard only once per chat entry, and never reopen after
        // a user-initiated dismiss (back/swipe/minimize).
        const hasAutoOpenedForThisChat =
          autoOpenedKeyboardChatIdRef.current === chatId;
        if (!hasAutoOpenedForThisChat) {
          // First chat entry should auto-open keyboard by design.
          // Clear any prior dismissal memory for this chat so entry behavior
          // is consistent across tabs/routes.
          dismissedKeyboardByChatId.delete(chatId);
          setComposerAutoFocusSuppressed(false);
          inputRef.current?.focus();
          KeyboardHandler.getInstance().showKeyboard();

          if (chatEntryShowRetryTimeoutRef.current) {
            clearTimeout(chatEntryShowRetryTimeoutRef.current);
          }
          chatEntryShowRetryTimeoutRef.current = setTimeout(() => {
            chatEntryShowRetryTimeoutRef.current = null;
            const stillFocused = document.activeElement === inputRef.current;
            const viewportKeyboardOpen = (() => {
              const viewport = window.visualViewport;
              if (!viewport) return false;
              return window.innerHeight - viewport.height > 120;
            })();
            const keyboardStillClosed =
              previousKeyboardHeightRef.current === 0 && !viewportKeyboardOpen;

            if (stillFocused && keyboardStillClosed && !suppressComposerAutoFocusRef.current) {
              KeyboardHandler.getInstance().showKeyboard();
            }
          }, 260);

          autoOpenedKeyboardChatIdRef.current = chatId;
        }
      }
      // If activity is still loading (!activity), don't focus yet

      // scroll after a short delay to ensure layout is stable
      let attempts = 0;
      const maxAttempts = 10;
      const tryScroll = () => {
        attempts++;
        scrollToBottom(() => {
          const container = messagesContainerRef.current;
          if (!container) return;

          const distanceFromBottom =
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight;
          const isAtBottom = distanceFromBottom < 1;

          if (!isAtBottom && attempts < maxAttempts) {
            // Retry after a short delay
            setTimeout(tryScroll, 100);
          }
        });
      };
    }

    // Clear active chat when leaving this page
    return () => {
      PushNotificationManager.setActiveChatId(null);
      if (chatEntryShowRetryTimeoutRef.current) {
        clearTimeout(chatEntryShowRetryTimeoutRef.current);
        chatEntryShowRetryTimeoutRef.current = null;
      }
      if (chatEntryShowRetryTimeout2Ref.current) {
        clearTimeout(chatEntryShowRetryTimeout2Ref.current);
        chatEntryShowRetryTimeout2Ref.current = null;
      }
    };
  }, [chatId, activity?.isReported, isDirectChat]);

  const refreshParticipants = useCallback(async () => {
    if (!chat) {
      setParticipantUsers([]);
      return;
    }

    const runFetch = async () => {
      const allUserIds = [
        ...chat.participants,
        ...(chat.leftParticipantIds || []),
        ...(activity?.kickedParticipantIds || []),
      ];
      const uniqueUserIds = Array.from(new Set(allUserIds));

      if (uniqueUserIds.length === 0) {
        setParticipantUsers([]);
        return;
      }

      const users = await getUsersByIds(uniqueUserIds);
      const sortedUsers = [...users].sort((a, b) => a.id.localeCompare(b.id));

      setParticipantUsers((prev) => {
        if (prev.length !== sortedUsers.length) return sortedUsers;

        const hasChanged = prev.some((prevUser, index) => {
          const nextUser = sortedUsers[index];
          return (
            prevUser.id !== nextUser.id ||
            prevUser.username !== nextUser.username ||
            prevUser.profileImageUrl !== nextUser.profileImageUrl ||
            prevUser.isOnline !== nextUser.isOnline ||
            prevUser.lastActiveAt !== nextUser.lastActiveAt
          );
        });

        return hasChanged ? sortedUsers : prev;
      });
    };

    if (participantFetchInFlightRef.current) {
      await participantFetchInFlightRef.current;
      return;
    }

    const fetchPromise = runFetch().finally(() => {
      participantFetchInFlightRef.current = null;
    });

    participantFetchInFlightRef.current = fetchPromise;
    await fetchPromise;
  }, [chat, getUsersByIds, activity?.kickedParticipantIds]);

  useEffect(() => {
    refreshParticipants();
  }, [refreshParticipants]);

  // Refresh participant data when page becomes visible or is navigated to
  // This ensures chat titles and usernames update when users edit their profiles
  useEffect(() => {
    // Refresh when page becomes visible (tab/window focus)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshParticipants();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Also refresh when navigating back to this page
    const handleFocus = () => {
      refreshParticipants();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [refreshParticipants]);

  // Reset pagination when chat changes
  useEffect(() => {
    setDisplayedMessageCount(MESSAGES_PER_PAGE);
    setHasScrolledToBottom(false);
  }, [chatId]);

  // Get paginated messages (show latest N messages)
  const displayedMessages = useMemo(() => {
    if (!chat?.messages) return optimisticMessages;
    const optimisticIds = new Set(optimisticMessages.map((msg) => msg.id));
    const mergedMessages = [
      ...effectiveChatMessages,
      ...optimisticMessages.filter(
        (msg) => !effectiveChatMessages.some((m) => m.id === msg.id),
      ),
    ]
      // Keep deterministic message ordering even while optimistic entries are present.
      .sort((a, b) => {
        const tA = new Date(a.timestamp).getTime();
        const tB = new Date(b.timestamp).getTime();
        if (tA !== tB) return tA - tB;
        if (optimisticIds.has(a.id) === optimisticIds.has(b.id)) {
          return a.id.localeCompare(b.id);
        }
        return optimisticIds.has(a.id) ? 1 : -1;
      });

    const visibleMessages = currentUser
      ? mergedMessages.filter(
          (msg) => !(msg.deletedForUsers || []).includes(currentUser.id),
        )
      : mergedMessages;

    const totalMessages = visibleMessages.length;
    const startIndex = Math.max(0, totalMessages - displayedMessageCount);
    return visibleMessages.slice(startIndex);
  }, [chat?.messages, effectiveChatMessages, displayedMessageCount, currentUser, optimisticMessages]);

  const selectedDisplayedMessages = useMemo(() => {
    if (!displayedMessages.length || selectedMessageIds.size === 0) return [];
    return displayedMessages.filter((msg) => selectedMessageIds.has(msg.id));
  }, [displayedMessages, selectedMessageIds]);

  const primarySelectedMessage =
    selectedDisplayedMessages.length === 1 ? selectedDisplayedMessages[0] : null;

  useEffect(() => {
    if (!chat?.messages?.length || optimisticMessages.length === 0) return;

    const confirmedRequestIds = new Set(
      chat.messages
        .map((msg) => msg.clientRequestId)
        .filter((id): id is string => Boolean(id)),
    );

    if (confirmedRequestIds.size === 0) return;

    setOptimisticMessages((prev) =>
      prev.filter((msg) => !msg.clientRequestId || !confirmedRequestIds.has(msg.clientRequestId)),
    );
  }, [chat?.messages, optimisticMessages.length]);

  // Mark messages as read when viewing chat
  useEffect(() => {
    if (!chatId || !chat || !currentUser) return;

    const unreadMessageIds = effectiveChatMessages
      .filter(
        (msg) =>
          msg.senderId !== currentUser.id &&
          !(msg.readBy || []).includes(currentUser.id),
      )
      .map((msg) => msg.id);

    if (unreadMessageIds.length === 0) return;

    const timeoutId = setTimeout(() => {
      markChatMessagesAsRead({
        chatId,
        messageIds: unreadMessageIds,
      }).catch((error) => {
        crashlytics.logError(
          `Error batch marking messages as read in chat ${chatId}: ${JSON.stringify(error)}`,
          "ActivityChatPage.tsx",
          error,
        );
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [chatId, chat?.messages, currentUser?.id, effectiveChatMessages]);

  // Add a useEffect to lock body scroll on iOS
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Prevent body scroll/bounce on iOS
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.height = "100%";

    // Prevent rubber band scrolling
    const preventBounce = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const isInScrollableModalArea = Boolean(
        target.closest("[data-modal-scroll-allow='true']"),
      );
      if (!messagesContainerRef.current?.contains(target) && !isInScrollableModalArea) {
        e.preventDefault();
      }
    };

    let touchStartX = 0;
    let touchStartY = 0;
    let isHorizontalSwipe = false;

    const trackTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isHorizontalSwipe = false;
    };

    const trackTouchMove = (e: TouchEvent) => {
      const dx = Math.abs(e.touches[0].clientX - touchStartX);
      const dy = Math.abs(e.touches[0].clientY - touchStartY);
      if (dx > 10 && dx > dy) {
        isHorizontalSwipe = true;
      }
    };

    // On Android, prevent keyboard from closing when touching non-input areas
    const preventKeyboardClose = (e: TouchEvent) => {
      if (!isAndroid) return;

      // Never intercept swipe gestures. Let message touch handlers receive
      // touchend so swipe state resets and reply gestures complete cleanly.
      if (isHorizontalSwipe) {
        isHorizontalSwipe = false;
        return;
      }

      const target = e.target as HTMLElement;

      // Only prevent touchend (which causes blur), allow touchstart and touchmove for scrolling
      if (e.type !== "touchend") return;

      // If input is focused, prevent touchend in messages area from causing blur
      if (inputRef.current && document.activeElement === inputRef.current) {
        const isInput =
          target === inputRef.current || inputRef.current.contains(target);
        const isInMessagesArea = messagesContainerRef.current?.contains(target);
        const isSendButton = target.closest('button[type="submit"]');
        const isReactionPicker = target.closest("[data-reaction-picker]");
        const isReactionButton = target.closest("[data-reaction-button]");
        const isMessageStatusButton = target.closest(
          "[data-message-status-button]",
        );
        const isReplyToButton = target.closest("[data-reply-to-button]");
        const isMediaOpenButton = target.closest("[data-media-open-button]");
        const isMediaDownloadButton = target.closest("[data-media-download-button]");
        const isAudioControl = target.closest("audio");
        const isMessageLink = target.closest("[data-message-link]");

        // Prevent default on touchend in messages area (except input, send button, reaction picker, reaction buttons, message status button, and reply-to button)
        // This prevents blur while still allowing scroll gestures and interaction with reactions
        if (
          !isInput &&
          isInMessagesArea &&
          !isSendButton &&
          !isReactionPicker &&
          !isReactionButton &&
          !isMessageStatusButton &&
          !isReplyToButton &&
          !isMediaOpenButton &&
          !isMediaDownloadButton &&
          !isAudioControl &&
          !isMessageLink
        ) {
          if (e.cancelable) {
            e.preventDefault();
            if (!selectionMode) {
              e.stopPropagation();
            }
          }
        }
      }
    };

    // Prevent context menu (long press) from closing keyboard
    const preventContextMenu = (e: Event) => {
      if (!isAndroid) return;

      const target = e.target as HTMLElement;

      // If input is focused (keyboard is open), prevent context menu in messages area
      if (inputRef.current && document.activeElement === inputRef.current) {
        const isInMessagesArea = messagesContainerRef.current?.contains(target);
        const isInput =
          target === inputRef.current || inputRef.current.contains(target);

        if (!isInput && isInMessagesArea) {
          e.preventDefault();
          if (!selectionMode) {
            e.stopPropagation();
          }
        }
      }
    };

    document.addEventListener("touchmove", preventBounce, { passive: false });
    document.addEventListener("touchstart", trackTouchStart, { passive: true });
    document.addEventListener("touchmove", trackTouchMove, { passive: true });
    document.addEventListener("touchend", preventKeyboardClose, {
      passive: false,
    });
    document.addEventListener("contextmenu", preventContextMenu, {
      passive: false,
    });

    // Add native touchstart listener to messages container
    const messagesContainer = messagesContainerRef.current;
    const handleMessagesContainerTouchStart = (e: TouchEvent) => {
      if (!isAndroid) return;

      const target = e.target as HTMLElement;

      // Only prevent if keyboard is open and touching the container itself (background)
      if (inputRef.current && document.activeElement === inputRef.current) {
        if (target === messagesContainer) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    if (messagesContainer) {
      messagesContainer.addEventListener(
        "touchstart",
        handleMessagesContainerTouchStart,
        { passive: false },
      );
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
      document.removeEventListener("touchmove", preventBounce);
      document.removeEventListener("touchstart", trackTouchStart);
      document.removeEventListener("touchmove", trackTouchMove);
      document.removeEventListener("touchend", preventKeyboardClose);
      document.removeEventListener("contextmenu", preventContextMenu);
      if (messagesContainer) {
        messagesContainer.removeEventListener(
          "touchstart",
          handleMessagesContainerTouchStart,
        );
      }
    };
  }, [isAndroid, selectionMode]);

  // Separate useEffect for input blur handler - persists even when button is removed
  useEffect(() => {
    if (!isAndroid || !inputRef.current) return;

    const input = inputRef.current;

    const handleInputBlur = (e: FocusEvent) => {

      if (isScrollingToBottomRef.current) {
        // Immediate refocus to minimize keyboard flicker
        // Note: preventDefault doesn't work on blur (already happened), so just refocus ASAP
        input.focus();
      }
    };

    input.addEventListener("blur", handleInputBlur, { capture: true });

    return () => {
      input.removeEventListener("blur", handleInputBlur, { capture: true });
    };
  }, [isAndroid]);

  // Add native touch listeners for scroll button to prevent keyboard close
  // This effect runs whenever the button appears/disappears
  useEffect(() => {
    if (
      !isAndroid ||
      !scrollButtonRef.current ||
      !inputRef.current ||
      !showNewMessageIndicator
    )
      return;

    const button = scrollButtonRef.current;
    const input = inputRef.current;
    let touchStartTime = 0;
    let isButtonTouchActive = false;
    let longPressTimer: NodeJS.Timeout | null = null;
    let isPotentialLongPress = false;

    const handleButtonTouchStart = (e: TouchEvent) => {
      touchStartTime = Date.now();
      isButtonTouchActive = true;
      isPotentialLongPress = false;

      // Don't prevent default here - it blocks React's synthetic events
      // Instead, we'll handle focus on the button itself

      // Set a timer to mark this as a potential long press after 300ms
      longPressTimer = setTimeout(() => {
        isPotentialLongPress = true;
      }, 300);
    };

    // Prevent the button from receiving focus when touched
    const handleButtonFocus = (e: FocusEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // If button gets focus, immediately blur it and refocus input
      button.blur();
      if (input && document.activeElement !== input) {
        input.focus();
      }
    };

    const handleButtonTouchEnd = (e: TouchEvent) => {
      const touchDuration = Date.now() - touchStartTime;

      // Clear the long press timer
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }

      // Only prevent on LONG press (>=500ms) to stop keyboard close
      // For short tap (<500ms), allow React handlers to execute scroll action
      if (input === document.activeElement && touchDuration >= 500) {
        if (e.cancelable) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      }

      // Small delay to ensure any pending events see the flag
      setTimeout(() => {
        isButtonTouchActive = false;
        isPotentialLongPress = false;
      }, 100);
    };

    const handleButtonTouchCancel = (e: TouchEvent) => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }

      if (input === document.activeElement) {
        e.preventDefault();
        e.stopPropagation();
      }
      isButtonTouchActive = false;
      isPotentialLongPress = false;
    };

    // Log blur events for debugging (scroll blur handled by separate useEffect)
    const handleInputBlur = (e: FocusEvent) => {
      if (isButtonTouchActive && isPotentialLongPress) {
      }
    };

    button.addEventListener("touchstart", handleButtonTouchStart, {
      passive: false,
      capture: true,
    });
    button.addEventListener("touchend", handleButtonTouchEnd, {
      passive: false,
      capture: true,
    });
    button.addEventListener("touchcancel", handleButtonTouchCancel, {
      passive: false,
      capture: true,
    });
    button.addEventListener("focus", handleButtonFocus, { capture: true });
    input.addEventListener("blur", handleInputBlur, { capture: true });

    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
      button.removeEventListener("touchstart", handleButtonTouchStart, {
        capture: true,
      });
      button.removeEventListener("touchend", handleButtonTouchEnd, {
        capture: true,
      });
      button.removeEventListener("touchcancel", handleButtonTouchCancel, {
        capture: true,
      });
      button.removeEventListener("focus", handleButtonFocus, { capture: true });
      input.removeEventListener("blur", handleInputBlur, { capture: true });
    };
  }, [isAndroid, showNewMessageIndicator]); // Re-attach when button shows/hides

  // Handle keyboard show/hide on native platforms
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const cb = (isOpen: boolean, height: number) => {
      setKeyboardHeight(height);
    };
    KeyboardHandler.getInstance().initialize();
    KeyboardHandler.getInstance().onKeyboardChange(cb);
    return () => {
      KeyboardHandler.getInstance().offKeyboardChange(cb);
    };
  }, []);

  // Handle keyboard height changes
  const prevKeyboardHeight = useRef(0);
  const keyboardScrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const blurDebounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Clear any pending scroll from previous keyboard changes
    if (keyboardScrollTimeout.current) {
      clearTimeout(keyboardScrollTimeout.current);
      keyboardScrollTimeout.current = null;
    }

    // Don't handle if we're opening the reaction picker or if any picker is open
    if (isOpeningReactionPickerRef.current || anyReactionPickerOpen) {
      prevKeyboardHeight.current = keyboardHeight;
      return;
    }

    const isKeyboardOpening = keyboardHeight > prevKeyboardHeight.current;
    const isKeyboardClosing = keyboardHeight < prevKeyboardHeight.current;

    prevKeyboardHeight.current = keyboardHeight;

    // Handle both opening and closing
    if (!isKeyboardOpening && !isKeyboardClosing) {
      return;
    }

    // Wait to see if keyboard stabilizes before scrolling
    // If keyboard changes again within 150ms, this will be cancelled
    keyboardScrollTimeout.current = setTimeout(() => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      if (isKeyboardOpening) {
        // Account for keyboard height in threshold since container shrinks when keyboard opens
        const threshold = Math.max(200, keyboardHeight + 50);
        const isNearBottom = distanceFromBottom < threshold;

        if (isNearBottom) {
          scrollToBottom();
        } else {
          setShowNewMessageIndicator(true);
        }
      } else if (isKeyboardClosing) {
        // When closing, use a larger threshold since nav bar reappears
        const threshold = Math.max(200, navHeight + 100);
        const isNearBottom = distanceFromBottom < threshold;

        if (isNearBottom) {
          // Scroll to maintain bottom visibility when keyboard closes
          scrollToBottom();
        }
      }

      keyboardScrollTimeout.current = null;
    }, 150);

    return () => {
      if (keyboardScrollTimeout.current) {
        clearTimeout(keyboardScrollTimeout.current);
        keyboardScrollTimeout.current = null;
      }
    };
  }, [keyboardHeight, navHeight, scrollToBottom, anyReactionPickerOpen]);

  const hasMoreMessages = effectiveChatMessages.length > displayedMessageCount;

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  useEffect(() => {
    hasMoreMessagesRef.current = hasMoreMessages;
  }, [hasMoreMessages]);

  useEffect(() => {
    showNewMessageIndicatorRef.current = showNewMessageIndicator;
  }, [showNewMessageIndicator]);

  const loadMoreMessages = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (isLoadingMoreRef.current || !hasMoreMessagesRef.current) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    previousScrollHeightRef.current = container.scrollHeight;

    if (loadMoreTimeoutRef.current) {
      clearTimeout(loadMoreTimeoutRef.current);
      loadMoreTimeoutRef.current = null;
    }

    loadMoreTimeoutRef.current = setTimeout(() => {
      setDisplayedMessageCount((prev) => prev + MESSAGES_PER_PAGE);
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
      loadMoreTimeoutRef.current = null;
    }, 300);
  }, [MESSAGES_PER_PAGE]);

  // Handle scroll to load more messages and show/hide scroll-to-bottom button
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (scrollRafRef.current !== null) return;

    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;

      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      const isNearBottom = distanceFromBottom < 100;

      if (!isNearBottom && !showNewMessageIndicatorRef.current) {
        showNewMessageIndicatorRef.current = true;
        setShowNewMessageIndicator(true);
      } else if (isNearBottom && showNewMessageIndicatorRef.current) {
        showNewMessageIndicatorRef.current = false;
        setShowNewMessageIndicator(false);
      }

        if (isLoadingMoreRef.current || !hasMoreMessagesRef.current) return;
        if (container.scrollTop < 100) {
          loadMoreMessages();
        }
    });
  }, [loadMoreMessages]);

  // Attach scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
        loadMoreTimeoutRef.current = null;
      }
    };
  }, [handleScroll]);

  // Maintain scroll position after loading more messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || previousScrollHeightRef.current === 0) return;

    // Calculate new scroll position to maintain visual position
    const newScrollHeight = container.scrollHeight;
    const scrollDiff = newScrollHeight - previousScrollHeightRef.current;

    if (scrollDiff > 0) {
      container.scrollTop = scrollDiff;
      previousScrollHeightRef.current = 0;
    }
  }, [displayedMessages]);

  // Single unified scroll effect - handles initial load, new messages, and notifications
  useEffect(() => {
    if (effectiveChatMessages.length === 0) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    // Don't scroll if we're opening the reaction picker or if any picker is open
    if (isOpeningReactionPickerRef.current || anyReactionPickerOpen) return;

    // Clear any pending scroll from previous message changes
    if (messageScrollTimeout.current) {
      clearTimeout(messageScrollTimeout.current);
      messageScrollTimeout.current = null;
    }

    // Check if this is from a notification
    const urlHasNotificationHash =
      window.location.hash.includes("from-notification");
    const hasNotificationState =
      window.location.search.includes("notification");
    const isFromNotification = urlHasNotificationHash || hasNotificationState;

    // For notifications, scroll immediately without debouncing
    if (isFromNotification) {
      scrollToBottom();
      setShowNewMessageIndicator(false);
      setHasUnseenNewMessage(false);
      return;
    }

    // For regular messages, debounce to handle rapid message arrivals
    messageScrollTimeout.current = setTimeout(() => {
      // Calculate distance from bottom
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      const isNearBottom = !hasScrolledToBottom || distanceFromBottom < 300;

      if (isNearBottom) {
        scrollToBottom();
        setShowNewMessageIndicator(false);
        setHasUnseenNewMessage(false);
      } else {
        // User is not near bottom and new message arrived - show indicator
        setShowNewMessageIndicator(true);
        // check if the new message has come from another user and is not read yet
        const latestMessage = effectiveChatMessages[effectiveChatMessages.length - 1];
        if (
          latestMessage.senderId !== currentUser?.id &&
          !latestMessage.readBy.includes(currentUser?.id || "")
        ) {
          setHasUnseenNewMessage(true);
        }
      }

      messageScrollTimeout.current = null;
    }, 100); // 100ms debounce for message scroll

    return () => {
      if (messageScrollTimeout.current) {
        clearTimeout(messageScrollTimeout.current);
        messageScrollTimeout.current = null;
      }
    };
  }, [
    effectiveChatMessages,
    hasScrolledToBottom,
    chatId,
    headerHeight,
    footerHeight,
    scrollToBottom,
    anyReactionPickerOpen,
  ]);

  const handleReply = (message: ChatMessage, sender: User) => {
    // Don't focus if activity is reported
    if (!isDirectChat && activity?.isReported) {
      return;
    }

    // Reply action comes from an explicit tap/swipe gesture.
    // Mark next focus as user-initiated so Android suppression logic
    // doesn't treat it as programmatic and ignore the first attempt.
    userInitiatedComposerFocusRef.current = true;
    setComposerAutoFocusSuppressed(false);
    if (chatId) {
      dismissedKeyboardByChatId.delete(chatId);
    }

    setReplyingTo({ message, sender });

    // Defer focus until after selection/header state updates settle.
    requestAnimationFrame(() => {
      const composerAlreadyFocused = document.activeElement === inputRef.current;
      if (!composerAlreadyFocused) {
        inputRef.current?.focus();
      }

      if (isAndroid && Capacitor.isNativePlatform()) {
        KeyboardHandler.getInstance().showKeyboard();
      }
    });
  };

  const handleToggleSearch = useCallback(() => {
    if (searchActive) {
      setSearchActive(false);
      setSearchQuery("");
      return;
    }

    setSearchActive(true);
    setSearchMatchIndex(0);

    requestAnimationFrame(() => {
      inputRef.current?.blur();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    });
  }, [searchActive]);

  const cancelReply = () => {
    // Check if keyboard is currently open (input is focused)
    const wasKeyboardOpen =
      inputRef.current && document.activeElement === inputRef.current;

    setReplyingTo(null);
    setReplyPreviewHeight(0);

    // Force immediate footer height recalculation after reply preview is removed
    requestAnimationFrame(() => {
      if (footerRef.current) {
        const newFooterHeight = footerRef.current.clientHeight;
        if (Math.abs(newFooterHeight - footerHeight) > 2) {
          setFooterHeight(newFooterHeight);
        }
      }
    });

    // Only refocus if keyboard was already open
    if (wasKeyboardOpen) {
      // Don't refocus if activity is reported
      if (!isDirectChat && activity?.isReported) {
        return;
      }
      inputRef.current?.focus();
    }
  };

  const handleShowReadReceipts = (message: ChatMessage) => {
    setReadReceiptsMessage(message);
  };

  const handleShowMessageStatus = (message: ChatMessage) => {
    setMessageStatusMessage(message);
  };

  const handleAddReaction = async (message: ChatMessage, emoji: string) => {
    if (!chatId) return;
    applyOptimisticReaction(message.id, emoji);
    setReactingToMessage(null);

    try {
      enqueueReactionSync(message.id, emoji);
    } catch (error) {
      crashlytics.logError(
        `Error adding reaction: ${JSON.stringify(error?.message)}`,
        "ActivityChatPage.tsx",
        error,
      );
    }
  };

  const handleEditMessage = useCallback((message: ChatMessage) => {
    if (message.deletedForEveryone) return;
    if (message.senderId !== currentUser?.id) return;

    if (message.status === "sending" || message.status === "failed") {
      const pendingPayload = pendingSendsRef.current.get(message.id);
      if (!pendingPayload) {
        addToast("Message is still being processed. Edit after it is delivered.", "info");
        return;
      }
    }

    if (!isMessageWithinWindow(message.timestamp, CHAT_MESSAGE_ACTION_WINDOW_MS)) {
      addToast("You can only edit messages within 10 minutes.", "info");
      return;
    }

    setEditingMessage(message);
    setReplyingTo(null);
    setInputValue(message.text || "");
    inputRef.current?.focus();
  }, [currentUser?.id, addToast]);

  const cancelEdit = () => {
    const wasKeyboardOpen =
      inputRef.current && document.activeElement === inputRef.current;
    setEditingMessage(null);
    setInputValue("");
    if (wasKeyboardOpen) {
      inputRef.current?.focus();
    }
  };

  const handleReplySelectedMessage = useCallback(() => {
    if (!primarySelectedMessage || !currentUser) return;

    const selectedSender =
      participantUsers.find(
        (participant) => participant.id === primarySelectedMessage.senderId,
      ) ||
      (currentUser.id === primarySelectedMessage.senderId ? currentUser : null);

    if (!selectedSender) {
      addToast("Unable to reply to this message right now.", "error");
      return;
    }

    handleReply(primarySelectedMessage, selectedSender);
    clearMessageSelection();
  }, [
    primarySelectedMessage,
    currentUser,
    participantUsers,
    handleReply,
    clearMessageSelection,
  ]);

  const handleEditSelectedMessage = useCallback(() => {
    if (!primarySelectedMessage) return;
    handleEditMessage(primarySelectedMessage);
    clearMessageSelection();
  }, [primarySelectedMessage, handleEditMessage, clearMessageSelection]);

  const handleCancelSelectedPendingMessage = useCallback(() => {
    if (!primarySelectedMessage) return;
    if (primarySelectedMessage.status !== "sending") return;

    const payload = pendingSendsRef.current.get(primarySelectedMessage.id);
    if (!payload) {
      addToast("Unable to cancel this message right now.", "info");
      return;
    }

    pendingSendsRef.current.set(primarySelectedMessage.id, {
      ...payload,
      cancelled: true,
    });
    removeChatOutboxRecord(payload.clientRequestId);
    pendingSendsRef.current.delete(primarySelectedMessage.id);

    const timer = slowSendTimersRef.current.get(primarySelectedMessage.id);
    if (timer) {
      clearTimeout(timer);
      slowSendTimersRef.current.delete(primarySelectedMessage.id);
    }

    setSlowSendingMessageIds((prev) => {
      if (!prev.has(primarySelectedMessage.id)) return prev;
      const next = new Set(prev);
      next.delete(primarySelectedMessage.id);
      return next;
    });

    payload.mediaSnapshot.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setOptimisticMessages((prev) =>
      prev.filter((msg) => msg.id !== primarySelectedMessage.id),
    );

    if (editingMessage?.id === primarySelectedMessage.id) {
      setEditingMessage(null);
      setInputValue("");
    }

    addToast("Message cancelled.", "success");
    clearMessageSelection();
  }, [primarySelectedMessage, editingMessage?.id, addToast, clearMessageSelection]);

  const handleInfoSelectedMessage = useCallback(() => {
    if (!primarySelectedMessage) return;
    setMessageStatusMessage(primarySelectedMessage);
    clearMessageSelection();
  }, [primarySelectedMessage, clearMessageSelection]);

  const activityById = useMemo(
    () => new Map(activities.map((item) => [item.id, item])),
    [activities],
  );

  const canForwardToChat = useCallback(
    (candidate: Chat) =>
      isForwardTargetEligible({
        candidate,
        currentUserId: currentUser?.id,
        currentChatId: chatId,
        candidateActivity: candidate.activityId
          ? (activityById.get(candidate.activityId) ?? null)
          : null,
      }),
    [currentUser?.id, chatId, activityById],
  );

  const handleForwardSelectedMessages = useCallback(() => {
    if (selectedMessages.length === 0) return;
    setForwardTargetIds(new Set());
    setShowForwardPicker(true);
  }, [selectedMessages]);

  const handleSubmitForwardSelectedMessages = useCallback(async () => {
    const currentUserId = currentUser?.id;
    const chatsForUser = currentUserId ? getChatsForUser(currentUserId) : [];

    const targetIds = Array.from(forwardTargetIds).filter((targetId) => {
      if (!currentUserId) return false;

      const candidate = chatsForUser.find((chatCandidate) => chatCandidate.id === targetId);
      if (!candidate) return false;
      return canForwardToChat(candidate);
    });

    if (!chatId || selectedMessages.length === 0 || targetIds.length === 0) {
      return;
    }

    setIsForwarding(true);
    try {
      const count = await forwardMessages(
        chatId,
        selectedMessages.map((message) => message.id),
        targetIds,
      );

      addToast(
        `Forwarded ${count} message${count === 1 ? "" : "s"}.`,
        "success",
      );
      setShowForwardPicker(false);
      setForwardTargetIds(new Set());
      clearMessageSelection();
    } catch (error) {
      crashlytics.logError(
        `Error forwarding selected messages: ${JSON.stringify(error?.message)}`,
        "ActivityChatPage.tsx",
        error,
      );
      addToast("Failed to forward selected messages.", "error");
    } finally {
      setIsForwarding(false);
    }
  }, [
    chatId,
    currentUser?.id,
    selectedMessages,
    forwardTargetIds,
    getChatsForUser,
    canForwardToChat,
    forwardMessages,
    clearMessageSelection,
  ]);

  const handleStarSelectedMessages = useCallback(async () => {
    if (!chatId || selectedMessages.length === 0) return;

    try {
      const results = selectedMessages.map((message) => {
        const currentlyStarred = isMessageCurrentlyStarred(message.id);
        const targetStarred = !currentlyStarred;
        applyOptimisticStarUpdate(message.id, targetStarred);
        upsertStarOutboxRecord(chatId, message.id, targetStarred);
        void processStarOutbox();
        return targetStarred;
      });
      const starredCount = results.filter(Boolean).length;
      const unstarredCount = results.length - starredCount;

      if (starredCount > 0 && unstarredCount > 0) {
        addToast(
          `Updated stars: ${starredCount} starred, ${unstarredCount} unstarred.`,
          "success",
        );
      } else if (starredCount > 0) {
        addToast(
          `Starred ${starredCount} message${starredCount === 1 ? "" : "s"}.`,
          "success",
        );
      } else {
        addToast(
          `Unstarred ${unstarredCount} message${unstarredCount === 1 ? "" : "s"}.`,
          "success",
        );
      }

      clearMessageSelection();
    } catch (error) {
      crashlytics.logError(
        `Error starring selected messages: ${JSON.stringify(error?.message)}`,
        "ActivityChatPage.tsx",
        error,
      );
      addToast("Failed to update stars.", "error");
    }
  }, [
    chatId,
    selectedMessages,
    isMessageCurrentlyStarred,
    applyOptimisticStarUpdate,
    processStarOutbox,
    clearMessageSelection,
  ]);

  const handleUnstarFromStarredMessages = useCallback(
    (messageId: string) => {
      if (!chatId) return;
      applyOptimisticStarUpdate(messageId, false);
      upsertStarOutboxRecord(chatId, messageId, false);
      void processStarOutbox();
    },
    [chatId, applyOptimisticStarUpdate, processStarOutbox],
  );

  const handleReactSelectedMessages = useCallback(
    async (emoji: string) => {
      if (!chatId || selectedDisplayedMessages.length === 0) return;

      const eligibleMessages = selectedDisplayedMessages.filter(
        (message) => !pendingSendsRef.current.has(message.id),
      );

      if (eligibleMessages.length === 0) {
        addToast("Only delivered messages can be reacted to.", "info");
        return;
      }

      try {
        eligibleMessages.forEach((message) => {
          applyOptimisticReaction(message.id, emoji);
          enqueueReactionSync(message.id, emoji);
        });
        addToast(
          `Reacted to ${eligibleMessages.length} message${eligibleMessages.length > 1 ? "s" : ""}.`,
          "success",
        );
        rememberRecentReaction(emoji);
        setShowBulkReactionPicker(false);
        setShowBulkCustomReactionInput(false);
        setBulkCustomReactionInput("");
        clearMessageSelection();
      } catch (error) {
        crashlytics.logError(
          `Error adding bulk reactions: ${JSON.stringify(error?.message)}`,
          "ActivityChatPage.tsx",
          error,
        );
        addToast("Failed to add reaction.", "error");
      }
    },
    [
      chatId,
      selectedDisplayedMessages,
      applyOptimisticReaction,
      enqueueReactionSync,
      clearMessageSelection,
      addToast,
      rememberRecentReaction,
    ],
  );

  const handleDeleteSelectedMessages = useCallback(
    async (mode: "for_me" | "for_everyone") => {
      if (!chatId || selectedMessages.length === 0) return;

      const targets =
        mode === "for_everyone"
          ? selectedMessages.filter(
            (message) =>
              message.senderId === currentUser?.id &&
              !message.deletedForEveryone &&
              isMessageWithinWindow(
                message.timestamp,
                CHAT_MESSAGE_ACTION_WINDOW_MS,
              ),
          )
          : selectedMessages.filter((message) =>
              isMessageWithinWindow(
                message.timestamp,
                CHAT_MESSAGE_ACTION_WINDOW_MS,
              ),
            );

      if (targets.length === 0) {
        addToast("No eligible messages for this action.", "info");
        return;
      }

      try {
        await Promise.all(
          targets.map((message) => deleteMessage(chatId, message.id, mode)),
        );

        if (
          editingMessage &&
          targets.some((message) => message.id === editingMessage.id)
        ) {
          setEditingMessage(null);
          setInputValue("");
        }

        addToast(
          mode === "for_everyone"
            ? `Deleted ${targets.length} message${targets.length > 1 ? "s" : ""} for everyone`
            : `Deleted ${targets.length} message${targets.length > 1 ? "s" : ""} for you`,
          "success",
        );
        clearMessageSelection();
      } catch (error) {
        crashlytics.logError(
          `Error deleting selected messages: ${JSON.stringify(error?.message)}`,
          "ActivityChatPage.tsx",
          error,
        );
        addToast("Failed to delete selected messages.", "error");
      }
    },
    [
      chatId,
      selectedMessages,
      currentUser?.id,
      deleteMessage,
      editingMessage,
      clearMessageSelection,
    ],
  );

  const handleDeleteSelectedDefault = useCallback(() => {
    if (selectedMessages.length === 0) return;
    const deleteForEveryoneEligible = selectedMessages.every(
      (message) =>
        message.senderId === currentUser?.id &&
        !message.deletedForEveryone &&
        isMessageWithinWindow(
          message.timestamp,
          CHAT_MESSAGE_ACTION_WINDOW_MS,
        ),
    );
    handleDeleteSelectedMessages(
      deleteForEveryoneEligible ? "for_everyone" : "for_me",
    );
  }, [
    selectedMessages.length,
    selectedMessages,
    currentUser?.id,
    handleDeleteSelectedMessages,
  ]);

  const handleOpenFullscreenMedia = useCallback(
    (media: ChatMediaAttachment) => {
      if (media.type === "document") {
        void (async () => {
          const opened = await openChatAttachmentInBrowser(media);
          if (!opened) {
            addToast("Unable to open document preview. Please try again.", "error");
          }
        })();
        return;
      }

      if (
        media.type === "image" ||
        media.type === "video"
      ) {
        if (!fullscreenMediaHistoryActiveRef.current) {
          const currentState = window.history.state;
          window.history.pushState(
            {
              ...(typeof currentState === "object" && currentState ? currentState : {}),
              __chatMediaPreview: true,
            },
            "",
          );
          fullscreenMediaHistoryActiveRef.current = true;
        }
        setFullscreenMedia(media);
      }
    },
    [addToast],
  );

  const closeFullscreenMedia = useCallback((fromPopState = false) => {
    setFullscreenMedia(null);

    if (!fromPopState && fullscreenMediaHistoryActiveRef.current) {
      ignorePreviewPopstateRef.current = true;
      fullscreenMediaHistoryActiveRef.current = false;
      window.history.back();
      return;
    }

    if (fromPopState) {
      fullscreenMediaHistoryActiveRef.current = false;
    }
  }, []);

  useEffect(() => {
    fullscreenMediaRef.current = fullscreenMedia;
  }, [fullscreenMedia]);

  useEffect(() => {
    const token = "chat-media-preview";
    SwipeNavigationManager.setGestureSuppressed(token, Boolean(fullscreenMedia));
    return () => {
      SwipeNavigationManager.setGestureSuppressed(token, false);
    };
  }, [fullscreenMedia]);

  useEffect(() => {
    const handlePopState = () => {
      if (ignorePreviewPopstateRef.current) {
        ignorePreviewPopstateRef.current = false;
        return;
      }

      if (fullscreenMediaRef.current) {
        closeFullscreenMedia(true);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [closeFullscreenMedia]);

  useEffect(() => {
    if (!isAndroid || !Capacitor.isNativePlatform()) return;

    const listenerPromise = CapacitorApp.addListener("backButton", () => {
      if (!fullscreenMediaRef.current) return;
      closeFullscreenMedia();
    });

    return () => {
      listenerPromise
        .then((listener) => listener.remove())
        .catch(() => {
          // ignore cleanup failures for non-critical preview listener
        });
    };
  }, [closeFullscreenMedia, isAndroid]);

  const handleDownloadMedia = useCallback(async (media: ChatMediaAttachment) => {
    const shouldOpenInBrowser =
      media.type === "image" ||
      media.type === "video" ||
      media.type === "document";
    const success = shouldOpenInBrowser
      ? await openChatAttachmentInBrowser(media)
      : await downloadChatAttachment(media);
    if (!success) {
      addToast("Unable to download attachment. Please try again.", "error");
    }
    return success;
  }, [addToast]);

  const handlePickerStateChange = useCallback((isOpen: boolean) => {
    setAnyReactionPickerOpen(isOpen);
  }, []);

  const handleFullscreenMediaTouchStart = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      fullscreenMediaTouchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
    },
    [],
  );

  const handleFullscreenMediaTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const start = fullscreenMediaTouchStartRef.current;
      fullscreenMediaTouchStartRef.current = null;
      if (!start) return;

      const touch = event.changedTouches?.[0];
      if (!touch) return;

      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;

      // Close preview on deliberate horizontal swipe in either direction.
      if (Math.abs(deltaX) >= 60 && Math.abs(deltaX) > Math.abs(deltaY)) {
        closeFullscreenMedia();
      }
    },
    [closeFullscreenMedia],
  );

  const closeHeaderOverlays = useCallback(() => {
    setReactingToMessage(null);
    setAnyReactionPickerOpen(false);
    setShowBulkReactionPicker(false);
    setShowBulkCustomReactionInput(false);
    setBulkCustomReactionInput("");
  }, []);

  const runHeaderAction = useCallback(
    (action: () => void) => {
      closeHeaderOverlays();
      action();
    },
    [closeHeaderOverlays],
  );

  const handleScrollToBottom = () => {
    setShowNewMessageIndicator(false);
    isScrollingToBottomRef.current = true;
    scrollToBottom();
  };

  const scrollToMessage = (messageId: string) => {
    // Don't scroll if a reaction picker is open - it would disrupt the picker
    if (isOpeningReactionPickerRef.current || anyReactionPickerOpen) return;

    // Save keyboard state and input ref before any operations
    const wasKeyboardOpen =
      isAndroid &&
      inputRef.current &&
      document.activeElement === inputRef.current;
    const savedInputRef = inputRef.current;

    // Helper to restore keyboard
    const restoreKeyboard = () => {
      if (wasKeyboardOpen && savedInputRef) {
        if (savedInputRef && document.activeElement !== savedInputRef) {
          // Don't restore keyboard if activity is reported
          if (activity?.isReported) return;
          savedInputRef.focus();
        }
      }
    };

    // Check if message exists in the full chat
    if (!effectiveChatMessages.length) {
      restoreKeyboard();
      return;
    }

    const messageIndex = effectiveChatMessages.findIndex((m) => m.id === messageId);
    if (messageIndex === -1) {
      restoreKeyboard();
      return;
    }

    // Check if message is in displayed messages
    const messageInDisplayed = displayedMessages.find(
      (m) => m.id === messageId,
    );
    let needsToLoadMore = false;

    if (!messageInDisplayed) {
      // Message exists but not loaded yet - load more messages until we find it
      const totalMessages = effectiveChatMessages.length;
      const messagesFromEnd = totalMessages - messageIndex;

      if (messagesFromEnd > displayedMessageCount) {
        needsToLoadMore = true;
        // Load enough messages to include the target message
        setDisplayedMessageCount(messagesFromEnd + 10); // Add 10 for context
      }
    }

    // Wait for render, then scroll to the message
    const scrollDelay = needsToLoadMore ? 300 : 100;
    setTimeout(() => {
      const messageElement = document.querySelector(
        `[data-message-id="${messageId}"]`,
      );

      if (messageElement) {
        // Restore keyboard BEFORE scrolling to prevent blur
        if (
          wasKeyboardOpen &&
          savedInputRef &&
          document.activeElement !== savedInputRef
        ) {
          // Don't restore keyboard if activity is reported
          if (!activity?.isReported) {
            savedInputRef.focus();
          }
        }

        // Scroll without losing focus
        messageElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Add highlight effect
        messageElement.classList.add("highlight-message");
        setTimeout(() => {
          messageElement.classList.remove("highlight-message");
        }, 2000);

        // Restore keyboard again after scroll starts to ensure it stays open
        if (wasKeyboardOpen && savedInputRef) {
          setTimeout(() => {
            if (savedInputRef && document.activeElement !== savedInputRef) {
              // Don't restore keyboard if activity is reported
              if (!activity?.isReported) {
                savedInputRef.focus();
              }
            }
          }, 50);
        }
      } else {
        // Restore keyboard even if scroll failed
        restoreKeyboard();
      }
    }, scrollDelay);
  };

  const handlePickMedia = () => {
    const logPrefix = "[ChatComposer:+]";
    console.log(`${logPrefix} tapped`);
    void crashlytics.log(`${logPrefix} tapped`);

    if (selectedMediaRef.current.length >= CHAT_MEDIA_MAX_FILES) {
      addToast(`Maximum ${CHAT_MEDIA_MAX_FILES} attachments allowed.`, "error");
      return;
    }

    setShowAttachmentSheet(true);
  };

  const handleMediaSelection = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      console.log("[ChatComposer:+] picker returned 0 files");
      void crashlytics.log("[ChatComposer:+] picker returned 0 files");
      return;
    }

    console.log(`[ChatComposer:+] selected ${files.length} file(s)`);
    void crashlytics.log(`[ChatComposer:+] selected ${files.length} file(s)`);

    appendSelectedFiles(files);

    e.target.accept = CHAT_MEDIA_ACCEPT_ALL;
    e.target.multiple = true;
    e.target.value = "";
  };

  const handleRemoveSelectedMedia = (id: string) => {
    setSelectedMedia((prev) => {
      const itemToRemove = prev.find((item) => item.id === id);
      if (itemToRemove) {
        URL.revokeObjectURL(itemToRemove.previewUrl);
      }
      return prev.filter((item) => item.id !== id);
    });
  };

  const canShowVoiceNoteButton =
    !editingMessage &&
    !isRecordingVoiceNote &&
    !inputValue.trim() &&
    selectedMedia.length === 0;

  const requestCurrentChatPosition = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      const permissionStatus = await Geolocation.checkPermissions();
      if (
        permissionStatus.location !== "granted" &&
        permissionStatus.coarseLocation !== "granted"
      ) {
        const requestStatus = await Geolocation.requestPermissions({
          permissions: ["location", "coarseLocation"],
        });
        if (
          requestStatus.location !== "granted" &&
          requestStatus.coarseLocation !== "granted"
        ) {
          throw new Error("Location permission denied");
        }
      }
    }

    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 5000,
    });

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracyMeters: position.coords.accuracy,
    };
  }, []);

  const handleStopLiveLocationShare = useCallback(async (shareId: string) => {
    if (!chatId || !currentUser || !db) return;

    try {
      if (getActiveChatLiveLocationShareId() === shareId) {
        await stopChatLiveLocationShare({
          chatId,
          shareId,
          userId: currentUser.id,
          markStopped: true,
        });
      } else {
        await updateDoc(
          firestoreDoc(db, "chats", chatId, "liveLocations", shareId),
          {
            isActive: false,
            endedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        );
      }

      setLiveLocationSharesById((prev) => {
        const next = { ...prev };
        delete next[shareId];
        return next;
      });
      liveLocationStartInFlightRef.current.delete(shareId);
      if (liveLocationWatcherShareIdRef.current === shareId) {
        liveLocationWatcherShareIdRef.current = null;
      }
      addToast("Live location stopped.", "success");
    } catch (error) {
      crashlytics.logError(
        `Failed to stop live location share: ${JSON.stringify((error as Error)?.message || error)}`,
        "ActivityChatPage.tsx",
        error as Error,
      );
      addToast("Failed to stop live location.", "error");
    }
  }, [chatId, currentUser, db]);

  const shareLocationMessage = useCallback(
    async (option: LiveLocationDurationOption) => {
      if (!chatId || !currentUser) return;

      setIsPreparingLocationShare(true);
      setShowLocationShareSheet(false);

      const sharedAt = new Date().toISOString();
      const localMessageId = `local-location-${Date.now()}-${crypto.randomUUID()}`;
      const clientRequestId = `req-location-${Date.now()}-${crypto.randomUUID()}`;
      let createdLiveShareId: string | null = null;
      let locationFeedbackShown = false;

      try {
        const coords = await requestCurrentChatPosition();

        const existingOwnShare = Object.values(liveLocationSharesById).find(
          (share) =>
            share.senderId === currentUser.id &&
            share.isActive !== false &&
            (!share.expiresAt || new Date(share.expiresAt).getTime() > Date.now()),
        );

        if (existingOwnShare) {
          await handleStopLiveLocationShare(existingOwnShare.id);
        }

        const expiresAt =
          option.kind === "live" && option.durationMs
            ? new Date(Date.now() + option.durationMs).toISOString()
            : undefined;
        const liveShareId = option.kind === "live" ? `live-${crypto.randomUUID()}` : undefined;
        createdLiveShareId = liveShareId || null;
        if (liveShareId) {
          liveLocationStartInFlightRef.current.add(liveShareId);
        }

        const locationPayload: ChatLocationPayload = {
          kind: option.kind,
          latitude: coords.latitude,
          longitude: coords.longitude,
          label: option.kind === "live" ? "Live location" : "Current location",
          accuracyMeters: coords.accuracyMeters,
          sharedAt,
          ...(liveShareId ? { liveShareId } : {}),
          ...(expiresAt ? { expiresAt } : {}),
        };

        const replySnapshot = replyingTo;

        const optimisticMessage: ChatMessage = {
          id: localMessageId,
          clientRequestId,
          senderId: currentUser.id,
          text: "",
          format: "whatsapp",
          timestamp: sharedAt,
          status: "sending",
          readBy: [currentUser.id],
          deliveredTo: [currentUser.id],
          reactions: [],
          location: locationPayload,
          ...(replySnapshot
            ? {
                replyTo: {
                  messageId: replySnapshot.message.id,
                  senderId: replySnapshot.sender.id,
                  senderUsername: replySnapshot.sender.username,
                  text:
                    replySnapshot.message.text ||
                    (replySnapshot.message.location
                      ? "Shared location"
                      : (replySnapshot.message.media?.length || 0) > 0
                        ? "Media"
                        : "Message"),
                },
              }
            : {}),
        };

        setOptimisticMessages((prev) => [...prev, optimisticMessage]);

        let liveShareSetupPromise: Promise<void> | null = null;
        if (liveShareId && db) {
          const liveShareRecord: ChatLiveLocationShareRecord = {
            id: liveShareId,
            chatId,
            senderId: currentUser.id,
            latitude: coords.latitude,
            longitude: coords.longitude,
            label: "Live location",
            accuracyMeters: coords.accuracyMeters,
            updatedAt: sharedAt,
            expiresAt,
            isActive: true,
          };

          setLiveLocationSharesById((prev) => ({
            ...prev,
            [liveShareId]: liveShareRecord,
          }));

          liveShareSetupPromise = (async () => {
            await setDoc(
              firestoreDoc(db, "chats", chatId, "liveLocations", liveShareId),
              {
                chatId,
                senderId: currentUser.id,
                latitude: coords.latitude,
                longitude: coords.longitude,
                accuracyMeters: coords.accuracyMeters,
                label: "Live location",
                expiresAt,
                startedAt: sharedAt,
                updatedAt: sharedAt,
                isActive: true,
              },
              { merge: true },
            );

            if (expiresAt) {
              await startChatLiveLocationShare({
                chatId,
                shareId: liveShareId,
                userId: currentUser.id,
                expiresAt,
                label: "Live location",
              });
              liveLocationWatcherShareIdRef.current = liveShareId;
            }
          })();
        }

        setReplyingTo(null);
        addToast(
          option.kind === "live"
            ? `Sharing live location for ${option.label.replace("Share live for ", "")}.`
            : "Sharing location.",
          "success",
        );
        locationFeedbackShown = true;

        await Promise.all([
          sendMessage(
            chatId,
            "",
            replySnapshot
              ? {
                  messageId: replySnapshot.message.id,
                  senderId: replySnapshot.sender.id,
                  senderUsername: replySnapshot.sender.username,
                  text:
                    replySnapshot.message.text ||
                    (replySnapshot.message.location
                      ? "Shared location"
                      : (replySnapshot.message.media?.length || 0) > 0
                        ? "Media"
                        : "Message"),
                }
              : undefined,
            {
              format: "whatsapp",
              clientRequestId,
              location: locationPayload,
            },
          ),
          liveShareSetupPromise,
        ]);

        if (liveShareId) {
          liveLocationStartInFlightRef.current.delete(liveShareId);
        }

        setOptimisticMessages((prev) => prev.filter((msg) => msg.id !== localMessageId));
      } catch (error) {
        setOptimisticMessages((prev) => prev.filter((msg) => msg.id !== localMessageId));

        if (createdLiveShareId && db) {
          try {
            await updateDoc(
              firestoreDoc(db, "chats", chatId, "liveLocations", createdLiveShareId),
              {
                isActive: false,
                endedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              },
            );
          } catch {
            // Ignore cleanup failures for optimistic live share records.
          }
        }
        if (createdLiveShareId) {
          liveLocationStartInFlightRef.current.delete(createdLiveShareId);
          if (liveLocationWatcherShareIdRef.current === createdLiveShareId) {
            liveLocationWatcherShareIdRef.current = null;
          }
        }

        crashlytics.logError(
          `Failed to share location: ${JSON.stringify((error as Error)?.message || error)}`,
          "ActivityChatPage.tsx",
          error as Error,
        );
        if (locationFeedbackShown) {
          addToast("Location sharing failed. Please try again.", "error");
        } else {
          addToast("Failed to share location.", "error");
        }
      } finally {
        setIsPreparingLocationShare(false);
      }
    },
    [
      chatId,
      currentUser,
      db,
      handleStopLiveLocationShare,
      liveLocationSharesById,
      replyingTo,
      requestCurrentChatPosition,
      sendMessage,
    ],
  );

  useEffect(() => {
    if (!db || !chatId) {
      setLiveLocationSharesById({});
      return;
    }

    const unsubscribe = onSnapshot(
      query(collection(db, "chats", chatId, "liveLocations"), where("isActive", "==", true)),
      (snapshot) => {
        const nextShares: Record<string, ChatLiveLocationShareRecord> = {};
        snapshot.forEach((shareDoc) => {
          const data = shareDoc.data() as Record<string, unknown>;
          nextShares[shareDoc.id] = {
            id: shareDoc.id,
            chatId,
            senderId: String(data.senderId || ""),
            latitude: Number(data.latitude || 0),
            longitude: Number(data.longitude || 0),
            label: typeof data.label === "string" ? data.label : undefined,
            placeName: typeof data.placeName === "string" ? data.placeName : undefined,
            accuracyMeters:
              typeof data.accuracyMeters === "number"
                ? data.accuracyMeters
                : undefined,
            updatedAt:
              typeof data.updatedAt === "string"
                ? data.updatedAt
                : undefined,
            expiresAt:
              typeof data.expiresAt === "string"
                ? data.expiresAt
                : undefined,
            isActive: data.isActive !== false,
          };
        });
        setLiveLocationSharesById(nextShares);

        const ownActiveShare = Object.values(nextShares).find(
          (share) =>
            share.senderId === currentUser?.id &&
            share.isActive !== false &&
            (!share.expiresAt || new Date(share.expiresAt).getTime() > Date.now()),
        );
      },
    );

    return () => unsubscribe();
  }, [chatId, currentUser?.id]);

  useEffect(() => {
    if (!chatId || !currentUser?.id) return;

    const ownActiveShare = Object.values(liveLocationSharesById).find(
      (share) =>
        share.senderId === currentUser.id &&
        share.isActive !== false &&
        (!share.expiresAt || new Date(share.expiresAt).getTime() > Date.now()),
    );

    if (!ownActiveShare) return;
    if (liveLocationStartInFlightRef.current.has(ownActiveShare.id)) return;
    if (getActiveChatLiveLocationShareId() === ownActiveShare.id) return;
    if (liveLocationWatcherShareIdRef.current === ownActiveShare.id) return;

    liveLocationStartInFlightRef.current.add(ownActiveShare.id);
    void startChatLiveLocationShare({
      chatId,
      shareId: ownActiveShare.id,
      userId: currentUser.id,
      expiresAt: ownActiveShare.expiresAt || new Date(Date.now() + 5 * 60 * 1000).toISOString(),
      label: ownActiveShare.label,
      placeName: ownActiveShare.placeName,
    }).catch((error) => {
      liveLocationStartInFlightRef.current.delete(ownActiveShare.id);
      crashlytics.logError(
        `Failed to resume live location sharing: ${JSON.stringify((error as Error)?.message || error)}`,
        "ActivityChatPage.tsx",
        error as Error,
      );
    }).then(() => {
      liveLocationWatcherShareIdRef.current = ownActiveShare.id;
      liveLocationStartInFlightRef.current.delete(ownActiveShare.id);
    });
  }, [chatId, currentUser?.id, liveLocationSharesById]);

  const clearTypingStatus = useCallback(() => {
    if (!db || !currentUser?.id || !chat?.id) return;
    setDoc(
      firestoreDoc(db, "chatMeta", chat.id),
      {
        typing: {
          [currentUser.id]: null,
        },
        updatedAt: new Date().toISOString(),
      },
      { merge: true },
    ).catch(() => {});
  }, [chat?.id, currentUser?.id]);

  const handleTypingInput = useCallback(
    (value: string) => {
      setInputValue(value);
      if (!db || !currentUser?.id || !chat?.id) return;
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      setDoc(
        firestoreDoc(db, "chatMeta", chat.id),
        {
          typing: {
            [currentUser.id]: new Date().toISOString(),
          },
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      ).catch(() => {});
      typingTimeoutRef.current = setTimeout(() => {
        clearTypingStatus();
      }, 3000);
    },
    [chat?.id, currentUser?.id, clearTypingStatus],
  );

  const updateComposerSelection = useCallback(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;
    const start = inputEl.selectionStart || 0;
    const end = inputEl.selectionEnd || 0;
    setComposerSelection({
      start,
      end,
    });
    lastComposerSelectionRef.current = { start, end };
  }, []);

  useEffect(() => {
    const handleSelectionChange = () => {
      const inputEl = inputRef.current;
      if (!inputEl) return;

      // Native context-menu flows can temporarily blur the textarea on mobile.
      // Keep tracking selection as long as the composer still has a selected range.
      const hasSelection = (inputEl.selectionEnd || 0) > (inputEl.selectionStart || 0);
      if (!hasSelection && document.activeElement !== inputEl) return;

      updateComposerSelection();
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [updateComposerSelection]);

  useEffect(() => {
    if (!isComposerFocused) {
      if (composerSelectionPollRef.current) {
        clearInterval(composerSelectionPollRef.current);
        composerSelectionPollRef.current = null;
      }
      return;
    }

    // Android WebView sometimes misses select/selectionchange callbacks.
    // Poll briefly while focused so selected text still triggers the floating menu.
    composerSelectionPollRef.current = setInterval(() => {
      const inputEl = inputRef.current;
      if (!inputEl) return;

      const start = inputEl.selectionStart || 0;
      const end = inputEl.selectionEnd || 0;
      const previous = lastComposerSelectionRef.current;

      if (start !== previous.start || end !== previous.end) {
        updateComposerSelection();
      }
    }, 120);

    return () => {
      if (composerSelectionPollRef.current) {
        clearInterval(composerSelectionPollRef.current);
        composerSelectionPollRef.current = null;
      }
    };
  }, [isComposerFocused, updateComposerSelection]);

  const applyInlineFormat = useCallback(
    (prefix: string, suffix = prefix) => {
      const inputEl = inputRef.current;
      if (!inputEl) return;

      const start = inputEl.selectionStart || 0;
      const end = inputEl.selectionEnd || 0;
      const selectedText = inputValue.slice(start, end);
      const replacement = `${prefix}${selectedText}${suffix}`;
      const nextValue = inputValue.slice(0, start) + replacement + inputValue.slice(end);
      const nextSelectionStart = selectedText.length === 0 ? start + prefix.length : start;
      const nextSelectionEnd = selectedText.length === 0
        ? start + prefix.length
        : start + replacement.length;

      handleTypingInput(nextValue);
      requestAnimationFrame(() => {
        inputEl.focus();
        inputEl.setSelectionRange(nextSelectionStart, nextSelectionEnd);
        setComposerSelection({ start: nextSelectionStart, end: nextSelectionEnd });
        updateComposerSelection();
      });
    },
    [inputValue, handleTypingInput, updateComposerSelection],
  );

  const applyLinePrefixFormat = useCallback(
    (prefix: string) => {
      const inputEl = inputRef.current;
      if (!inputEl) return;

      const start = inputEl.selectionStart || 0;
      const end = inputEl.selectionEnd || 0;
      const lineStart = inputValue.lastIndexOf("\n", Math.max(0, start - 1)) + 1;
      const lineEndRaw = inputValue.indexOf("\n", end);
      const lineEnd = lineEndRaw === -1 ? inputValue.length : lineEndRaw;
      const block = inputValue.slice(lineStart, lineEnd);
      const nextBlock = block
        .split("\n")
        .map((line) => (line.startsWith(prefix) ? line.slice(prefix.length) : `${prefix}${line}`))
        .join("\n");

      const nextValue = inputValue.slice(0, lineStart) + nextBlock + inputValue.slice(lineEnd);
      const nextSelectionStart = lineStart;
      const nextSelectionEnd = lineStart + nextBlock.length;

      handleTypingInput(nextValue);
      requestAnimationFrame(() => {
        inputEl.focus();
        inputEl.setSelectionRange(nextSelectionStart, nextSelectionEnd);
        setComposerSelection({ start: nextSelectionStart, end: nextSelectionEnd });
        updateComposerSelection();
      });
    },
    [inputValue, handleTypingInput, updateComposerSelection],
  );

  const handleComposerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const modifierPressed = e.metaKey || e.ctrlKey;
      if (!modifierPressed) return;

      const key = e.key.toLowerCase();

      if (key === "b" && !e.shiftKey) {
        e.preventDefault();
        applyInlineFormat("*");
      } else if (key === "i" && !e.shiftKey) {
        e.preventDefault();
        applyInlineFormat("_");
      } else if (key === "x" && e.shiftKey) {
        e.preventDefault();
        applyInlineFormat("~");
      } else if (key === "e" && !e.shiftKey) {
        e.preventDefault();
        applyInlineFormat("`");
      }
    },
    [applyInlineFormat],
  );

  const hasComposerSelection = composerSelection.end > composerSelection.start;

  useEffect(() => {
    if (!isComposerFocused || !hasComposerSelection) {
      setShowComposerFormatOverflow(false);
    }
  }, [isComposerFocused, hasComposerSelection]);

  useEffect(() => {
    if (!db || !chatId) {
      setTypingByUserId({});
      setMessageReceiptsById({});
      setMessageStarsById({});
      return;
    }

    const unsub = onSnapshot(
      firestoreDoc(db, "chatMeta", chatId),
      (docSnap) => {
        const data = docSnap.data();
        const typing = data?.typing;
        const receipts = data?.receipts;
        const stars = data?.stars;

        if (receipts && typeof receipts === "object") {
          setMessageReceiptsById(receipts as Record<string, ChatRuntimeReceipt>);
        } else {
          setMessageReceiptsById({});
        }

        if (stars && typeof stars === "object") {
          setMessageStarsById(stars as ChatRuntimeStarsByMessage);
        } else {
          setMessageStarsById({});
        }

        if (!typing || typeof typing !== "object") {
          setTypingByUserId({});
          return;
        }

        const normalized = Object.fromEntries(
          Object.entries(typing).filter(
            ([, ts]) => typeof ts === "string" && ts.length > 0,
          ),
        ) as Record<string, string>;

        setTypingByUserId(normalized);
      },
      () => {
        setTypingByUserId({});
        setMessageReceiptsById({});
        setMessageStarsById({});
      },
    );

    return () => {
      unsub();
    };
  }, [chatId]);

  useEffect(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;

    inputEl.style.height = "0px";
    const nextHeight = Math.min(inputEl.scrollHeight, CHAT_COMPOSER_MAX_HEIGHT_PX);
    inputEl.style.height = `${Math.max(CHAT_COMPOSER_TEXT_SIZE_PX, nextHeight)}px`;
    inputEl.style.overflowY = inputEl.scrollHeight > CHAT_COMPOSER_MAX_HEIGHT_PX ? "auto" : "hidden";
  }, [inputValue, replyingTo, editingMessage]);

  useEffect(() => {
    return () => {
      if (composerBlurTimeoutRef.current) {
        clearTimeout(composerBlurTimeoutRef.current);
      }
    };
  }, []);

  // Clear typing indicator on unmount
  useEffect(() => {
    return () => {
      clearTypingStatus();
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      stopVoiceNoteRecording(true);
    };
  }, [clearTypingStatus, stopVoiceNoteRecording]);

  // Auto-scroll to first search match when query changes
  useEffect(() => {
    if (searchActive && searchQuery.trim()) {
      setSearchMatchIndex(0);
    }
  }, [searchQuery, searchActive]);

  const uploadChatMedia = async (
    chatIdValue: string,
    mediaItems: ChatComposerMediaItem[],
  ): Promise<ChatMediaAttachment[]> => {
    if (!currentUser) {
      throw new Error("User not authenticated");
    }
    if (!storage) {
      throw new Error("Storage is not initialized");
    }

    const uploads = mediaItems.map(async (mediaItem) => {
      const uploadLogPrefix = `[ChatComposer:upload:${mediaItem.id}]`;
      const timestamp = Date.now();
      const safeName = mediaItem.file.name.replace(/[^a-zA-Z0-9_.-]/g, "_");
      const path = `chat-media/${chatIdValue}/${currentUser.id}/${timestamp}-${mediaItem.id}-${safeName}`;
      const fileRef = storageRef(storage, path);

      console.log(
        `${uploadLogPrefix} start name=${mediaItem.file.name} size=${mediaItem.file.size} type=${mediaItem.file.type}`,
      );
      void crashlytics.log(
        `${uploadLogPrefix} start size=${mediaItem.file.size} type=${mediaItem.file.type}`,
      );

      const metadata = {
        contentType: mediaItem.file.type,
        customMetadata: {
          uploadedBy: currentUser.id,
          chatId: chatIdValue,
          localMediaId: mediaItem.id,
        },
      };

      const uploadTask = uploadBytesResumable(fileRef, mediaItem.file, metadata);

      let lastProgress = 0;
      let lastState = "running";
      let lastErrorCode = "";
      let lastErrorMessage = "";
      const uploadStartedAt = Date.now();

      try {
        await withTimeout(
          new Promise<void>((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                lastState = snapshot.state;
                const progress = snapshot.totalBytes
                  ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
                  : 0;
                lastProgress = progress;
                if (progress === 0 || progress === 100 || progress % 25 === 0) {
                  console.log(`${uploadLogPrefix} progress=${progress}%`);
                }
              },
              (error) => {
                if (error && typeof error === "object" && "code" in error) {
                  lastErrorCode = String((error as { code?: unknown }).code || "");
                }
                if (error && typeof error === "object" && "message" in error) {
                  lastErrorMessage = String((error as { message?: unknown }).message || "");
                }
                reject(error);
              },
              () => resolve(),
            );
          }),
          CHAT_UPLOAD_TIMEOUT_MS,
          `Upload timed out for ${mediaItem.file.name}`,
        );
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const elapsedMs = Date.now() - uploadStartedAt;
        const isTimeout = errorMessage.includes("Upload timed out for");

        if (isTimeout) {
          const cancelled = uploadTask.cancel();
          const timeoutDiagnostic = `${uploadLogPrefix} timeout elapsedMs=${elapsedMs} progress=${lastProgress}% state=${lastState} online=${typeof navigator !== "undefined" ? String(navigator.onLine) : "unknown"} cancelled=${cancelled} code=${lastErrorCode || "none"} message=${lastErrorMessage || "none"}`;
          console.error(timeoutDiagnostic);
          void crashlytics.log(timeoutDiagnostic);

          throw new Error(
            `Upload timed out for ${mediaItem.file.name} (progress=${lastProgress}%, state=${lastState}, code=${lastErrorCode || "none"})`,
          );
        }

        const uploadDiagnostic = `${uploadLogPrefix} failed elapsedMs=${elapsedMs} progress=${lastProgress}% state=${lastState} code=${lastErrorCode || "none"} message=${lastErrorMessage || "none"}`;
        console.error(uploadDiagnostic, error);
        void crashlytics.log(uploadDiagnostic);
        throw error;
      }

      console.log(`${uploadLogPrefix} upload complete`);
      void crashlytics.log(`${uploadLogPrefix} upload complete`);

      const url = await withTimeout(
        getDownloadURL(fileRef),
        CHAT_SEND_TIMEOUT_MS,
        `Download URL timed out for ${mediaItem.file.name}`,
      );

      console.log(`${uploadLogPrefix} download url ready`);
      void crashlytics.log(`${uploadLogPrefix} download url ready`);

      return {
        id: mediaItem.id,
        type: mediaItem.type,
        url,
        mimeType: mediaItem.file.type,
        fileName: mediaItem.file.name,
        size: mediaItem.file.size,
      } as ChatMediaAttachment;
    });

    return Promise.all(uploads);
  };

  const queueBackgroundSend = useCallback(
    (localMessageId: string, payload: PendingChatSendPayload) => {
      if (!chatId) return;

      if (payload.mediaSnapshot.length > 0) {
        void persistOutboxMediaSnapshot(chatId, payload.clientRequestId, payload.mediaSnapshot);
      }

      upsertChatOutboxRecord({
        chatId,
        clientRequestId: payload.clientRequestId,
        text: payload.inputSnapshot,
        createdAt: new Date().toISOString(),
        status: "queued",
        format: "whatsapp",
        replyTo: payload.replySnapshot
          ? {
              messageId: payload.replySnapshot.message.id,
              senderId: payload.replySnapshot.sender.id,
              senderUsername: payload.replySnapshot.sender.username,
              text: payload.replySnapshot.message.text || "Message",
            }
          : undefined,
        hasMedia: payload.mediaSnapshot.length > 0,
        attempts: 0,
        nextAttemptAt: new Date().toISOString(),
      });

      const existingSlowTimer = slowSendTimersRef.current.get(localMessageId);
      if (existingSlowTimer) {
        clearTimeout(existingSlowTimer);
      }
      setSlowSendingMessageIds((prev) => {
        if (!prev.has(localMessageId)) return prev;
        const next = new Set(prev);
        next.delete(localMessageId);
        return next;
      });
      const slowTimer = setTimeout(() => {
        setSlowSendingMessageIds((prev) => new Set(prev).add(localMessageId));
      }, CHAT_SLOW_SEND_THRESHOLD_MS);
      slowSendTimersRef.current.set(localMessageId, slowTimer);

      pendingSendsRef.current.set(localMessageId, payload);
      setActiveBackgroundSends((prev) => prev + 1);

      void (async () => {
        try {
          let livePayload = pendingSendsRef.current.get(localMessageId) || payload;
          if (livePayload.cancelled) {
            throw new Error("send_cancelled");
          }

          const { mediaSnapshot } = livePayload;

          const mediaPayload =
            mediaSnapshot.length > 0
              ? await uploadChatMedia(chatId, mediaSnapshot)
              : [];

          console.log(`[ChatComposer:send] media payload ready count=${mediaPayload.length}`);
          void crashlytics.log(
            `[ChatComposer:send] media payload ready count=${mediaPayload.length}`,
          );

          livePayload = pendingSendsRef.current.get(localMessageId) || livePayload;
          if (!livePayload || livePayload.cancelled) {
            throw new Error("send_cancelled");
          }

          const {
            clientRequestId,
            inputSnapshot,
            replySnapshot,
          } = livePayload;

          const updatedReplyToData = replySnapshot
            ? {
                messageId: replySnapshot.message.id,
                senderId: replySnapshot.sender.id,
                senderUsername: replySnapshot.sender.username,
                text:
                  (replySnapshot.message.text || "")
                    ? (replySnapshot.message.text || "").length > 100
                      ? replySnapshot.message.text.substring(0, 100) + "..."
                      : replySnapshot.message.text
                    : (replySnapshot.message.media?.length || 0) > 0
                      ? `📎 ${replySnapshot.message.media?.length} attachment${(replySnapshot.message.media?.length || 0) > 1 ? "s" : ""}`
                      : "Message",
              }
            : undefined;

          await sendMessage(chatId, inputSnapshot, updatedReplyToData, {
            format: "whatsapp",
            media: mediaPayload,
            clientRequestId,
          });

          console.log("[ChatComposer:send] sendMessage success");
          void crashlytics.log("[ChatComposer:send] sendMessage success");

          setOptimisticMessages((prev) => prev.filter((msg) => msg.id !== localMessageId));
          pendingSendsRef.current.delete(localMessageId);
          mediaSnapshot.forEach((item) => URL.revokeObjectURL(item.previewUrl));
          removeChatOutboxRecord(clientRequestId);
          void deleteOutboxMediaRecord(clientRequestId);
          writeChatDraft(chatId, "");
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error || "");
          if (errorMessage === "send_cancelled") {
            const cancelledPayload =
              pendingSendsRef.current.get(localMessageId) || payload;
            cancelledPayload.mediaSnapshot.forEach((item) =>
              URL.revokeObjectURL(item.previewUrl),
            );
            removeChatOutboxRecord(cancelledPayload.clientRequestId);
            void deleteOutboxMediaRecord(cancelledPayload.clientRequestId);
            pendingSendsRef.current.delete(localMessageId);
            setOptimisticMessages((prev) =>
              prev.filter((msg) => msg.id !== localMessageId),
            );
            return;
          }

          const livePayload = pendingSendsRef.current.get(localMessageId) || payload;
          upsertChatOutboxRecord({
            chatId,
            clientRequestId: livePayload.clientRequestId,
            text: livePayload.inputSnapshot,
            createdAt: new Date().toISOString(),
            status: "failed",
            format: "whatsapp",
            replyTo: livePayload.replySnapshot
              ? {
                  messageId: livePayload.replySnapshot.message.id,
                  senderId: livePayload.replySnapshot.sender.id,
                  senderUsername: livePayload.replySnapshot.sender.username,
                  text: livePayload.replySnapshot.message.text || "Message",
                }
              : undefined,
            hasMedia: livePayload.mediaSnapshot.length > 0,
          });

          updateChatOutboxRecord(livePayload.clientRequestId, (record) => {
            const nextAttempts = (record.attempts || 0) + 1;
            const retryDelay = Math.min(
              CHAT_OUTBOX_RETRY_BASE_DELAY_MS * Math.pow(2, Math.min(nextAttempts, 6)),
              CHAT_OUTBOX_RETRY_MAX_DELAY_MS,
            );
            return {
              ...record,
              status: "failed",
              attempts: nextAttempts,
              nextAttemptAt: new Date(Date.now() + retryDelay).toISOString(),
            };
          });

          console.error("[ChatComposer:send] failed", error);
          void crashlytics.log(
            `[ChatComposer:send] failed ${JSON.stringify(error?.message || error)}`,
          );
          crashlytics.logError(
            `Error sending message: ${JSON.stringify(error?.message)}`,
            "ActivityChatPage.tsx",
            error,
          );

          setOptimisticMessages((prev) =>
            prev.map((msg) =>
              msg.id === localMessageId
                ? { ...msg, status: "failed" }
                : msg,
            ),
          );
          addToast("Failed to send message. Tap status icon to retry.", "error");
        } finally {
          const timer = slowSendTimersRef.current.get(localMessageId);
          if (timer) {
            clearTimeout(timer);
            slowSendTimersRef.current.delete(localMessageId);
          }
          setSlowSendingMessageIds((prev) => {
            if (!prev.has(localMessageId)) return prev;
            const next = new Set(prev);
            next.delete(localMessageId);
            return next;
          });
          setActiveBackgroundSends((prev) => Math.max(0, prev - 1));
        }
      })();
    },
    [addToast, chatId, sendMessage],
  );

  const processPersistedOutbox = useCallback(async () => {
    if (!chatId || outboxProcessingRef.current) return;
    outboxProcessingRef.current = true;

    try {
      while (true) {
        let records = readChatOutbox().filter((record) => record.chatId === chatId);

        if (records.length === 0) {
          if (outboxWakeTimeoutRef.current) {
            clearTimeout(outboxWakeTimeoutRef.current);
            outboxWakeTimeoutRef.current = null;
          }
          break;
        }

        const confirmedClientRequestIds = new Set(
          (chat?.messages || [])
            .map((message) => message.clientRequestId)
            .filter((id): id is string => Boolean(id)),
        );

        const resolvedIds = records
          .filter((record) => confirmedClientRequestIds.has(record.clientRequestId))
          .map((record) => record.clientRequestId);
        if (resolvedIds.length > 0) {
          resolvedIds.forEach((id) => removeChatOutboxRecord(id));
          records = readChatOutbox().filter((record) => record.chatId === chatId);
        }

        if (records.length === 0) {
          break;
        }

        const now = Date.now();
        const due = records
          .filter((record) => {
            const nextAttemptAt = record.nextAttemptAt
              ? new Date(record.nextAttemptAt).getTime()
              : 0;
            return Number.isNaN(nextAttemptAt) || nextAttemptAt <= now;
          })
          .sort((a, b) => {
            const aAt = a.nextAttemptAt ? new Date(a.nextAttemptAt).getTime() : 0;
            const bAt = b.nextAttemptAt ? new Date(b.nextAttemptAt).getTime() : 0;
            return aAt - bAt;
          });

        if (due.length === 0) {
          const future = records
            .filter((record) => record.nextAttemptAt)
            .map((record) => new Date(record.nextAttemptAt as string).getTime())
            .filter((ts) => !Number.isNaN(ts));

          if (future.length > 0) {
            const delay = Math.max(300, Math.min(...future) - now);
            if (outboxWakeTimeoutRef.current) {
              clearTimeout(outboxWakeTimeoutRef.current);
            }
            outboxWakeTimeoutRef.current = setTimeout(() => {
              void processPersistedOutbox();
            }, delay);
          }
          break;
        }

        for (const record of due) {
          let hydratedMedia: ChatComposerMediaItem[] = [];
          try {
            let mediaPayload: ChatMediaAttachment[] | undefined;

            if (record.hasMedia) {
              hydratedMedia = await hydrateOutboxMediaSnapshot(record.clientRequestId);
              if (hydratedMedia.length === 0) {
                throw new Error("missing_persisted_media");
              }
              mediaPayload = await uploadChatMedia(chatId, hydratedMedia);
            }

            await sendMessage(chatId, record.text, record.replyTo, {
              format: record.format || "whatsapp",
              clientRequestId: record.clientRequestId,
              media: mediaPayload,
            });

            hydratedMedia.forEach((item) => URL.revokeObjectURL(item.previewUrl));
            removeChatOutboxRecord(record.clientRequestId);
            void deleteOutboxMediaRecord(record.clientRequestId);
          } catch {
            hydratedMedia.forEach((item) => URL.revokeObjectURL(item.previewUrl));

            updateChatOutboxRecord(record.clientRequestId, (prev) => {
              const nextAttempts = (prev.attempts || 0) + 1;
              const retryDelay = Math.min(
                CHAT_OUTBOX_RETRY_BASE_DELAY_MS * Math.pow(2, Math.min(nextAttempts, 6)),
                CHAT_OUTBOX_RETRY_MAX_DELAY_MS,
              );
              return {
                ...prev,
                status: "failed",
                attempts: nextAttempts,
                nextAttemptAt: new Date(Date.now() + retryDelay).toISOString(),
              };
            });
          }
        }
      }
    } finally {
      outboxProcessingRef.current = false;
    }
  }, [chatId, chat?.messages, sendMessage]);

  useEffect(() => {
    if (!chatId) return;
    void processPersistedOutbox();

    const handleOnline = () => {
      void processPersistedOutbox();
    };
    const handleVisibility = () => {
      if (!document.hidden) {
        void processPersistedOutbox();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (outboxWakeTimeoutRef.current) {
        clearTimeout(outboxWakeTimeoutRef.current);
        outboxWakeTimeoutRef.current = null;
      }
    };
  }, [chatId, processPersistedOutbox]);

  const handleRetryFailedMessage = useCallback(
    (message: ChatMessage) => {
      if (message.status !== "failed") return;
      const payload = pendingSendsRef.current.get(message.id);
      if (!payload) {
        addToast("Retry data unavailable. Please resend manually.", "error");
        return;
      }

      setOptimisticMessages((prev) =>
        prev.map((msg) =>
          msg.id === message.id
            ? { ...msg, status: "sending", timestamp: new Date().toISOString() }
            : msg,
        ),
      );
      queueBackgroundSend(message.id, payload);
    },
    [addToast, queueBackgroundSend],
  );

  const handleRetrySelectedMessage = useCallback(() => {
    if (!primarySelectedMessage) return;
    if (primarySelectedMessage.status !== "failed") return;
    handleRetryFailedMessage(primarySelectedMessage);
    clearMessageSelection();
  }, [primarySelectedMessage, handleRetryFailedMessage, clearMessageSelection]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatId) return;
    if (!currentUser) return;

    if (editingMessage) {
      if (!inputValue.trim()) return;
    } else if (!inputValue.trim() && selectedMedia.length === 0) {
      return;
    }

    const messageFingerprint = JSON.stringify({
      chatId,
      editingId: editingMessage?.id || null,
      text: inputValue.trim(),
      media: selectedMedia.map((item) => ({
        name: item.file.name,
        size: item.file.size,
        type: item.file.type,
      })),
      replyToId: replyingTo?.message.id || null,
    });
    const dedupeWindowMs = 900;
    const now = Date.now();
    if (
      submitDedupeRef.current &&
      submitDedupeRef.current.fingerprint === messageFingerprint &&
      now - submitDedupeRef.current.ts < dedupeWindowMs
    ) {
      return;
    }
    submitDedupeRef.current = { fingerprint: messageFingerprint, ts: now };

    // Prevent sending if activity is reported
    if (!isDirectChat && activity?.isReported) {
      return;
    }

    // Send message without clearing input first to maintain focus
    try {
      console.log(
        `[ChatComposer:send] start editing=${Boolean(editingMessage)} mediaCount=${selectedMedia.length} textLength=${inputValue.trim().length}`,
      );
      void crashlytics.log(
        `[ChatComposer:send] start editing=${Boolean(editingMessage)} mediaCount=${selectedMedia.length}`,
      );

      if (editingMessage) {
        const pendingPayload = pendingSendsRef.current.get(editingMessage.id);
        if (
          pendingPayload &&
          (editingMessage.status === "sending" || editingMessage.status === "failed")
        ) {
          const nextStatus: "queued" | "failed" =
            editingMessage.status === "failed" ? "failed" : "queued";
          pendingSendsRef.current.set(editingMessage.id, {
            ...pendingPayload,
            inputSnapshot: inputValue,
            cancelled: false,
          });
          upsertChatOutboxRecord({
            chatId,
            clientRequestId: pendingPayload.clientRequestId,
            text: inputValue,
            createdAt: new Date().toISOString(),
            status: nextStatus,
            format: "whatsapp",
            replyTo: pendingPayload.replySnapshot
              ? {
                  messageId: pendingPayload.replySnapshot.message.id,
                  senderId: pendingPayload.replySnapshot.sender.id,
                  senderUsername: pendingPayload.replySnapshot.sender.username,
                  text: pendingPayload.replySnapshot.message.text || "Message",
                }
              : undefined,
            hasMedia: pendingPayload.mediaSnapshot.length > 0,
            attempts: nextStatus === "failed" ? 1 : 0,
            nextAttemptAt: new Date().toISOString(),
          });
          setOptimisticMessages((prev) =>
            prev.map((msg) =>
              msg.id === editingMessage.id
                ? {
                    ...msg,
                    text: inputValue,
                    format: "whatsapp",
                    timestamp: new Date().toISOString(),
                  }
                : msg,
            ),
          );
          setEditingMessage(null);
          setInputValue("");
          addToast("Updated pending message.", "success");
          return;
        }

        await withTimeout(
          editMessage(chatId, editingMessage.id, inputValue, "whatsapp"),
          CHAT_SEND_TIMEOUT_MS,
          "Edit message timed out",
        );
        setEditingMessage(null);
        setInputValue("");
        return;
      }

      const inputSnapshot = inputValue;
      const replySnapshot = replyingTo;
      const mediaSnapshot = [...selectedMedia];
      const localMessageId = `local-${Date.now()}-${crypto.randomUUID()}`;
      const clientRequestId = `req-${Date.now()}-${crypto.randomUUID()}`;
      const optimisticMessage: ChatMessage = {
        id: localMessageId,
        clientRequestId,
        senderId: currentUser.id,
        text: inputSnapshot,
        format: "whatsapp",
        timestamp: new Date().toISOString(),
        status: "sending",
        readBy: [currentUser.id],
        deliveredTo: [currentUser.id],
        media: mediaSnapshot.map((item) => ({
          id: item.id,
          type: item.type,
          url: item.previewUrl,
          mimeType: item.file.type,
          fileName: item.file.name,
          size: item.file.size,
        })),
        ...(replySnapshot
          ? {
              replyTo: {
                messageId: replySnapshot.message.id,
                senderId: replySnapshot.sender.id,
                senderUsername: replySnapshot.sender.username,
                text: replySnapshot.message.text || "Message",
              },
            }
          : {}),
      };

      setOptimisticMessages((prev) => [...prev, optimisticMessage]);

      setInputValue("");
      setSelectedMedia([]);
      setReplyingTo(null);
      writeChatDraft(chatId, "");
      clearTypingStatus();
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      scrollToBottom();

      queueBackgroundSend(localMessageId, {
        clientRequestId,
        inputSnapshot,
        replySnapshot,
        mediaSnapshot,
      });

      return;
    } catch (error) {
      console.error("[ChatComposer:send] failed", error);
      void crashlytics.log(
        `[ChatComposer:send] failed ${JSON.stringify(error?.message || error)}`,
      );
      crashlytics.logError(
        `Error sending message: ${JSON.stringify(error?.message)}`,
        "ActivityChatPage.tsx",
        error,
      );
      // Show error toast
      addToast("Failed to send message.", "error");
    } finally {
    }
  };

  const handleBackNavigation = () => {
    if (routeState?.returnToPath) {
      navigate(routeState.returnToPath, {
        state: routeState.returnToState,
      });
      return;
    }

    // Prefer true back behavior so users return to the exact previous tab/page.
    // Fallback keeps behavior deterministic when opened directly (no history).
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/chat", { state: { initialTab: isDirectChat ? "chats" : "groups" } });
  };

  const userMap = useMemo(
    () => new Map(participantUsers.map((u) => [u.id, u])),
    [participantUsers],
  );

  const kickedParticipantIdsSet = useMemo(
    () => new Set(activity?.kickedParticipantIds || []),
    [activity?.kickedParticipantIds],
  );

  const leftParticipantIdsSet = useMemo(
    () => new Set(chat?.leftParticipantIds || []),
    [chat?.leftParticipantIds],
  );

  const messageRows = useMemo(() => {
    return displayedMessages.map((msg, index) => {
      const prevMsg = index > 0 ? displayedMessages[index - 1] : null;
      const sender =
        msg.senderId === currentUser?.id ? currentUser : userMap.get(msg.senderId);
      const repliedToSender = msg.replyTo
        ? msg.replyTo.senderId === currentUser?.id
          ? currentUser
          : userMap.get(msg.replyTo.senderId)
        : null;

      const showSenderName = !prevMsg || prevMsg.senderId !== msg.senderId;
      const showDateSeparator =
        !prevMsg || !isSameDay(prevMsg.timestamp, msg.timestamp);
      const dateSeparatorText = showDateSeparator
        ? formatDateSeparator(msg.timestamp)
        : null;
      const isSelected = reactingToMessage?.id === msg.id;

      const senderStatus: "active" | "left" | "kicked" = kickedParticipantIdsSet.has(
        msg.senderId,
      )
        ? "kicked"
        : leftParticipantIdsSet.has(msg.senderId)
          ? "left"
          : "active";

      return {
        msg,
        sender: sender || null,
        repliedToSender,
        showSenderName,
        showDateSeparator,
        dateSeparatorText,
        isSelected,
        senderStatus,
      };
    });
  }, [
    displayedMessages,
    currentUser,
    userMap,
    reactingToMessage?.id,
    kickedParticipantIdsSet,
    leftParticipantIdsSet,
  ]);

  const contentTopPosition = useMemo(
    () =>
      isNative
        ? `calc(max(env(safe-area-inset-top, 0px), 0px) + ${headerHeight}px)`
        : `calc(max(env(safe-area-inset-top, 0px), 0px) + ${headerHeight}px)`,
    [isNative, headerHeight],
  );

  const contentBottomPosition = useMemo(
    () =>
      isNative
        ? `${footerHeight + (keyboardHeight > 0 ? (isAndroid ? 0 : keyboardHeight) : navHeight)}px`
        : `calc(max(env(safe-area-inset-bottom, 0px), 0px) + ${footerHeight + navHeight}px)`,
    [isNative, footerHeight, keyboardHeight, isAndroid, navHeight],
  );

  const footerBottomPosition = useMemo(
    () =>
      isNative
        ? `${keyboardHeight > 0 ? (isAndroid ? 0 : keyboardHeight) : navHeight}px`
        : `calc(max(env(safe-area-inset-bottom, 0px), 0px) + ${navHeight}px)`,
    [isNative, keyboardHeight, isAndroid, navHeight],
  );

  const otherParticipant = isDirectChat
    ? participantUsers.find((u) => u.id !== currentUser?.id)
    : null;
  const headerTitle = isDirectChat
    ? otherParticipant?.username || "Chat"
    : chat?.activityTitle || "Activity Chat";

  let headerSubtitle: React.ReactNode;
  if (isDirectChat) {
    if (otherParticipant?.isOnline) {
      headerSubtitle = (
        <span className="flex items-center">
          <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
          Online
        </span>
      );
    } else if (otherParticipant?.lastActiveAt) {
      headerSubtitle = `Last seen ${timeAgoPlain(otherParticipant.lastActiveAt)}`;
    } else {
      headerSubtitle = "Offline";
    }
  } else {
    headerSubtitle = `${chat?.participants.length} ${chat?.participants.length === 1 ? "member" : "members"}`;
  }

  const organizerName = activity
    ? activity.creatorId === currentUser?.id
      ? "You"
      : activity.creator?.username
    : "";
  const participantCount = activity?.participants?.length ?? 0;

  // Calculate heights once after initial render
  useEffect(() => {
    setInitializedForScroll(false);

    const updateHeights = () => {
      // Single measurement pass
      const header = headerRef.current;
      const footer = footerRef.current;
      const nav = document.querySelector("footer.z-50") as HTMLElement;

      if (!header || !footer) {
        // Retry once if elements aren't ready
        setTimeout(updateHeights, 100);
        return;
      }

      // Read all heights in one batch
      const measuredHeaderHeight = header.clientHeight;
      const footerHeightValue = footer.clientHeight;
      const navHeightValue = nav ? nav.clientHeight : 0;

      // Add status banner height if present
      let totalHeaderHeight = measuredHeaderHeight;
      if (!isDirectChat && (isActivityDeleted || isActivityExpired)) {
        const statusBanner = document.querySelector(
          ".chat-container .bg-red-50, .chat-container .bg-orange-50",
        ) as HTMLElement;
        if (statusBanner) {
          totalHeaderHeight += statusBanner.clientHeight;
        }
      }

      // Always update on first initialization, then only if changed significantly
      if (!heightsInitialized) {
        setHeaderHeight(totalHeaderHeight);
        setFooterHeight(footerHeightValue);
        setNavHeight(navHeightValue);
        setHeightsInitialized(true);
      } else {
        // Update state only if changed significantly (avoid unnecessary rerenders)
        if (Math.abs(totalHeaderHeight - headerHeight) > 2) {
          setHeaderHeight(totalHeaderHeight);
        }
        if (Math.abs(footerHeightValue - footerHeight) > 2) {
          setFooterHeight(footerHeightValue);
        }
        if (Math.abs(navHeightValue - navHeight) > 2) {
          setNavHeight(navHeightValue);
        }
      }
    };

    // For iOS, use multiple attempts with increasing delays
    const timer = setTimeout(updateHeights, 50);

    // Also observe footer height changes
    let footerResizeObserver = null;
    if (footerRef.current) {
      footerResizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateHeights);
      });
      footerResizeObserver.observe(footerRef.current);
    }

    // Also observer messagesContainerRef height changes (e.g. keyboard open/close)
    const chatContainer = messagesContainerRef.current;
    let messagesContainerResizeObserver = null;
    if (chatContainer) {
      messagesContainerResizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const d1 =
            chatContainer.scrollHeight -
            chatContainer.scrollTop -
            chatContainer.clientHeight;
          const d2 =
            chatContainer.scrollHeight -
            chatContainer.scrollTop -
            entry.target.clientHeight;
          const approach1AtBottom = d1 < 1;
          const approach2AtBottom = d2 < 1;
          if (!approach1AtBottom || !approach2AtBottom) {
            requestAnimationFrame(updateHeights);
          }
        }
      });
      messagesContainerResizeObserver.observe(chatContainer);
    }

    // Mark as initialized for scroll after all measurements
    setInitializedForScroll(true);

    return () => {
      clearTimeout(timer);
      footerResizeObserver && footerResizeObserver.disconnect();
      messagesContainerResizeObserver && messagesContainerResizeObserver.disconnect();
    };
  }, [
    isActivityExpired,
    isActivityDeleted,
    isDirectChat,
    headerHeight,
    footerHeight,
    navHeight,
    heightsInitialized,
    keyboardHeight,
  ]);

  useEffect(() => {
    // Only scroll to bottom after heights are initialized and layout is stable
    if (heightsInitialized && initializedForScroll) {
      //inputRef?.current?.focus();
      // Focus input after scrolling
      scrollToBottom(() => {
        //inputRef?.current?.focus();
      });
      // scroll after a short delay to ensure layout is stable
      let attempts = 0;
      const maxAttempts = 2;
      const tryScroll = (forced?: boolean) => {
        attempts++;
        scrollToBottom(() => {
          const container = messagesContainerRef.current;
          if (!container) return;

          const distanceFromBottom =
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight;
          const isAtBottom = distanceFromBottom < 1;

          if ((forced || !isAtBottom) && attempts < maxAttempts) {
            // Retry after a short delay
            setTimeout(tryScroll, 100);
          }
        });
      };
      tryScroll();

      const container = messagesContainerRef.current;
      if (!container) return;
      // 1. Create the observer
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          // entry.contentRect or entry.target.clientHeight
          // will give you the most up-to-date dimensions
          const d1 =
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight;
          const d2 =
            container.scrollHeight -
            container.scrollTop -
            entry.target.clientHeight;
          const isNotNearBottomFromApproach1 = d1 < 1;
          const isNotNearBottomFromApproach2 = d2 < 1;
          if (!isNotNearBottomFromApproach1 || !isNotNearBottomFromApproach2) {
            tryScroll(true);
          }
        }
      });
      resizeObserver.observe(container);
      return () => resizeObserver.disconnect();
    }
  }, [
    heightsInitialized,
    initializedForScroll,
    chatId,
    scrollToBottom,
  ]); // Trigger when heights are initialized

  useEffect(() => {
    if (replyPreviewRef.current && replyingTo) {
      // Use requestAnimationFrame to ensure layout is complete
      requestAnimationFrame(() => {
        if (replyPreviewRef.current) {
          setReplyPreviewHeight(replyPreviewRef.current.clientHeight);
        }
      });
    } else {
      setReplyPreviewHeight(0);
    }
  }, [replyingTo, keyboardHeight]); // Add keyboardHeight to trigger recalculation on keyboard state change

  // When a reaction picker is open, dim the rest of the UI except the selected message
  const isDimming = Boolean(reactingToMessage);
  const selectedCount = selectedDisplayedMessages.length;
  const {
    canReplySelected,
    canReactSelected,
    canEditPendingSelected,
    canEditSelected,
    canRetrySelected,
    canCancelSelected,
    canDeleteForEveryoneSelected,
    canDeleteSelected,
  } = getSelectionActionAvailability({
    primarySelectedMessage,
    selectedMessages,
    selectedCount,
    currentUserId: currentUser?.id,
    pendingSendMessageIds: pendingSendsRef.current,
    actionWindowMs: CHAT_MESSAGE_ACTION_WINDOW_MS,
  });

  //   isScrollingToBottomRef: isScrollingToBottomRef.current,
  //   chatId,
  //   locationKey: location.key,
  //   locationPath: location.pathname,
  //   currentUserId: currentUser?.id,
  //   chat: chat ? { id: chat.id, messagesLength: chat.messages?.length } : null,
  //   participantUsers: participantUsers.map(u => u.id),
  //   inputValue,
  //   replyingTo: replyingTo ? replyingTo.message.id : null,
  //   displayedMessageCount,
  //   isLoadingMore,
  //   hasScrolledToBottom,
  //   hasUnseenNewMessage,
  //   showNewMessageIndicator,
  //   keyboardHeight,
  //   navHeight,
  //   headerHeight,
  //   footerHeight,
  //   heightsInitialized,
  //   initializedForScroll,
  //   activityId: activity?.id,
  //   isActivityExpired,
  //   isActivityDeleted,
  //   isDirectChat,
  //   isReadonly,
  //   anyReactionPickerOpen,
  //   reactingToMessageId: reactingToMessage?.id,
  //   readReceiptsMessageId: readReceiptsMessage?.id,
  //   messageStatusMessageId: messageStatusMessage?.id,
  //   reactionDetailsEmoji,
  //   reactionDetailsMessageId: reactionDetailsMessage?.id,
  // }));

  // Search match IDs (client-side filter over displayed messages)
  const searchMatchIds = useMemo(
    () => getSearchMatchIds(displayedMessages, searchQuery, searchActive),
    [searchQuery, searchActive, displayedMessages],
  );

  // Users currently typing (exclude self, stale after 8s)
  const typingUsers = useMemo(() => {
    if (!currentUser?.id) return [] as User[];
    const now = Date.now();
    return Object.entries(typingByUserId)
      .filter(([uid, ts]) => uid !== currentUser.id && now - new Date(ts as string).getTime() < 8000)
      .map(([uid]) => participantUsers.find((u) => u.id === uid))
      .filter(Boolean) as User[];
  }, [typingByUserId, currentUser?.id, participantUsers]);

  const forwardChatOptions = useMemo(() => {
    if (!currentUser?.id) return [];
    return getChatsForUser(currentUser.id).filter((candidate) =>
      canForwardToChat(candidate),
    );
  }, [currentUser?.id, getChatsForUser, canForwardToChat]);

  const eligibleForwardChatIds = useMemo(
    () => new Set(forwardChatOptions.map((candidate) => candidate.id)),
    [forwardChatOptions],
  );

  useEffect(() => {
    if (!showForwardPicker) return;
    setForwardTargetIds((prev) => {
      let changed = false;
      const next = new Set<string>();

      prev.forEach((targetId) => {
        if (eligibleForwardChatIds.has(targetId)) {
          next.add(targetId);
        } else {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [showForwardPicker, eligibleForwardChatIds]);

  const forwardDirectParticipantByChatId = useMemo(() => {
    if (!currentUser?.id) return {} as Record<string, string>;

    const byChatId: Record<string, string> = {};
    forwardChatOptions.forEach((candidate) => {
      if (candidate.activityId) return;
      const otherParticipantId =
        candidate.participants.find((participantId) => participantId !== currentUser.id) ||
        candidate.participants[0];
      if (otherParticipantId) {
        byChatId[candidate.id] = otherParticipantId;
      }
    });

    return byChatId;
  }, [forwardChatOptions, currentUser?.id]);

  useEffect(() => {
    if (!showForwardPicker || !currentUser?.id) return;

    let cancelled = false;

    const hydrateDirectChatNames = async () => {
      const knownUsersById = new Map<string, User>();
      knownUsersById.set(currentUser.id, currentUser);
      participantUsers.forEach((user) => {
        knownUsersById.set(user.id, user);
      });

      const participantIds = Array.from(
        new Set(Object.values(forwardDirectParticipantByChatId)),
      );
      const missingParticipantIds = participantIds.filter(
        (participantId) => !knownUsersById.has(participantId),
      );

      if (missingParticipantIds.length > 0) {
        try {
          const fetchedUsers = await getUsersByIds(missingParticipantIds);
          fetchedUsers.forEach((user) => {
            knownUsersById.set(user.id, user);
          });
        } catch {
          // Keep fallback titles when user lookups fail.
        }
      }

      if (cancelled) return;

      const nextNames: Record<string, string> = {};
      forwardChatOptions.forEach((candidate) => {
        if (candidate.activityId) return;
        const participantId = forwardDirectParticipantByChatId[candidate.id];
        const participant = participantId
          ? knownUsersById.get(participantId)
          : undefined;
        nextNames[candidate.id] =
          participant?.username || candidate.activityTitle || "Direct chat";
      });

      setForwardDirectChatNameById(nextNames);
    };

    void hydrateDirectChatNames();

    return () => {
      cancelled = true;
    };
  }, [
    showForwardPicker,
    currentUser,
    participantUsers,
    forwardChatOptions,
    forwardDirectParticipantByChatId,
    getUsersByIds,
  ]);

  const starredMessages = useMemo(() => {
    if (!effectiveChatMessages.length || !currentUser?.id) return [] as ChatMessage[];
    return effectiveChatMessages
      .filter(
        (message) =>
          !message.deletedForEveryone &&
          Array.isArray(message.starredBy) &&
          message.starredBy.includes(currentUser.id),
      )
      .slice()
      .reverse();
  }, [effectiveChatMessages, currentUser?.id]);

  if (!chatId) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-gray-500">Chat does not exist.</div>
      </div>
    );
  }

  if (isAuthLoading || (!chat && isWaitingForChat)) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-gray-500">Loading chat...</div>
      </div>
    );
  }

  if (!chat || !currentUser) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-gray-500">
          Chat not found or you're not a participant.
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-container relative h-screen bg-white`}>
      {/* Header fixed at viewport top */}
      <header
        ref={headerRef}
        className="chat-header z-[60] border-b border-gray-200/80 bg-white fixed left-0 right-0"
        style={{
          top: "max(env(safe-area-inset-top, 0px), 0px)",
          position: "fixed", // Explicitly set
          touchAction: "none", // Prevent touch gestures on header
        }}
      >
        <div className="flex items-center p-3">
          <button
            onClick={() =>
              runHeaderAction(
                selectionMode ? clearMessageSelection : handleBackNavigation,
              )
            }
            className="w-10 h-10 flex items-center justify-center text-gray-500 mr-2.5 rounded-full hover:bg-gray-100/50"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          {selectionMode ? (
            <div className="flex items-center justify-between flex-1 min-w-0 gap-2">
              <div className="min-w-0">
                <h2 className="font-bold text-gray-900 truncate">
                  {selectedCount} selected
                </h2>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {selectedCount === 1 && (
                  <button
                    type="button"
                    onClick={() => runHeaderAction(handleReplySelectedMessage)}
                    disabled={!canReplySelected}
                    className={`w-10 h-10 flex items-center justify-center rounded-full ${canReplySelected ? "text-gray-700 hover:bg-gray-100" : "text-gray-300 cursor-not-allowed"}`}
                    title="Reply"
                  >
                    <CornerDownRightIcon className="w-[18px] h-[18px]" />
                  </button>
                )}
                {canReactSelected && (
                  <button
                    type="button"
                    onClick={() => {
                      runHeaderAction(() => {
                        setShowBulkReactionPicker(true);
                      });
                    }}
                    className="w-10 h-10 flex items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                    title="React"
                  >
                    <SmileIcon className="w-[18px] h-[18px]" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    runHeaderAction(() => {
                      void handleCopySelectedMessages();
                    })
                  }
                  className="w-10 h-10 flex items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                  title="Copy"
                >
                  <CopyIcon className="w-[18px] h-[18px]" />
                </button>
                {selectedCount === 1 && canRetrySelected && (
                  <button
                    type="button"
                    onClick={() => runHeaderAction(handleRetrySelectedMessage)}
                    className="h-10 px-3 flex items-center justify-center rounded-full text-xs font-semibold text-gray-700 hover:bg-gray-100"
                    title="Retry"
                  >
                    Retry
                  </button>
                )}
                {selectedCount === 1 && canCancelSelected && (
                  <button
                    type="button"
                    onClick={() =>
                      runHeaderAction(handleCancelSelectedPendingMessage)
                    }
                    className="h-10 px-3 flex items-center justify-center rounded-full text-xs font-semibold text-gray-700 hover:bg-gray-100"
                    title="Cancel pending message"
                  >
                    Cancel
                  </button>
                )}
                {selectedCount === 1 && !canRetrySelected && !canCancelSelected && canEditSelected && (
                  <button
                    type="button"
                    onClick={() => runHeaderAction(handleEditSelectedMessage)}
                    className="w-10 h-10 flex items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                    title="Edit"
                  >
                    <Edit2Icon className="w-[18px] h-[18px]" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    runHeaderAction(() => {
                      void handleStarSelectedMessages();
                    })
                  }
                  className="relative w-10 h-10 flex items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                  title="Star / Unstar"
                >
                  <StarIcon className="w-[18px] h-[18px]" />
                  {starredMessages.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] leading-4 text-center font-semibold">
                      {starredMessages.length}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    runHeaderAction(() => {
                      setShowSelectionActionsMenu(true);
                    })
                  }
                  className="w-10 h-10 flex items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                  title="More actions"
                >
                  <MoreVerticalIcon className="w-[18px] h-[18px]" />
                </button>
              </div>
            </div>
          ) : isDirectChat && otherParticipant ? (
            <button
              onClick={() => navigate(`/profile/${otherParticipant.id}`)}
              className="font-bold text-gray-900 truncate hover:text-primary transition-colors cursor-pointer text-left flex-1"
            >
              <h2 className="font-semibold text-[15px] leading-5 text-gray-900 truncate">
                {headerTitle}
              </h2>
              <p className="text-[12px] leading-4 flex items-center gap-2 text-gray-500 truncate mt-0.5">
                {headerSubtitle}
              </p>
            </button>
          ) : (
            <div className="flex-1 overflow-hidden">
              {activity ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/activity/${activity.id}`)}
                    className="font-bold text-gray-900 truncate hover:text-primary transition-colors cursor-pointer text-left flex-1"
                  >
                    {headerTitle}
                  </button>
                  {(activity.isPrivate ||
                    isActivityDeleted ||
                    isActivityExpired) && (
                      <div className="flex items-center gap-1">
                        {activity.isPrivate && (
                          <div
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                            title="Private activity"
                          >
                            🔒 Private
                          </div>
                        )}
                        {isActivityDeleted && (
                          <div
                            className="flex items-center text-red-500"
                            title="Activity deleted"
                          >
                            <Trash2Icon className="w-4 h-4" />
                          </div>
                        )}
                        {isActivityExpired && !isActivityDeleted && (
                          <div
                            className="flex items-center text-orange-500"
                            title="Activity expired"
                          >
                            <ClockIcon className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    )}
                </div>
              ) : (
                <h2 className="font-semibold text-[15px] leading-5 text-gray-900 truncate">
                  {headerTitle}
                </h2>
              )}
              {activity ? (
                (organizerName || isActivityDeleted || isActivityExpired) && (
                <div className="flex items-center gap-2">
                  {organizerName && (
                    <div className="inline-flex items-center gap-1 bg-blue-50/60 text-blue-700/80 text-[11px] font-medium px-2 py-0.5 rounded-full max-w-full">
                      <StarIcon className="w-3 h-3 text-blue-500/80" />
                      <span className="truncate">
                        Created by {organizerName}{" "}
                        {timeAgoPlain(activity?.createdAt)} ({participantCount}/
                        {activity.groupSize} filled)
                      </span>
                    </div>
                  )}
                  {(isActivityDeleted || isActivityExpired) && (
                    <span className="text-xs font-medium">
                      {isActivityDeleted ? (
                        <span className="text-red-500">Deleted</span>
                      ) : isActivityExpired ? (
                        <span className="text-orange-500">Expired</span>
                      ) : null}
                    </span>
                  )}
                </div>
                )
              ) : (
                <p className="text-[12px] leading-4 text-gray-500 truncate mt-0.5">
                  {headerSubtitle}
                </p>
              )}
            </div>
          )}
          {/* Search button - visible when not in selection mode */}
          {!selectionMode && (
            <div className="ml-1 flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() =>
                  runHeaderAction(() => {
                    setShowStarredMessages(true);
                  })
                }
                className="relative w-10 h-10 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100/50"
                title="Starred messages"
              >
                <StarIcon className="w-[18px] h-[18px]" />
                {starredMessages.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] leading-4 text-center font-semibold">
                    {starredMessages.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => runHeaderAction(handleToggleSearch)}
                className="w-10 h-10 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100/50"
                title={searchActive ? "Close search" : "Search messages"}
              >
                {searchActive ? <XIcon className="w-[18px] h-[18px]" /> : <SearchIcon className="w-[18px] h-[18px]" />}
              </button>
            </div>
          )}
        </div>
        {/* Search bar row */}
        {searchActive && (
          <div className="px-3 pb-2.5 flex items-center gap-1.5">
            <div className="flex-1 flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1.5">
              <SearchIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchMatchIndex(0); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const next = searchMatchIds.length > 0 ? (searchMatchIndex + 1) % searchMatchIds.length : 0;
                    setSearchMatchIndex(next);
                    if (searchMatchIds[next]) scrollToMessage(searchMatchIds[next]);
                  }
                }}
                placeholder="Search messages..."
                className="flex-1 bg-transparent text-[13px] leading-5 text-gray-900 placeholder-gray-400 outline-none min-w-0"
                name="chat-message-search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="search"
              />
              {searchQuery && (
                <button type="button" onClick={() => { setSearchQuery(""); setSearchMatchIndex(0); }} className="text-gray-400 hover:text-gray-600">
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {searchQuery && (
              <span className="text-[11px] text-gray-500 flex-shrink-0 min-w-[3rem] text-right">
                {searchMatchIds.length > 0 ? `${searchMatchIndex + 1}/${searchMatchIds.length}` : "No results"}
              </span>
            )}
            <div className="flex gap-0.5 flex-shrink-0">
              <button
                type="button"
                disabled={searchMatchIds.length < 2}
                onClick={() => { const next = (searchMatchIndex - 1 + searchMatchIds.length) % searchMatchIds.length; setSearchMatchIndex(next); scrollToMessage(searchMatchIds[next]); }}
                className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-30 text-gray-600"
              >
                <ChevronUpIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                disabled={searchMatchIds.length < 2}
                onClick={() => { const next = (searchMatchIndex + 1) % searchMatchIds.length; setSearchMatchIndex(next); scrollToMessage(searchMatchIds[next]); }}
                className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-30 text-gray-600"
              >
                <ChevronDownIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Status Banner for Expired/Deleted Activities */}
      {((!isDirectChat &&
        (isActivityDeleted || isActivityExpired || activity?.isReported)) ||
        isReadonly) && (
          <div
            className={`px-4 py-3 text-center text-sm font-medium border-b ${isActivityDeleted
              ? "bg-red-50 text-red-700 border-red-200"
              : "bg-orange-50 text-orange-700 border-orange-200"
              }`}
            style={{
              position: "fixed",
              height: "45px",
              top: `calc(${contentTopPosition} - 45px)` /* Safe area + header height */,
              left: "0",
              right: "0",
              overscrollBehavior: "none" /* Prevent parent scroll */,
              // On Android, when keyboard is open, prevent pointer events on empty areas
              ...(isAndroid &&
                inputRef.current &&
                document.activeElement === inputRef.current
                ? {
                  pointerEvents: "none" as const,
                }
                : {}),
            }}
          >
            <div className="flex items-center justify-center gap-2">
              {isActivityDeleted ? (
                <>
                  <Trash2Icon className="w-4 h-4" />
                  <span>This activity has been deleted. Chat is view-only.</span>
                </>
              ) : isActivityExpired ? (
                <>
                  <ClockIcon className="w-4 h-4" />
                  <span>This activity has expired. Chat is view-only.</span>
                </>
              ) : activity?.isReported ? (
                <>
                  <AlertTriangleIcon className="w-4 h-4" />
                  <span>This activity has been reported. Chat is view-only.</span>
                </>
              ) : (
                <>
                  <LockIcon className="w-4 h-4" />
                  <span>Chat is view-only.</span>
                </>
              )}
            </div>
          </div>
        )}

      {/* Chat content area - scrollable messages between fixed header and footer */}
      <div
        ref={messagesContainerRef}
        className={`overflow-y-auto bg-chat-pattern`}
        style={{
          position: "fixed",
          top: contentTopPosition /* Safe area + header height */,
          bottom: contentBottomPosition /* Safe area + footer + nav */,
          left: "0",
          right: "0",
          WebkitOverflowScrolling:
            "touch" /* Enable momentum scrolling on iOS */,
          touchAction: "pan-y" /* Allow vertical scrolling */,
          overscrollBehavior: "none" /* Prevent parent scroll */,
          transition: "top 0.2s ease-out, bottom 0.2s ease-out", // Add smooth transition
          // On Android, when keyboard is open, prevent pointer events on empty areas
          ...(isAndroid &&
            inputRef.current &&
            document.activeElement === inputRef.current
            ? {
              pointerEvents: "none" as const,
            }
            : {}),
        }}
      >
        <div
          className="space-y-1 px-4 pt-4 pb-4"
          style={{
            // Re-enable pointer events on the content container
            pointerEvents: "auto",
          }}
        >
          {/* Loading indicator for older messages */}
          {hasMoreMessages && (
            <div className="flex justify-center py-2">
              <div className="text-sm text-gray-500 flex items-center gap-2">
                {isLoadingMore ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Loading older messages...</span>
                  </>
                ) : (
                  <button
                    onClick={loadMoreMessages}
                    className="text-primary hover:text-primary/80 font-medium"
                  >
                    Load older messages (
                    {Math.max(0, effectiveChatMessages.length - displayedMessageCount)} more)
                  </button>
                )}
              </div>
            </div>
          )}

          {messageRows.map(
            ({
              msg,
              sender,
              repliedToSender,
              showSenderName,
              isSelected,
              showDateSeparator,
              dateSeparatorText,
              senderStatus,
            }) => {

            const isActionSelected = selectedMessageIds.has(msg.id);
            const rowHighlightClass = [
              searchActive && searchMatchIds.includes(msg.id)
                ? "bg-yellow-100/70"
                : "",
              isActionSelected ? "bg-primary/10" : "",
              "rounded-xl transition-colors",
            ]
              .filter(Boolean)
              .join(" ");

            if (isSelected) {
              return (
                <div key={msg.id} className={rowHighlightClass}>
                  {showDateSeparator && dateSeparatorText && (
                    <div className="flex justify-center my-3">
                      <span className="bg-white text-gray-500 text-xs font-medium px-3 py-1 rounded-full shadow-sm border border-gray-200">
                        {dateSeparatorText}
                      </span>
                    </div>
                  )}
                  {ReactDOM.createPortal(
                    <div className="fixed inset-0 bg-black/20 z-[51] pointer-events-none" />,
                    document.body,
                  )}
                  {ReactDOM.createPortal(
                    <div
                      className="z-[51] fixed left-0 right-0"
                      style={{ top: contentTopPosition }}
                    >
                      <ChatBubble
                        message={msg}
                        sender={sender || null}
                        activeLiveLocationShare={msg.location?.liveShareId ? liveLocationSharesById[msg.location.liveShareId] || null : null}
                        onReply={handleReply}
                        onRetryFailedMessage={handleRetryFailedMessage}
                        isSlowSending={slowSendingMessageIds.has(msg.id)}
                        onEnterSelectionMode={enterSelectionMode}
                        onToggleSelection={toggleMessageSelection}
                        selectionMode={selectionMode}
                        isSelectedForAction={selectedMessageIds.has(msg.id)}
                        onReplyClick={scrollToMessage}
                        repliedToSender={repliedToSender}
                        isGroupChat={!isDirectChat}
                        participants={participantUsers}
                        onShowReadReceipts={handleShowReadReceipts}
                        onAddReaction={handleAddReaction}
                        onOpenMedia={handleOpenFullscreenMedia}
                        onDownloadMedia={handleDownloadMedia}
                        onShowMessageStatus={handleShowMessageStatus}
                        onShowReactionDetails={(emoji, message) => {
                          setReactionDetailsEmoji(emoji);
                          setReactionDetailsMessage(message);
                        }}
                        onReactionUsed={rememberRecentReaction}
                        quickReactionEmojis={quickReactionEmojis}
                        isReactingTo={isSelected}
                        showSenderName={showSenderName}
                        inputRef={inputRef}
                        isOpeningReactionPickerRef={isOpeningReactionPickerRef}
                        isNavigatingToReplyRef={isNavigatingToReplyRef}
                        onPickerStateChange={handlePickerStateChange}
                        senderStatus={senderStatus}
                        organizerId={activity?.creatorId}
                        keyboardHeight={keyboardHeight}
                      />
                    </div>,
                    document.body,
                  )}
                </div>
              );
            }

            // Otherwise, render normally
            return (
              <div key={msg.id} className={rowHighlightClass}>
                {showDateSeparator && dateSeparatorText && (
                  <div className="flex justify-center my-3">
                    <span className="bg-white text-gray-500 text-xs font-medium px-3 py-1 rounded-full shadow-sm border border-gray-200">
                      {dateSeparatorText}
                    </span>
                  </div>
                )}
                <ChatBubble
                  message={msg}
                  sender={sender || null}
                  activeLiveLocationShare={msg.location?.liveShareId ? liveLocationSharesById[msg.location.liveShareId] || null : null}
                  onReply={handleReply}
                  onRetryFailedMessage={handleRetryFailedMessage}
                  isSlowSending={slowSendingMessageIds.has(msg.id)}
                  onEnterSelectionMode={enterSelectionMode}
                  onToggleSelection={toggleMessageSelection}
                  selectionMode={selectionMode}
                  isSelectedForAction={selectedMessageIds.has(msg.id)}
                  onReplyClick={scrollToMessage}
                  repliedToSender={repliedToSender}
                  isGroupChat={!isDirectChat}
                  participants={participantUsers}
                  onShowReadReceipts={handleShowReadReceipts}
                  onAddReaction={handleAddReaction}
                  onOpenMedia={handleOpenFullscreenMedia}
                  onDownloadMedia={handleDownloadMedia}
                  onShowMessageStatus={handleShowMessageStatus}
                  onShowReactionDetails={(emoji, message) => {
                    setReactionDetailsEmoji(emoji);
                    setReactionDetailsMessage(message);
                  }}
                  onReactionUsed={rememberRecentReaction}
                  quickReactionEmojis={quickReactionEmojis}
                  isReactingTo={isSelected}
                  showSenderName={showSenderName}
                  inputRef={inputRef}
                  isOpeningReactionPickerRef={isOpeningReactionPickerRef}
                  isNavigatingToReplyRef={isNavigatingToReplyRef}
                  onPickerStateChange={handlePickerStateChange}
                  senderStatus={senderStatus}
                  organizerId={activity?.creatorId}
                  keyboardHeight={keyboardHeight}
                />
              </div>
            );
            },
          )}
          {/* Typing indicator bubble */}
          {typingUsers.length > 0 && (
            <div className="flex items-start gap-2 justify-start py-0.5">
              <div className="w-8 h-8 flex-shrink-0" />
              <div className="flex items-center gap-0 bg-white border border-gray-200 rounded-2xl rounded-tl-none px-3.5 py-3 shadow-sm">
                <span className="typing-dot" />
                <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                <span className="typing-dot" style={{ animationDelay: "0.30s" }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add the new message indicator button in the UI, positioned above the footer */}
      {showNewMessageIndicator && (
        <button
          ref={scrollButtonRef}
          tabIndex={-1}
          onPointerDown={(e) => {
            // Prevent button from receiving focus, but allow click/touch to propagate
            e.preventDefault();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            // Don't preventDefault here - it's a passive listener
            scrollButtonTouchStartTime.current = Date.now();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            // Don't preventDefault here - it's a passive listener

            // Only execute on short tap (< 500ms), ignore long press
            const touchDuration =
              Date.now() - scrollButtonTouchStartTime.current;
            if (touchDuration < 500) {
              handleScrollToBottom();
            }
            scrollButtonTouchStartTime.current = 0;
          }}
          onTouchCancel={(e) => {
            e.stopPropagation();
            // Don't preventDefault here - it's a passive listener
            scrollButtonTouchStartTime.current = 0;
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={handleScrollToBottom}
          className="fixed left-1/2 -translate-x-1/2 z-[60] bg-white text-primary px-4 py-2 rounded-full shadow-xl border border-gray-200 hover:bg-gray-50 transition-all select-none"
          style={{
            top: `calc(${contentTopPosition} + 1rem)`,
            pointerEvents: "auto",
            WebkitUserSelect: "none",
            userSelect: "none",
            WebkitTouchCallout: "none",
          }}
          aria-label="Scroll to new messages"
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
            {hasUnseenNewMessage ? "New message" : ""}
          </div>
        </button>
      )}

      {/* Chat input footer - fixed at bottom above nav */}
      <footer
        ref={footerRef}
        className={`chat-footer border-t border-gray-200/80 bg-white fixed left-0 right-0`}
        style={{
          bottom: footerBottomPosition,
          transition: "bottom 0.2s ease-out", // Add smooth transition
        }}
      >
        {(!isDirectChat &&
          (isActivityDeleted || isActivityExpired || activity?.isReported)) ||
          isReadonly ? (
          <div className="text-center text-sm text-gray-500 py-2">
            {isReadonly
              ? "Viewing chat history - Read only mode"
              : activity?.isReported
                ? "Activity is under review - Cannot send messages"
                : isActivityDeleted
                  ? "Cannot send messages - activity deleted"
                  : "Cannot send messages - activity ended"}
          </div>
        ) : (
          <div>
            {/* Reply preview */}
            {replyingTo && (
              <div
                ref={replyPreviewRef}
                className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-start gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <CornerDownRightIcon className="w-4 h-4 text-primary flex-shrink-0" />
                    <p className="text-xs font-semibold text-primary">
                      Replying to {replyingTo.sender.username}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 truncate pl-6">
                    {replyingTo.message.text}
                  </p>
                </div>
                <button
                  onMouseDown={(e) => {
                    // Prevent input from losing focus when clicking cancel
                    e.preventDefault();
                  }}
                  onClick={cancelReply}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                  aria-label="Cancel reply"
                >
                  <XIcon className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            )}

            {editingMessage && (
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-primary mb-1">Editing message</p>
                  <p className="text-sm text-gray-600 truncate">
                    {editingMessage.text || "Media message"}
                  </p>
                </div>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onClick={cancelEdit}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
                  aria-label="Cancel edit"
                >
                  <XIcon className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <input
                id={CHAT_MEDIA_INPUT_ID}
                ref={mediaInputRef}
                type="file"
                accept={CHAT_MEDIA_ACCEPT_ALL}
                multiple
                className="absolute w-px h-px opacity-0 pointer-events-none"
                onChange={handleMediaSelection}
              />

              {currentUserActiveLiveShare && !isRecordingVoiceNote && (
                <div className="mb-2 flex items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2">
                  <div className="min-w-0 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0">
                      <MapPinIcon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">Live location is on</p>
                      <p className="text-xs text-gray-600 truncate">
                        {formatLiveLocationRemaining(currentUserActiveLiveShare.expiresAt)
                          ? `Ends in ${formatLiveLocationRemaining(currentUserActiveLiveShare.expiresAt)}`
                          : "Recipients can see your live location until you stop sharing."}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-xl bg-red-500 px-3 py-2 text-xs font-semibold text-white hover:bg-red-600"
                    onClick={() => {
                      void handleStopLiveLocationShare(currentUserActiveLiveShare.id);
                    }}
                  >
                    Stop
                  </button>
                </div>
              )}

              {selectedMedia.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2 mb-1">
                  {selectedMedia.map((item) => (
                    <div
                      key={item.id}
                      className={`relative rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 ${item.type === "audio" ? "w-56 h-auto p-2 bg-gray-50" : "w-16 h-16"}`}
                    >
                      {item.type === "video" ? (
                        <video
                          src={item.previewUrl}
                          className="w-full h-full object-cover"
                        />
                      ) : item.type === "image" ? (
                        <img
                          src={item.previewUrl}
                          alt={item.file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : item.type === "audio" ? (
                        <div className="pr-6">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                              <MicIcon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {isVoiceNoteFileName(item.file.name) ? "Voice note" : item.file.name}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {formatFileSize(item.file.size)}
                              </p>
                            </div>
                          </div>
                          <audio
                            src={item.previewUrl}
                            controls
                            preload="metadata"
                            className="w-full h-10"
                          />
                        </div>
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center text-xl">
                          📄
                        </div>
                      )}
                      <button
                        type="button"
                        className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                        onClick={() => handleRemoveSelectedMedia(item.id)}
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2 bg-white border border-gray-300 rounded-[24px] px-3 py-1.5">
                  {isRecordingVoiceNote ? (
                    <>
                      <button
                        type="button"
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-red-500 cursor-pointer flex-shrink-0"
                        onClick={() => stopVoiceNoteRecording(true)}
                        title="Cancel voice note"
                      >
                        <Trash2Icon className="w-5 h-5" />
                      </button>
                      <div className="flex-1 min-w-0 flex items-center gap-3 py-1">
                        <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500"></span>
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">Recording voice note</p>
                          <p className="text-xs text-gray-500">
                            {formatDurationClock(voiceNoteDurationMs)} • Tap stop to preview
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePickMedia();
                        }}
                        title="Share"
                      >
                        <PlusIcon className="w-5 h-5 text-gray-500" />
                      </button>
                      <div className="flex-1 min-w-0 flex items-center">
                        <textarea
                          ref={inputRef}
                          rows={1}
                          value={inputValue}
                          onMouseDown={() => {
                            userInitiatedComposerFocusRef.current = true;
                          }}
                          onTouchStart={() => {
                            userInitiatedComposerFocusRef.current = true;
                          }}
                          onChange={(e) => handleTypingInput(e.target.value)}
                          onSelect={updateComposerSelection}
                          onKeyUp={updateComposerSelection}
                          onClick={updateComposerSelection}
                          onKeyDown={handleComposerKeyDown}
                          onTouchEnd={() => {
                            requestAnimationFrame(() => {
                              updateComposerSelection();
                            });
                          }}
                          onContextMenu={() => {
                            requestAnimationFrame(() => {
                              updateComposerSelection();
                            });
                          }}
                          placeholder={
                            editingMessage
                              ? "Edit message..."
                              : replyingTo
                              ? `Reply to ${replyingTo.sender.username}...`
                              : "Type a message"
                          }
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="sentences"
                          spellCheck={false}
                          name="chat-message-input"
                          enterKeyHint="send"
                          data-keyboard-suppressed={
                            suppressComposerAutoFocusRef.current ? "true" : "false"
                          }
                          onFocus={(e) => {
                            if (!isDirectChat && activity?.isReported) {
                              e.target.blur();
                              return;
                            }

                            const userInitiatedFocus =
                              userInitiatedComposerFocusRef.current;
                            userInitiatedComposerFocusRef.current = false;

                            // If keyboard was just dismissed and auto-focus is suppressed,
                            // ignore programmatic/non-user focus attempts that would reopen it.
                            if (
                              isAndroid &&
                              suppressComposerAutoFocusRef.current &&
                              !userInitiatedFocus
                            ) {
                              if (blockingProgrammaticFocusRef.current) {
                                return;
                              }
                              blockingProgrammaticFocusRef.current = true;
                              // Avoid force-blurring while Android still reports keyboard open,
                              // otherwise focus/blur can oscillate in a tight loop.
                              if (keyboardHeight <= 0) {
                                requestAnimationFrame(() => {
                                  inputRef.current?.blur();
                                  blockingProgrammaticFocusRef.current = false;
                                });
                              } else {
                                setIsComposerFocused(false);
                                requestAnimationFrame(() => {
                                  blockingProgrammaticFocusRef.current = false;
                                });
                              }
                              return;
                            }

                            // User explicitly focused composer, re-enable auto-focus logic.
                            setComposerAutoFocusSuppressed(false);
                            if (userInitiatedFocus && chatId) {
                              dismissedKeyboardByChatId.delete(chatId);
                            }
                            if (!isAndroid && Capacitor.isNativePlatform()) {
                              KeyboardHandler.getInstance().showKeyboard();
                            }
                            if (composerBlurTimeoutRef.current) {
                              clearTimeout(composerBlurTimeoutRef.current);
                              composerBlurTimeoutRef.current = null;
                            }
                            setIsComposerFocused(true);
                            updateComposerSelection();
                          }}
                          onBlur={() => {
                            userInitiatedComposerFocusRef.current = false;
                            blockingProgrammaticFocusRef.current = false;
                            if (chatEntryShowRetryTimeoutRef.current) {
                              clearTimeout(chatEntryShowRetryTimeoutRef.current);
                              chatEntryShowRetryTimeoutRef.current = null;
                            }
                            if (chatEntryShowRetryTimeout2Ref.current) {
                              clearTimeout(chatEntryShowRetryTimeout2Ref.current);
                              chatEntryShowRetryTimeout2Ref.current = null;
                            }
                            composerBlurTimeoutRef.current = setTimeout(() => {
                              const inputEl = inputRef.current;
                              const hasSelection =
                                Boolean(inputEl) &&
                                (inputEl?.selectionEnd || 0) > (inputEl?.selectionStart || 0);

                              if (hasSelection) {
                                setIsComposerFocused(true);
                                updateComposerSelection();
                                return;
                              }

                              setIsComposerFocused(false);
                            }, 120);
                          }}
                          onScroll={() => {
                            updateComposerSelection();
                          }}
                          className="w-full bg-transparent border-0 p-0 m-0 focus:outline-none text-gray-900 placeholder-gray-500 resize-none block"
                          style={{
                            fontSize: `${CHAT_COMPOSER_TEXT_SIZE_PX}px`,
                            lineHeight: `${CHAT_COMPOSER_TEXT_SIZE_PX}px`,
                            minHeight: `${CHAT_COMPOSER_TEXT_SIZE_PX}px`,
                            maxHeight: `${CHAT_COMPOSER_MAX_HEIGHT_PX}px`,
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
                {isRecordingVoiceNote ? (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      try {
                        e.stopPropagation();
                        e.preventDefault();
                      } catch { }
                    }}
                    onClick={() => stopVoiceNoteRecording(false)}
                    className="w-10 h-10 md:w-11 md:h-11 bg-red-500 text-white rounded-full flex items-center justify-center hover:opacity-90 transition-opacity shadow-lg shadow-red-500/30 flex-shrink-0"
                    style={{ alignSelf: "center" }}
                    title="Stop recording"
                  >
                    <StopCircleIcon className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                ) : (inputValue.trim() || selectedMedia.length > 0) ? (
                  <button
                    type="submit"
                    onMouseDown={(e) => {
                      try {
                        e.stopPropagation();
                        e.preventDefault();
                      } catch { }
                    }}
                    className="w-10 h-10 md:w-11 md:h-11 bg-primary text-white rounded-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity shadow-lg shadow-primary/30 flex-shrink-0"
                    style={{ alignSelf: "center" }}
                  >
                    <SendIcon
                      className="w-4 h-4 md:w-5 md:h-5"
                      onMouseDown={(e) => {
                        try {
                          e.stopPropagation();
                          e.preventDefault();
                        } catch { }
                      }}
                    />
                  </button>
                ) : canShowVoiceNoteButton ? (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      try {
                        e.stopPropagation();
                        e.preventDefault();
                      } catch { }
                    }}
                    onClick={() => {
                      void startVoiceNoteRecording();
                    }}
                    className="w-10 h-10 md:w-11 md:h-11 bg-primary text-white rounded-full flex items-center justify-center hover:opacity-90 transition-opacity shadow-lg shadow-primary/30 flex-shrink-0"
                    style={{ alignSelf: "center" }}
                    title="Record voice note"
                  >
                    <MicIcon className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                ) : null}
              </div>

              {isComposerFocused && hasComposerSelection && (
                <div className="mt-2 flex items-center gap-1.5 rounded-2xl bg-gray-100 border border-gray-200 text-gray-800 px-2 py-1.5 w-fit max-w-full overflow-x-auto">
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyInlineFormat("*")}
                    className="h-8 min-w-8 px-2 rounded-xl text-xs font-semibold bg-white border border-gray-200 hover:bg-gray-50"
                    title="Bold (Ctrl/Cmd+B)"
                  >
                    B
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyInlineFormat("_")}
                    className="h-8 min-w-8 px-2 rounded-xl text-xs italic bg-white border border-gray-200 hover:bg-gray-50"
                    title="Italic (Ctrl/Cmd+I)"
                  >
                    I
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyInlineFormat("~")}
                    className="h-8 min-w-8 px-2 rounded-xl text-xs line-through bg-white border border-gray-200 hover:bg-gray-50"
                    title="Strikethrough (Ctrl/Cmd+Shift+X)"
                  >
                    S
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowComposerFormatOverflow((prev) => !prev)}
                    className={`h-8 min-w-8 px-2 rounded-xl text-xs font-semibold border ${showComposerFormatOverflow
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                    title="More formatting"
                  >
                    +
                  </button>

                  {showComposerFormatOverflow && (
                    <>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyInlineFormat("`")}
                        className="h-8 min-w-8 px-2 rounded-xl text-xs font-mono bg-white border border-gray-200 hover:bg-gray-50"
                        title="Monospace (Ctrl/Cmd+E)"
                      >
                        Code
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyLinePrefixFormat("# ")}
                        className="h-8 min-w-8 px-2 rounded-xl text-xs bg-white border border-gray-200 hover:bg-gray-50"
                        title="Toggle heading"
                      >
                        H1
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyLinePrefixFormat("- ")}
                        className="h-8 min-w-8 px-2 rounded-xl text-xs bg-white border border-gray-200 hover:bg-gray-50"
                        title="Toggle bullet list"
                      >
                        List
                      </button>
                    </>
                  )}
                </div>
              )}

            </form>
          </div>
        )}
      </footer>

      {showAttachmentSheet
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[91]">
              <button
                type="button"
                className="absolute inset-0 bg-black/35"
                onClick={() => setShowAttachmentSheet(false)}
                aria-label="Close attachment sheet"
              />
              <div className="absolute left-0 right-0 bottom-0 rounded-t-3xl bg-white shadow-2xl border-t border-gray-200 px-4 pt-3 pb-6">
                <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-gray-900">Share</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Choose what you want to share without opening a generic file picker.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleAttachmentSheetPick("location")}
                    className="rounded-2xl border border-gray-200 px-4 py-4 text-left hover:bg-gray-50"
                  >
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-3 ${currentUserActiveLiveShare ? "bg-red-50 text-red-500" : "bg-rose-50 text-rose-600"}`}>
                      <MapPinIcon className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      {currentUserActiveLiveShare ? "Location sharing" : "Location"}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {currentUserActiveLiveShare
                        ? "Manage or stop your active live location share."
                        : "Share your current location or live updates."}
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAttachmentSheetPick("camera")}
                    className="rounded-2xl border border-gray-200 px-4 py-4 text-left hover:bg-gray-50"
                  >
                    <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-3">
                      <CameraIcon className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">Camera</p>
                    <p className="text-xs text-gray-500 mt-1">Take a photo right now.</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAttachmentSheetPick("media")}
                    className="rounded-2xl border border-gray-200 px-4 py-4 text-left hover:bg-gray-50"
                  >
                    <div className="w-11 h-11 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center mb-3">
                      <ImageIcon className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">Photos & videos</p>
                    <p className="text-xs text-gray-500 mt-1">Pick media from your library.</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAttachmentSheetPick("audio")}
                    className="rounded-2xl border border-gray-200 px-4 py-4 text-left hover:bg-gray-50"
                  >
                    <div className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                      <MicIcon className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">Audio</p>
                    <p className="text-xs text-gray-500 mt-1">Attach audio files and clips.</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAttachmentSheetPick("document")}
                    className="rounded-2xl border border-gray-200 px-4 py-4 text-left hover:bg-gray-50"
                  >
                    <div className="w-11 h-11 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mb-3">
                      <UploadIcon className="w-5 h-5" />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">Document</p>
                    <p className="text-xs text-gray-500 mt-1">Send PDFs, docs, sheets, and more.</p>
                  </button>
                </div>

                <button
                  type="button"
                  className="mt-4 w-full rounded-2xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200"
                  onClick={() => setShowAttachmentSheet(false)}
                >
                  Cancel
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}

      {showLocationShareSheet
        ? ReactDOM.createPortal(
            <div className="fixed inset-0 z-[90]">
              <button
                type="button"
                className="absolute inset-0 bg-black/30"
                onClick={() => {
                  if (!isPreparingLocationShare) {
                    setShowLocationShareSheet(false);
                  }
                }}
                aria-label="Close location sharing sheet"
              />
              <div className="absolute left-0 right-0 bottom-0 rounded-t-3xl bg-white shadow-2xl border-t border-gray-200 px-4 pt-3 pb-6">
                <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-gray-900">Share location</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Send your current location once, or share live updates for a set duration.
                  </p>
                </div>

                {currentUserActiveLiveShare && (
                  <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Live location is active</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {formatLiveLocationRemaining(currentUserActiveLiveShare.expiresAt)
                            ? `Ends in ${formatLiveLocationRemaining(currentUserActiveLiveShare.expiresAt)}`
                            : "Recipients can see your live updates until you stop sharing."}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="shrink-0 rounded-xl bg-red-500 px-3 py-2 text-xs font-semibold text-white hover:bg-red-600 disabled:opacity-50"
                        disabled={isPreparingLocationShare}
                        onClick={() => {
                          void handleStopLiveLocationShare(currentUserActiveLiveShare.id);
                        }}
                      >
                        Stop live location
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {LIVE_LOCATION_DURATION_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      disabled={isPreparingLocationShare}
                      onClick={() => {
                        void shareLocationMessage(option);
                      }}
                      className="w-full flex items-center gap-3 rounded-2xl border border-gray-200 px-4 py-3 text-left hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${option.kind === "live" ? "bg-red-50 text-red-500" : "bg-primary/10 text-primary"}`}>
                        <MapPinIcon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                        <p className="text-xs text-gray-500">
                          {option.kind === "live"
                            ? "Recipients will see your location move until the timer ends."
                            : "Sends a one-time map pin with your current coordinates."}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="mt-4 w-full rounded-2xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                  disabled={isPreparingLocationShare}
                  onClick={() => setShowLocationShareSheet(false)}
                >
                  Cancel
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}

      {fullscreenMedia && (
        <div
          data-fullscreen-media-overlay="true"
          className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center"
          style={{
            paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            paddingLeft: "12px",
            paddingRight: "12px",
          }}
          onTouchStart={handleFullscreenMediaTouchStart}
          onTouchEnd={handleFullscreenMediaTouchEnd}
          onClick={() => closeFullscreenMedia()}
        >
          <button
            type="button"
            data-media-download-button="true"
            className="absolute left-4 text-white/90 hover:text-white px-3 py-2 rounded-lg bg-white/10"
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
            onClick={(e) => {
              e.stopPropagation();
              void handleDownloadMedia(fullscreenMedia);
            }}
            aria-label="Download media"
          >
            Download
          </button>

          <button
            type="button"
            className="absolute right-4 text-white/90 hover:text-white p-2"
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
            onClick={() => closeFullscreenMedia()}
            aria-label="Close media viewer"
          >
            <XIcon className="w-6 h-6" />
          </button>

          <div
            className="w-full max-w-3xl"
            style={{
              maxHeight:
                "calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 96px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {fullscreenMedia.type === "video" ? (
              <video
                src={fullscreenMedia.url}
                controls
                autoPlay
                className="w-full object-contain rounded-xl"
                style={{
                  maxHeight:
                    "calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 136px)",
                }}
              />
            ) : fullscreenMedia.type === "image" ? (
              <img
                src={fullscreenMedia.url}
                alt={fullscreenMedia.fileName || "Media"}
                className="w-full object-contain rounded-xl"
                style={{
                  maxHeight:
                    "calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 136px)",
                }}
                onError={handleImageError}
              />
            ) : (
              <div
                className="w-full rounded-xl bg-white/95 overflow-hidden"
                style={{
                  height:
                    "calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 136px)",
                }}
              >
                <iframe
                  src={fullscreenMedia.url}
                  title={fullscreenMedia.fileName || "Document preview"}
                  className="w-full h-full border-0"
                />
              </div>
            )}
            <div className="mt-2 text-center text-white/80 text-sm">
              {fullscreenMedia.fileName || "Media"}
            </div>
          </div>
        </div>
      )}

      {showForwardPicker && (
        <div
          className="fixed inset-0 z-[95] bg-black/45 flex items-end"
          onClick={() => {
            if (isForwarding) return;
            setShowForwardPicker(false);
          }}
        >
          <div
            className="w-full bg-white rounded-t-3xl max-h-[70vh] min-h-0 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">
                Forward to ({selectedMessages.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowForwardPicker(false)}
                className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100"
                disabled={isForwarding}
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div
              className="overflow-y-auto flex-1 min-h-0"
              data-modal-scroll-allow="true"
              style={{
                WebkitOverflowScrolling: "touch",
                touchAction: "pan-y",
                overscrollBehavior: "contain",
              }}
            >
              {forwardChatOptions.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">
                  No other chats available
                </p>
              ) : (
                forwardChatOptions.map((candidate) => {
                  const checked = forwardTargetIds.has(candidate.id);
                  const candidateTitle = candidate.activityId
                    ? candidate.activityTitle || "Group chat"
                    : forwardDirectChatNameById[candidate.id] ||
                      candidate.activityTitle ||
                      "Direct chat";
                  return (
                    <button
                      key={candidate.id}
                      type="button"
                      onClick={() => {
                        setForwardTargetIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(candidate.id)) {
                            next.delete(candidate.id);
                          } else {
                            next.add(candidate.id);
                          }
                          return next;
                        });
                      }}
                      className="w-full px-4 py-3.5 flex items-center gap-3 hover:bg-gray-50 text-left"
                    >
                      <span
                        className={`w-5 h-5 rounded border flex items-center justify-center ${checked ? "bg-primary border-primary" : "border-gray-300"}`}
                      >
                        {checked && <CheckIcon2 className="w-3 h-3 text-white" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900 truncate font-medium">
                          {candidateTitle}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {candidate.activityId ? "Group chat" : "Direct chat"}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            <div
              className="border-t border-gray-200 p-3"
              style={{
                paddingBottom: "max(env(safe-area-inset-bottom, 0px), 12px)",
              }}
            >
              <button
                type="button"
                disabled={isForwarding || forwardTargetIds.size === 0}
                onClick={handleSubmitForwardSelectedMessages}
                className="w-full h-12 rounded-xl bg-primary text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isForwarding ? "Forwarding..." : `Forward to ${forwardTargetIds.size} chat${forwardTargetIds.size === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showStarredMessages && (
        <div className="fixed inset-0 z-[95] bg-white flex flex-col">
          <div
            className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200"
            style={{
              paddingTop: "max(env(safe-area-inset-top, 0px), 12px)",
            }}
          >
            <button
              type="button"
              onClick={() => setShowStarredMessages(false)}
              className="p-2 rounded-full text-gray-500 hover:bg-gray-100"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h3 className="text-[15px] font-semibold leading-5 text-gray-900">
              Starred messages
            </h3>
            <span className="ml-auto text-[11px] text-gray-500">
              {starredMessages.length}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {starredMessages.length === 0 ? (
              <p className="py-10 text-center text-[13px] text-gray-500">
                No starred messages yet
              </p>
            ) : (
              starredMessages.map((message) => {
                const messageDate = toJsDate(message.timestamp);
                const isOldMessage = Date.now() - messageDate.getTime() >= 24 * 60 * 60 * 1000;
                const sender =
                  message.senderId === currentUser.id
                    ? currentUser
                    : participantUsers.find((user) => user.id === message.senderId);
                const preview =
                  message.text?.trim() ||
                  (message.media?.length
                    ? `${message.media.length} attachment${message.media.length > 1 ? "s" : ""}`
                    : "Message");

                return (
                  <div
                    key={message.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setShowStarredMessages(false);
                      requestAnimationFrame(() => scrollToMessage(message.id));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setShowStarredMessages(false);
                        requestAnimationFrame(() => scrollToMessage(message.id));
                      }
                    }}
                    className="w-full px-4 py-3.5 border-b border-gray-100 text-left hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[11px] font-semibold text-gray-700 truncate">
                        {sender?.username || "Unknown"}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {isOldMessage
                          ? messageDate.toLocaleString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })
                          : messageDate.toLocaleTimeString("en-US", {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleUnstarFromStarredMessages(message.id);
                        }}
                        className="ml-auto inline-flex items-center gap-1 rounded-full border border-gray-200 px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100"
                        title="Unstar message"
                      >
                        <StarIcon className="w-3 h-3" />
                        Unstar
                      </button>
                    </div>
                    <p className="text-[13px] leading-5 text-gray-800 break-words line-clamp-2">
                      {preview}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {selectionMode && showSelectionActionsMenu && (
        <div
          className="fixed inset-0 z-[95] bg-black/35 flex items-end"
          onClick={() => setShowSelectionActionsMenu(false)}
        >
          <div
            className="w-full bg-white rounded-t-3xl border-t border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="py-2"
              style={{
                paddingBottom: "max(env(safe-area-inset-bottom, 0px), 12px)",
              }}
            >
              {selectedCount === 1 && canRetrySelected && (
                <button
                  type="button"
                  onClick={() => {
                    setShowSelectionActionsMenu(false);
                    handleRetrySelectedMessage();
                  }}
                  className="w-full px-5 py-3.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                >
                  Retry
                </button>
              )}
              {selectedCount === 1 && canCancelSelected && (
                <button
                  type="button"
                  onClick={() => {
                    setShowSelectionActionsMenu(false);
                    handleCancelSelectedPendingMessage();
                  }}
                  className="w-full px-5 py-3.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowSelectionActionsMenu(false);
                  handleForwardSelectedMessages();
                }}
                className="w-full px-5 py-3.5 text-left text-sm text-gray-800 hover:bg-gray-50"
              >
                Forward
              </button>
              {canDeleteSelected && (
                <button
                  type="button"
                  onClick={() => {
                    setShowSelectionActionsMenu(false);
                    handleDeleteSelectedDefault();
                  }}
                  className="w-full px-5 py-3.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                >
                  {canDeleteForEveryoneSelected ? "Delete for everyone" : "Delete for me"}
                </button>
              )}
              {selectedCount === 1 && (
                <button
                  type="button"
                  onClick={() => {
                    setShowSelectionActionsMenu(false);
                    handleInfoSelectedMessage();
                  }}
                  className="w-full px-5 py-3.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                >
                  Message info
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {selectionMode && showBulkReactionPicker && (
        <div
          className="fixed inset-0 z-[96] bg-black/35 flex items-end"
          onClick={closeBulkReactionPicker}
        >
          <div
            className="w-full bg-white rounded-t-3xl border-t border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="p-4"
              style={{
                paddingBottom: "max(env(safe-area-inset-bottom, 0px), 12px)",
              }}
            >
              <p className="text-xs font-semibold text-gray-500 mb-3">
                React to {selectedCount} selected
              </p>
              <div className="flex flex-wrap gap-2">
                {bulkPickerReactionEmojis.map((emoji) => (
                  <button
                    key={`bulk-reaction-${emoji}`}
                    type="button"
                    onClick={() => handleReactSelectedMessages(emoji)}
                    className="w-10 h-10 rounded-full border border-gray-200 text-xl hover:bg-gray-50"
                    title={`React with ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    armBulkReactionPickerDismissGuard();
                    setShowBulkCustomReactionInput(true);
                  }}
                  className="w-10 h-10 rounded-full border border-gray-300 text-gray-600 text-sm hover:bg-gray-50"
                  title="Open keyboard emoji input"
                >
                  ⌨
                </button>

                {showBulkCustomReactionInput && (
                  <div className="w-full mt-1 flex items-center gap-2">
                    <input
                      ref={bulkCustomReactionInputRef}
                      type="text"
                      data-reaction-keyboard-input="true"
                      value={bulkCustomReactionInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setBulkCustomReactionInput(value);
                      }}
                      placeholder="Pick emoji from keyboard, then tap it in the list"
                      className="flex-1 h-10 px-3 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
                      autoFocus
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      name="bulk-reaction-emoji-input"
                      enterKeyHint="done"
                      onFocus={() => {
                        armBulkReactionPickerDismissGuard();
                      }}
                      onBlur={() => {
                        armBulkReactionPickerDismissGuard(250);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Read receipts modal */}
      {readReceiptsMessage && (
        <ReadReceiptsModal
          message={readReceiptsMessage}
          participants={participantUsers}
          currentUserId={currentUser.id}
          onClose={() => setReadReceiptsMessage(null)}
        />
      )}

      {/* Message status modal */}
      {messageStatusMessage && (
        <MessageStatusModal
          message={messageStatusMessage}
          participants={participantUsers}
          currentUserId={currentUser.id}
          onClose={() => setMessageStatusMessage(null)}
        />
      )}

      {/* Reaction details modal */}
      {reactionDetailsEmoji && reactionDetailsMessage && (
        <ReactionDetailsModal
          emoji={reactionDetailsEmoji}
          reactions={reactionDetailsMessage.reactions || []}
          participants={participantUsers}
          onClose={() => {
            setReactionDetailsEmoji(null);
            setReactionDetailsMessage(null);
          }}
        />
      )}

      {/* Backdrop for reaction picker */}
      {reactingToMessage && isDimming && (
        <div className="fixed inset-0 bg-black/20 z-[50] pointer-events-none" />
      )}
    </div>
  );
};

export default ActivityChatPage;
// Simple toast implementation
function addToast(message: string, type: string) {
  // Create toast element
  const toast = document.createElement("div");
  toast.className = `fixed right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white font-medium transition-all duration-300 transform translate-x-full`;
  toast.style.top = `max(calc(env(safe-area-inset-top, 0px) + 1rem), calc(44px + 1rem))`;

  // Add type-specific styling
  if (type === "error") {
    toast.classList.add("bg-red-500");
  } else if (type === "success") {
    toast.classList.add("bg-green-500");
  } else {
    toast.classList.add("bg-blue-500");
  }

  toast.textContent = message;

  // Add to document
  document.body.appendChild(toast);

  // Animate in
  setTimeout(() => {
    toast.classList.remove("translate-x-full");
  }, 10);

  // Auto remove after 3 seconds
  setTimeout(() => {
    toast.classList.add("translate-x-full");
    setTimeout(() => {
      if (toast.parentNode) {
        document.body.removeChild(toast);
      }
    }, 300);
  }, 3000);
}
